import _extends from '@babel/runtime/helpers/extends';
import React, { useEffect, useState, useLayoutEffect, useRef, useMemo, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import { FormHelperText, Input, IconCalendar, Typography, Button, IconCaretLeft, IconCaretRight, utils, Box, IconClock } from '@screentone/core';
import ReactDOM from 'react-dom';

function requiredArgs(required, args) {
  if (args.length < required) {
    throw new TypeError(required + ' argument' + (required > 1 ? 's' : '') + ' required, but only ' + args.length + ' present');
  }
}

function _typeof$y(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$y = function _typeof(obj) { return typeof obj; }; } else { _typeof$y = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$y(obj); }
/**
 * @name isDate
 * @category Common Helpers
 * @summary Is the given value a date?
 *
 * @description
 * Returns true if the given value is an instance of Date. The function works for dates transferred across iframes.
 *
 * @param {*} value - the value to check
 * @returns {boolean} true if the given value is a date
 * @throws {TypeError} 1 arguments required
 *
 * @example
 * // For a valid date:
 * const result = isDate(new Date())
 * //=> true
 *
 * @example
 * // For an invalid date:
 * const result = isDate(new Date(NaN))
 * //=> true
 *
 * @example
 * // For some value:
 * const result = isDate('2014-02-31')
 * //=> false
 *
 * @example
 * // For an object:
 * const result = isDate({})
 * //=> false
 */

function isDate(value) {
  requiredArgs(1, arguments);
  return value instanceof Date || _typeof$y(value) === 'object' && Object.prototype.toString.call(value) === '[object Date]';
}

function _typeof$x(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$x = function _typeof(obj) { return typeof obj; }; } else { _typeof$x = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$x(obj); }
/**
 * @name toDate
 * @category Common Helpers
 * @summary Convert the given argument to an instance of Date.
 *
 * @description
 * Convert the given argument to an instance of Date.
 *
 * If the argument is an instance of Date, the function returns its clone.
 *
 * If the argument is a number, it is treated as a timestamp.
 *
 * If the argument is none of the above, the function returns Invalid Date.
 *
 * **Note**: *all* Date arguments passed to any *date-fns* function is processed by `toDate`.
 *
 * @param {Date|Number} argument - the value to convert
 * @returns {Date} the parsed date in the local time zone
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // Clone the date:
 * const result = toDate(new Date(2014, 1, 11, 11, 30, 30))
 * //=> Tue Feb 11 2014 11:30:30
 *
 * @example
 * // Convert the timestamp to date:
 * const result = toDate(1392098430000)
 * //=> Tue Feb 11 2014 11:30:30
 */

function toDate(argument) {
  requiredArgs(1, arguments);
  var argStr = Object.prototype.toString.call(argument); // Clone the date

  if (argument instanceof Date || _typeof$x(argument) === 'object' && argStr === '[object Date]') {
    // Prevent the date to lose the milliseconds when passed to new Date() in IE10
    return new Date(argument.getTime());
  } else if (typeof argument === 'number' || argStr === '[object Number]') {
    return new Date(argument);
  } else {
    if ((typeof argument === 'string' || argStr === '[object String]') && typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn("Starting with v2.0.0-beta.1 date-fns doesn't accept strings as date arguments. Please use `parseISO` to parse strings. See: https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#string-arguments"); // eslint-disable-next-line no-console

      console.warn(new Error().stack);
    }

    return new Date(NaN);
  }
}

/**
 * @name isValid
 * @category Common Helpers
 * @summary Is the given date valid?
 *
 * @description
 * Returns false if argument is Invalid Date and true otherwise.
 * Argument is converted to Date using `toDate`. See [toDate]{@link https://date-fns.org/docs/toDate}
 * Invalid Date is a Date, whose time value is NaN.
 *
 * Time value of Date: http://es5.github.io/#x15.9.1.1
 *
 * @param {*} date - the date to check
 * @returns {Boolean} the date is valid
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // For the valid date:
 * const result = isValid(new Date(2014, 1, 31))
 * //=> true
 *
 * @example
 * // For the value, convertable into a date:
 * const result = isValid(1393804800000)
 * //=> true
 *
 * @example
 * // For the invalid date:
 * const result = isValid(new Date(''))
 * //=> false
 */

function isValid(dirtyDate) {
  requiredArgs(1, arguments);

  if (!isDate(dirtyDate) && typeof dirtyDate !== 'number') {
    return false;
  }

  var date = toDate(dirtyDate);
  return !isNaN(Number(date));
}

function toInteger(dirtyNumber) {
  if (dirtyNumber === null || dirtyNumber === true || dirtyNumber === false) {
    return NaN;
  }

  var number = Number(dirtyNumber);

  if (isNaN(number)) {
    return number;
  }

  return number < 0 ? Math.ceil(number) : Math.floor(number);
}

/**
 * @name addMilliseconds
 * @category Millisecond Helpers
 * @summary Add the specified number of milliseconds to the given date.
 *
 * @description
 * Add the specified number of milliseconds to the given date.
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Number} amount - the amount of milliseconds to be added. Positive decimals will be rounded using `Math.floor`, decimals less than zero will be rounded using `Math.ceil`.
 * @returns {Date} the new date with the milliseconds added
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Add 750 milliseconds to 10 July 2014 12:45:30.000:
 * const result = addMilliseconds(new Date(2014, 6, 10, 12, 45, 30, 0), 750)
 * //=> Thu Jul 10 2014 12:45:30.750
 */

function addMilliseconds(dirtyDate, dirtyAmount) {
  requiredArgs(2, arguments);
  var timestamp = toDate(dirtyDate).getTime();
  var amount = toInteger(dirtyAmount);
  return new Date(timestamp + amount);
}

/**
 * @name subMilliseconds
 * @category Millisecond Helpers
 * @summary Subtract the specified number of milliseconds from the given date.
 *
 * @description
 * Subtract the specified number of milliseconds from the given date.
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Number} amount - the amount of milliseconds to be subtracted. Positive decimals will be rounded using `Math.floor`, decimals less than zero will be rounded using `Math.ceil`.
 * @returns {Date} the new date with the milliseconds subtracted
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Subtract 750 milliseconds from 10 July 2014 12:45:30.000:
 * const result = subMilliseconds(new Date(2014, 6, 10, 12, 45, 30, 0), 750)
 * //=> Thu Jul 10 2014 12:45:29.250
 */

function subMilliseconds(dirtyDate, dirtyAmount) {
  requiredArgs(2, arguments);
  var amount = toInteger(dirtyAmount);
  return addMilliseconds(dirtyDate, -amount);
}

var MILLISECONDS_IN_DAY$1 = 86400000;
function getUTCDayOfYear(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var timestamp = date.getTime();
  date.setUTCMonth(0, 1);
  date.setUTCHours(0, 0, 0, 0);
  var startOfYearTimestamp = date.getTime();
  var difference = timestamp - startOfYearTimestamp;
  return Math.floor(difference / MILLISECONDS_IN_DAY$1) + 1;
}

function startOfUTCISOWeek(dirtyDate) {
  requiredArgs(1, arguments);
  var weekStartsOn = 1;
  var date = toDate(dirtyDate);
  var day = date.getUTCDay();
  var diff = (day < weekStartsOn ? 7 : 0) + day - weekStartsOn;
  date.setUTCDate(date.getUTCDate() - diff);
  date.setUTCHours(0, 0, 0, 0);
  return date;
}

function getUTCISOWeekYear(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var year = date.getUTCFullYear();
  var fourthOfJanuaryOfNextYear = new Date(0);
  fourthOfJanuaryOfNextYear.setUTCFullYear(year + 1, 0, 4);
  fourthOfJanuaryOfNextYear.setUTCHours(0, 0, 0, 0);
  var startOfNextYear = startOfUTCISOWeek(fourthOfJanuaryOfNextYear);
  var fourthOfJanuaryOfThisYear = new Date(0);
  fourthOfJanuaryOfThisYear.setUTCFullYear(year, 0, 4);
  fourthOfJanuaryOfThisYear.setUTCHours(0, 0, 0, 0);
  var startOfThisYear = startOfUTCISOWeek(fourthOfJanuaryOfThisYear);

  if (date.getTime() >= startOfNextYear.getTime()) {
    return year + 1;
  } else if (date.getTime() >= startOfThisYear.getTime()) {
    return year;
  } else {
    return year - 1;
  }
}

function startOfUTCISOWeekYear(dirtyDate) {
  requiredArgs(1, arguments);
  var year = getUTCISOWeekYear(dirtyDate);
  var fourthOfJanuary = new Date(0);
  fourthOfJanuary.setUTCFullYear(year, 0, 4);
  fourthOfJanuary.setUTCHours(0, 0, 0, 0);
  var date = startOfUTCISOWeek(fourthOfJanuary);
  return date;
}

var MILLISECONDS_IN_WEEK$1 = 604800000;
function getUTCISOWeek(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var diff = startOfUTCISOWeek(date).getTime() - startOfUTCISOWeekYear(date).getTime(); // Round the number of days to the nearest integer
  // because the number of milliseconds in a week is not constant
  // (e.g. it's different in the week of the daylight saving time clock shift)

  return Math.round(diff / MILLISECONDS_IN_WEEK$1) + 1;
}

var defaultOptions = {};
function getDefaultOptions() {
  return defaultOptions;
}

function startOfUTCWeek(dirtyDate, options) {
  var _ref, _ref2, _ref3, _options$weekStartsOn, _options$locale, _options$locale$optio, _defaultOptions$local, _defaultOptions$local2;

  requiredArgs(1, arguments);
  var defaultOptions = getDefaultOptions();
  var weekStartsOn = toInteger((_ref = (_ref2 = (_ref3 = (_options$weekStartsOn = options === null || options === void 0 ? void 0 : options.weekStartsOn) !== null && _options$weekStartsOn !== void 0 ? _options$weekStartsOn : options === null || options === void 0 ? void 0 : (_options$locale = options.locale) === null || _options$locale === void 0 ? void 0 : (_options$locale$optio = _options$locale.options) === null || _options$locale$optio === void 0 ? void 0 : _options$locale$optio.weekStartsOn) !== null && _ref3 !== void 0 ? _ref3 : defaultOptions.weekStartsOn) !== null && _ref2 !== void 0 ? _ref2 : (_defaultOptions$local = defaultOptions.locale) === null || _defaultOptions$local === void 0 ? void 0 : (_defaultOptions$local2 = _defaultOptions$local.options) === null || _defaultOptions$local2 === void 0 ? void 0 : _defaultOptions$local2.weekStartsOn) !== null && _ref !== void 0 ? _ref : 0); // Test if weekStartsOn is between 0 and 6 _and_ is not NaN

  if (!(weekStartsOn >= 0 && weekStartsOn <= 6)) {
    throw new RangeError('weekStartsOn must be between 0 and 6 inclusively');
  }

  var date = toDate(dirtyDate);
  var day = date.getUTCDay();
  var diff = (day < weekStartsOn ? 7 : 0) + day - weekStartsOn;
  date.setUTCDate(date.getUTCDate() - diff);
  date.setUTCHours(0, 0, 0, 0);
  return date;
}

function getUTCWeekYear(dirtyDate, options) {
  var _ref, _ref2, _ref3, _options$firstWeekCon, _options$locale, _options$locale$optio, _defaultOptions$local, _defaultOptions$local2;

  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var year = date.getUTCFullYear();
  var defaultOptions = getDefaultOptions();
  var firstWeekContainsDate = toInteger((_ref = (_ref2 = (_ref3 = (_options$firstWeekCon = options === null || options === void 0 ? void 0 : options.firstWeekContainsDate) !== null && _options$firstWeekCon !== void 0 ? _options$firstWeekCon : options === null || options === void 0 ? void 0 : (_options$locale = options.locale) === null || _options$locale === void 0 ? void 0 : (_options$locale$optio = _options$locale.options) === null || _options$locale$optio === void 0 ? void 0 : _options$locale$optio.firstWeekContainsDate) !== null && _ref3 !== void 0 ? _ref3 : defaultOptions.firstWeekContainsDate) !== null && _ref2 !== void 0 ? _ref2 : (_defaultOptions$local = defaultOptions.locale) === null || _defaultOptions$local === void 0 ? void 0 : (_defaultOptions$local2 = _defaultOptions$local.options) === null || _defaultOptions$local2 === void 0 ? void 0 : _defaultOptions$local2.firstWeekContainsDate) !== null && _ref !== void 0 ? _ref : 1); // Test if weekStartsOn is between 1 and 7 _and_ is not NaN

  if (!(firstWeekContainsDate >= 1 && firstWeekContainsDate <= 7)) {
    throw new RangeError('firstWeekContainsDate must be between 1 and 7 inclusively');
  }

  var firstWeekOfNextYear = new Date(0);
  firstWeekOfNextYear.setUTCFullYear(year + 1, 0, firstWeekContainsDate);
  firstWeekOfNextYear.setUTCHours(0, 0, 0, 0);
  var startOfNextYear = startOfUTCWeek(firstWeekOfNextYear, options);
  var firstWeekOfThisYear = new Date(0);
  firstWeekOfThisYear.setUTCFullYear(year, 0, firstWeekContainsDate);
  firstWeekOfThisYear.setUTCHours(0, 0, 0, 0);
  var startOfThisYear = startOfUTCWeek(firstWeekOfThisYear, options);

  if (date.getTime() >= startOfNextYear.getTime()) {
    return year + 1;
  } else if (date.getTime() >= startOfThisYear.getTime()) {
    return year;
  } else {
    return year - 1;
  }
}

function startOfUTCWeekYear(dirtyDate, options) {
  var _ref, _ref2, _ref3, _options$firstWeekCon, _options$locale, _options$locale$optio, _defaultOptions$local, _defaultOptions$local2;

  requiredArgs(1, arguments);
  var defaultOptions = getDefaultOptions();
  var firstWeekContainsDate = toInteger((_ref = (_ref2 = (_ref3 = (_options$firstWeekCon = options === null || options === void 0 ? void 0 : options.firstWeekContainsDate) !== null && _options$firstWeekCon !== void 0 ? _options$firstWeekCon : options === null || options === void 0 ? void 0 : (_options$locale = options.locale) === null || _options$locale === void 0 ? void 0 : (_options$locale$optio = _options$locale.options) === null || _options$locale$optio === void 0 ? void 0 : _options$locale$optio.firstWeekContainsDate) !== null && _ref3 !== void 0 ? _ref3 : defaultOptions.firstWeekContainsDate) !== null && _ref2 !== void 0 ? _ref2 : (_defaultOptions$local = defaultOptions.locale) === null || _defaultOptions$local === void 0 ? void 0 : (_defaultOptions$local2 = _defaultOptions$local.options) === null || _defaultOptions$local2 === void 0 ? void 0 : _defaultOptions$local2.firstWeekContainsDate) !== null && _ref !== void 0 ? _ref : 1);
  var year = getUTCWeekYear(dirtyDate, options);
  var firstWeek = new Date(0);
  firstWeek.setUTCFullYear(year, 0, firstWeekContainsDate);
  firstWeek.setUTCHours(0, 0, 0, 0);
  var date = startOfUTCWeek(firstWeek, options);
  return date;
}

var MILLISECONDS_IN_WEEK = 604800000;
function getUTCWeek(dirtyDate, options) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var diff = startOfUTCWeek(date, options).getTime() - startOfUTCWeekYear(date, options).getTime(); // Round the number of days to the nearest integer
  // because the number of milliseconds in a week is not constant
  // (e.g. it's different in the week of the daylight saving time clock shift)

  return Math.round(diff / MILLISECONDS_IN_WEEK) + 1;
}

function addLeadingZeros(number, targetLength) {
  var sign = number < 0 ? '-' : '';
  var output = Math.abs(number).toString();

  while (output.length < targetLength) {
    output = '0' + output;
  }

  return sign + output;
}

/*
 * |     | Unit                           |     | Unit                           |
 * |-----|--------------------------------|-----|--------------------------------|
 * |  a  | AM, PM                         |  A* |                                |
 * |  d  | Day of month                   |  D  |                                |
 * |  h  | Hour [1-12]                    |  H  | Hour [0-23]                    |
 * |  m  | Minute                         |  M  | Month                          |
 * |  s  | Second                         |  S  | Fraction of second             |
 * |  y  | Year (abs)                     |  Y  |                                |
 *
 * Letters marked by * are not implemented but reserved by Unicode standard.
 */

var formatters$2 = {
  // Year
  y: function y(date, token) {
    // From http://www.unicode.org/reports/tr35/tr35-31/tr35-dates.html#Date_Format_tokens
    // | Year     |     y | yy |   yyy |  yyyy | yyyyy |
    // |----------|-------|----|-------|-------|-------|
    // | AD 1     |     1 | 01 |   001 |  0001 | 00001 |
    // | AD 12    |    12 | 12 |   012 |  0012 | 00012 |
    // | AD 123   |   123 | 23 |   123 |  0123 | 00123 |
    // | AD 1234  |  1234 | 34 |  1234 |  1234 | 01234 |
    // | AD 12345 | 12345 | 45 | 12345 | 12345 | 12345 |
    var signedYear = date.getUTCFullYear(); // Returns 1 for 1 BC (which is year 0 in JavaScript)

    var year = signedYear > 0 ? signedYear : 1 - signedYear;
    return addLeadingZeros(token === 'yy' ? year % 100 : year, token.length);
  },
  // Month
  M: function M(date, token) {
    var month = date.getUTCMonth();
    return token === 'M' ? String(month + 1) : addLeadingZeros(month + 1, 2);
  },
  // Day of the month
  d: function d(date, token) {
    return addLeadingZeros(date.getUTCDate(), token.length);
  },
  // AM or PM
  a: function a(date, token) {
    var dayPeriodEnumValue = date.getUTCHours() / 12 >= 1 ? 'pm' : 'am';

    switch (token) {
      case 'a':
      case 'aa':
        return dayPeriodEnumValue.toUpperCase();

      case 'aaa':
        return dayPeriodEnumValue;

      case 'aaaaa':
        return dayPeriodEnumValue[0];

      case 'aaaa':
      default:
        return dayPeriodEnumValue === 'am' ? 'a.m.' : 'p.m.';
    }
  },
  // Hour [1-12]
  h: function h(date, token) {
    return addLeadingZeros(date.getUTCHours() % 12 || 12, token.length);
  },
  // Hour [0-23]
  H: function H(date, token) {
    return addLeadingZeros(date.getUTCHours(), token.length);
  },
  // Minute
  m: function m(date, token) {
    return addLeadingZeros(date.getUTCMinutes(), token.length);
  },
  // Second
  s: function s(date, token) {
    return addLeadingZeros(date.getUTCSeconds(), token.length);
  },
  // Fraction of second
  S: function S(date, token) {
    var numberOfDigits = token.length;
    var milliseconds = date.getUTCMilliseconds();
    var fractionalSeconds = Math.floor(milliseconds * Math.pow(10, numberOfDigits - 3));
    return addLeadingZeros(fractionalSeconds, token.length);
  }
};
var lightFormatters = formatters$2;

var dayPeriodEnum = {
  am: 'am',
  pm: 'pm',
  midnight: 'midnight',
  noon: 'noon',
  morning: 'morning',
  afternoon: 'afternoon',
  evening: 'evening',
  night: 'night'
};

/*
 * |     | Unit                           |     | Unit                           |
 * |-----|--------------------------------|-----|--------------------------------|
 * |  a  | AM, PM                         |  A* | Milliseconds in day            |
 * |  b  | AM, PM, noon, midnight         |  B  | Flexible day period            |
 * |  c  | Stand-alone local day of week  |  C* | Localized hour w/ day period   |
 * |  d  | Day of month                   |  D  | Day of year                    |
 * |  e  | Local day of week              |  E  | Day of week                    |
 * |  f  |                                |  F* | Day of week in month           |
 * |  g* | Modified Julian day            |  G  | Era                            |
 * |  h  | Hour [1-12]                    |  H  | Hour [0-23]                    |
 * |  i! | ISO day of week                |  I! | ISO week of year               |
 * |  j* | Localized hour w/ day period   |  J* | Localized hour w/o day period  |
 * |  k  | Hour [1-24]                    |  K  | Hour [0-11]                    |
 * |  l* | (deprecated)                   |  L  | Stand-alone month              |
 * |  m  | Minute                         |  M  | Month                          |
 * |  n  |                                |  N  |                                |
 * |  o! | Ordinal number modifier        |  O  | Timezone (GMT)                 |
 * |  p! | Long localized time            |  P! | Long localized date            |
 * |  q  | Stand-alone quarter            |  Q  | Quarter                        |
 * |  r* | Related Gregorian year         |  R! | ISO week-numbering year        |
 * |  s  | Second                         |  S  | Fraction of second             |
 * |  t! | Seconds timestamp              |  T! | Milliseconds timestamp         |
 * |  u  | Extended year                  |  U* | Cyclic year                    |
 * |  v* | Timezone (generic non-locat.)  |  V* | Timezone (location)            |
 * |  w  | Local week of year             |  W* | Week of month                  |
 * |  x  | Timezone (ISO-8601 w/o Z)      |  X  | Timezone (ISO-8601)            |
 * |  y  | Year (abs)                     |  Y  | Local week-numbering year      |
 * |  z  | Timezone (specific non-locat.) |  Z* | Timezone (aliases)             |
 *
 * Letters marked by * are not implemented but reserved by Unicode standard.
 *
 * Letters marked by ! are non-standard, but implemented by date-fns:
 * - `o` modifies the previous token to turn it into an ordinal (see `format` docs)
 * - `i` is ISO day of week. For `i` and `ii` is returns numeric ISO week days,
 *   i.e. 7 for Sunday, 1 for Monday, etc.
 * - `I` is ISO week of year, as opposed to `w` which is local week of year.
 * - `R` is ISO week-numbering year, as opposed to `Y` which is local week-numbering year.
 *   `R` is supposed to be used in conjunction with `I` and `i`
 *   for universal ISO week-numbering date, whereas
 *   `Y` is supposed to be used in conjunction with `w` and `e`
 *   for week-numbering date specific to the locale.
 * - `P` is long localized date format
 * - `p` is long localized time format
 */
var formatters = {
  // Era
  G: function G(date, token, localize) {
    var era = date.getUTCFullYear() > 0 ? 1 : 0;

    switch (token) {
      // AD, BC
      case 'G':
      case 'GG':
      case 'GGG':
        return localize.era(era, {
          width: 'abbreviated'
        });
      // A, B

      case 'GGGGG':
        return localize.era(era, {
          width: 'narrow'
        });
      // Anno Domini, Before Christ

      case 'GGGG':
      default:
        return localize.era(era, {
          width: 'wide'
        });
    }
  },
  // Year
  y: function y(date, token, localize) {
    // Ordinal number
    if (token === 'yo') {
      var signedYear = date.getUTCFullYear(); // Returns 1 for 1 BC (which is year 0 in JavaScript)

      var year = signedYear > 0 ? signedYear : 1 - signedYear;
      return localize.ordinalNumber(year, {
        unit: 'year'
      });
    }

    return lightFormatters.y(date, token);
  },
  // Local week-numbering year
  Y: function Y(date, token, localize, options) {
    var signedWeekYear = getUTCWeekYear(date, options); // Returns 1 for 1 BC (which is year 0 in JavaScript)

    var weekYear = signedWeekYear > 0 ? signedWeekYear : 1 - signedWeekYear; // Two digit year

    if (token === 'YY') {
      var twoDigitYear = weekYear % 100;
      return addLeadingZeros(twoDigitYear, 2);
    } // Ordinal number


    if (token === 'Yo') {
      return localize.ordinalNumber(weekYear, {
        unit: 'year'
      });
    } // Padding


    return addLeadingZeros(weekYear, token.length);
  },
  // ISO week-numbering year
  R: function R(date, token) {
    var isoWeekYear = getUTCISOWeekYear(date); // Padding

    return addLeadingZeros(isoWeekYear, token.length);
  },
  // Extended year. This is a single number designating the year of this calendar system.
  // The main difference between `y` and `u` localizers are B.C. years:
  // | Year | `y` | `u` |
  // |------|-----|-----|
  // | AC 1 |   1 |   1 |
  // | BC 1 |   1 |   0 |
  // | BC 2 |   2 |  -1 |
  // Also `yy` always returns the last two digits of a year,
  // while `uu` pads single digit years to 2 characters and returns other years unchanged.
  u: function u(date, token) {
    var year = date.getUTCFullYear();
    return addLeadingZeros(year, token.length);
  },
  // Quarter
  Q: function Q(date, token, localize) {
    var quarter = Math.ceil((date.getUTCMonth() + 1) / 3);

    switch (token) {
      // 1, 2, 3, 4
      case 'Q':
        return String(quarter);
      // 01, 02, 03, 04

      case 'QQ':
        return addLeadingZeros(quarter, 2);
      // 1st, 2nd, 3rd, 4th

      case 'Qo':
        return localize.ordinalNumber(quarter, {
          unit: 'quarter'
        });
      // Q1, Q2, Q3, Q4

      case 'QQQ':
        return localize.quarter(quarter, {
          width: 'abbreviated',
          context: 'formatting'
        });
      // 1, 2, 3, 4 (narrow quarter; could be not numerical)

      case 'QQQQQ':
        return localize.quarter(quarter, {
          width: 'narrow',
          context: 'formatting'
        });
      // 1st quarter, 2nd quarter, ...

      case 'QQQQ':
      default:
        return localize.quarter(quarter, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // Stand-alone quarter
  q: function q(date, token, localize) {
    var quarter = Math.ceil((date.getUTCMonth() + 1) / 3);

    switch (token) {
      // 1, 2, 3, 4
      case 'q':
        return String(quarter);
      // 01, 02, 03, 04

      case 'qq':
        return addLeadingZeros(quarter, 2);
      // 1st, 2nd, 3rd, 4th

      case 'qo':
        return localize.ordinalNumber(quarter, {
          unit: 'quarter'
        });
      // Q1, Q2, Q3, Q4

      case 'qqq':
        return localize.quarter(quarter, {
          width: 'abbreviated',
          context: 'standalone'
        });
      // 1, 2, 3, 4 (narrow quarter; could be not numerical)

      case 'qqqqq':
        return localize.quarter(quarter, {
          width: 'narrow',
          context: 'standalone'
        });
      // 1st quarter, 2nd quarter, ...

      case 'qqqq':
      default:
        return localize.quarter(quarter, {
          width: 'wide',
          context: 'standalone'
        });
    }
  },
  // Month
  M: function M(date, token, localize) {
    var month = date.getUTCMonth();

    switch (token) {
      case 'M':
      case 'MM':
        return lightFormatters.M(date, token);
      // 1st, 2nd, ..., 12th

      case 'Mo':
        return localize.ordinalNumber(month + 1, {
          unit: 'month'
        });
      // Jan, Feb, ..., Dec

      case 'MMM':
        return localize.month(month, {
          width: 'abbreviated',
          context: 'formatting'
        });
      // J, F, ..., D

      case 'MMMMM':
        return localize.month(month, {
          width: 'narrow',
          context: 'formatting'
        });
      // January, February, ..., December

      case 'MMMM':
      default:
        return localize.month(month, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // Stand-alone month
  L: function L(date, token, localize) {
    var month = date.getUTCMonth();

    switch (token) {
      // 1, 2, ..., 12
      case 'L':
        return String(month + 1);
      // 01, 02, ..., 12

      case 'LL':
        return addLeadingZeros(month + 1, 2);
      // 1st, 2nd, ..., 12th

      case 'Lo':
        return localize.ordinalNumber(month + 1, {
          unit: 'month'
        });
      // Jan, Feb, ..., Dec

      case 'LLL':
        return localize.month(month, {
          width: 'abbreviated',
          context: 'standalone'
        });
      // J, F, ..., D

      case 'LLLLL':
        return localize.month(month, {
          width: 'narrow',
          context: 'standalone'
        });
      // January, February, ..., December

      case 'LLLL':
      default:
        return localize.month(month, {
          width: 'wide',
          context: 'standalone'
        });
    }
  },
  // Local week of year
  w: function w(date, token, localize, options) {
    var week = getUTCWeek(date, options);

    if (token === 'wo') {
      return localize.ordinalNumber(week, {
        unit: 'week'
      });
    }

    return addLeadingZeros(week, token.length);
  },
  // ISO week of year
  I: function I(date, token, localize) {
    var isoWeek = getUTCISOWeek(date);

    if (token === 'Io') {
      return localize.ordinalNumber(isoWeek, {
        unit: 'week'
      });
    }

    return addLeadingZeros(isoWeek, token.length);
  },
  // Day of the month
  d: function d(date, token, localize) {
    if (token === 'do') {
      return localize.ordinalNumber(date.getUTCDate(), {
        unit: 'date'
      });
    }

    return lightFormatters.d(date, token);
  },
  // Day of year
  D: function D(date, token, localize) {
    var dayOfYear = getUTCDayOfYear(date);

    if (token === 'Do') {
      return localize.ordinalNumber(dayOfYear, {
        unit: 'dayOfYear'
      });
    }

    return addLeadingZeros(dayOfYear, token.length);
  },
  // Day of week
  E: function E(date, token, localize) {
    var dayOfWeek = date.getUTCDay();

    switch (token) {
      // Tue
      case 'E':
      case 'EE':
      case 'EEE':
        return localize.day(dayOfWeek, {
          width: 'abbreviated',
          context: 'formatting'
        });
      // T

      case 'EEEEE':
        return localize.day(dayOfWeek, {
          width: 'narrow',
          context: 'formatting'
        });
      // Tu

      case 'EEEEEE':
        return localize.day(dayOfWeek, {
          width: 'short',
          context: 'formatting'
        });
      // Tuesday

      case 'EEEE':
      default:
        return localize.day(dayOfWeek, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // Local day of week
  e: function e(date, token, localize, options) {
    var dayOfWeek = date.getUTCDay();
    var localDayOfWeek = (dayOfWeek - options.weekStartsOn + 8) % 7 || 7;

    switch (token) {
      // Numerical value (Nth day of week with current locale or weekStartsOn)
      case 'e':
        return String(localDayOfWeek);
      // Padded numerical value

      case 'ee':
        return addLeadingZeros(localDayOfWeek, 2);
      // 1st, 2nd, ..., 7th

      case 'eo':
        return localize.ordinalNumber(localDayOfWeek, {
          unit: 'day'
        });

      case 'eee':
        return localize.day(dayOfWeek, {
          width: 'abbreviated',
          context: 'formatting'
        });
      // T

      case 'eeeee':
        return localize.day(dayOfWeek, {
          width: 'narrow',
          context: 'formatting'
        });
      // Tu

      case 'eeeeee':
        return localize.day(dayOfWeek, {
          width: 'short',
          context: 'formatting'
        });
      // Tuesday

      case 'eeee':
      default:
        return localize.day(dayOfWeek, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // Stand-alone local day of week
  c: function c(date, token, localize, options) {
    var dayOfWeek = date.getUTCDay();
    var localDayOfWeek = (dayOfWeek - options.weekStartsOn + 8) % 7 || 7;

    switch (token) {
      // Numerical value (same as in `e`)
      case 'c':
        return String(localDayOfWeek);
      // Padded numerical value

      case 'cc':
        return addLeadingZeros(localDayOfWeek, token.length);
      // 1st, 2nd, ..., 7th

      case 'co':
        return localize.ordinalNumber(localDayOfWeek, {
          unit: 'day'
        });

      case 'ccc':
        return localize.day(dayOfWeek, {
          width: 'abbreviated',
          context: 'standalone'
        });
      // T

      case 'ccccc':
        return localize.day(dayOfWeek, {
          width: 'narrow',
          context: 'standalone'
        });
      // Tu

      case 'cccccc':
        return localize.day(dayOfWeek, {
          width: 'short',
          context: 'standalone'
        });
      // Tuesday

      case 'cccc':
      default:
        return localize.day(dayOfWeek, {
          width: 'wide',
          context: 'standalone'
        });
    }
  },
  // ISO day of week
  i: function i(date, token, localize) {
    var dayOfWeek = date.getUTCDay();
    var isoDayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek;

    switch (token) {
      // 2
      case 'i':
        return String(isoDayOfWeek);
      // 02

      case 'ii':
        return addLeadingZeros(isoDayOfWeek, token.length);
      // 2nd

      case 'io':
        return localize.ordinalNumber(isoDayOfWeek, {
          unit: 'day'
        });
      // Tue

      case 'iii':
        return localize.day(dayOfWeek, {
          width: 'abbreviated',
          context: 'formatting'
        });
      // T

      case 'iiiii':
        return localize.day(dayOfWeek, {
          width: 'narrow',
          context: 'formatting'
        });
      // Tu

      case 'iiiiii':
        return localize.day(dayOfWeek, {
          width: 'short',
          context: 'formatting'
        });
      // Tuesday

      case 'iiii':
      default:
        return localize.day(dayOfWeek, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // AM or PM
  a: function a(date, token, localize) {
    var hours = date.getUTCHours();
    var dayPeriodEnumValue = hours / 12 >= 1 ? 'pm' : 'am';

    switch (token) {
      case 'a':
      case 'aa':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'abbreviated',
          context: 'formatting'
        });

      case 'aaa':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'abbreviated',
          context: 'formatting'
        }).toLowerCase();

      case 'aaaaa':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'narrow',
          context: 'formatting'
        });

      case 'aaaa':
      default:
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // AM, PM, midnight, noon
  b: function b(date, token, localize) {
    var hours = date.getUTCHours();
    var dayPeriodEnumValue;

    if (hours === 12) {
      dayPeriodEnumValue = dayPeriodEnum.noon;
    } else if (hours === 0) {
      dayPeriodEnumValue = dayPeriodEnum.midnight;
    } else {
      dayPeriodEnumValue = hours / 12 >= 1 ? 'pm' : 'am';
    }

    switch (token) {
      case 'b':
      case 'bb':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'abbreviated',
          context: 'formatting'
        });

      case 'bbb':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'abbreviated',
          context: 'formatting'
        }).toLowerCase();

      case 'bbbbb':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'narrow',
          context: 'formatting'
        });

      case 'bbbb':
      default:
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // in the morning, in the afternoon, in the evening, at night
  B: function B(date, token, localize) {
    var hours = date.getUTCHours();
    var dayPeriodEnumValue;

    if (hours >= 17) {
      dayPeriodEnumValue = dayPeriodEnum.evening;
    } else if (hours >= 12) {
      dayPeriodEnumValue = dayPeriodEnum.afternoon;
    } else if (hours >= 4) {
      dayPeriodEnumValue = dayPeriodEnum.morning;
    } else {
      dayPeriodEnumValue = dayPeriodEnum.night;
    }

    switch (token) {
      case 'B':
      case 'BB':
      case 'BBB':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'abbreviated',
          context: 'formatting'
        });

      case 'BBBBB':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'narrow',
          context: 'formatting'
        });

      case 'BBBB':
      default:
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // Hour [1-12]
  h: function h(date, token, localize) {
    if (token === 'ho') {
      var hours = date.getUTCHours() % 12;
      if (hours === 0) hours = 12;
      return localize.ordinalNumber(hours, {
        unit: 'hour'
      });
    }

    return lightFormatters.h(date, token);
  },
  // Hour [0-23]
  H: function H(date, token, localize) {
    if (token === 'Ho') {
      return localize.ordinalNumber(date.getUTCHours(), {
        unit: 'hour'
      });
    }

    return lightFormatters.H(date, token);
  },
  // Hour [0-11]
  K: function K(date, token, localize) {
    var hours = date.getUTCHours() % 12;

    if (token === 'Ko') {
      return localize.ordinalNumber(hours, {
        unit: 'hour'
      });
    }

    return addLeadingZeros(hours, token.length);
  },
  // Hour [1-24]
  k: function k(date, token, localize) {
    var hours = date.getUTCHours();
    if (hours === 0) hours = 24;

    if (token === 'ko') {
      return localize.ordinalNumber(hours, {
        unit: 'hour'
      });
    }

    return addLeadingZeros(hours, token.length);
  },
  // Minute
  m: function m(date, token, localize) {
    if (token === 'mo') {
      return localize.ordinalNumber(date.getUTCMinutes(), {
        unit: 'minute'
      });
    }

    return lightFormatters.m(date, token);
  },
  // Second
  s: function s(date, token, localize) {
    if (token === 'so') {
      return localize.ordinalNumber(date.getUTCSeconds(), {
        unit: 'second'
      });
    }

    return lightFormatters.s(date, token);
  },
  // Fraction of second
  S: function S(date, token) {
    return lightFormatters.S(date, token);
  },
  // Timezone (ISO-8601. If offset is 0, output is always `'Z'`)
  X: function X(date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timezoneOffset = originalDate.getTimezoneOffset();

    if (timezoneOffset === 0) {
      return 'Z';
    }

    switch (token) {
      // Hours and optional minutes
      case 'X':
        return formatTimezoneWithOptionalMinutes(timezoneOffset);
      // Hours, minutes and optional seconds without `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `XX`

      case 'XXXX':
      case 'XX':
        // Hours and minutes without `:` delimiter
        return formatTimezone(timezoneOffset);
      // Hours, minutes and optional seconds with `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `XXX`

      case 'XXXXX':
      case 'XXX': // Hours and minutes with `:` delimiter

      default:
        return formatTimezone(timezoneOffset, ':');
    }
  },
  // Timezone (ISO-8601. If offset is 0, output is `'+00:00'` or equivalent)
  x: function x(date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timezoneOffset = originalDate.getTimezoneOffset();

    switch (token) {
      // Hours and optional minutes
      case 'x':
        return formatTimezoneWithOptionalMinutes(timezoneOffset);
      // Hours, minutes and optional seconds without `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `xx`

      case 'xxxx':
      case 'xx':
        // Hours and minutes without `:` delimiter
        return formatTimezone(timezoneOffset);
      // Hours, minutes and optional seconds with `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `xxx`

      case 'xxxxx':
      case 'xxx': // Hours and minutes with `:` delimiter

      default:
        return formatTimezone(timezoneOffset, ':');
    }
  },
  // Timezone (GMT)
  O: function O(date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timezoneOffset = originalDate.getTimezoneOffset();

    switch (token) {
      // Short
      case 'O':
      case 'OO':
      case 'OOO':
        return 'GMT' + formatTimezoneShort(timezoneOffset, ':');
      // Long

      case 'OOOO':
      default:
        return 'GMT' + formatTimezone(timezoneOffset, ':');
    }
  },
  // Timezone (specific non-location)
  z: function z(date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timezoneOffset = originalDate.getTimezoneOffset();

    switch (token) {
      // Short
      case 'z':
      case 'zz':
      case 'zzz':
        return 'GMT' + formatTimezoneShort(timezoneOffset, ':');
      // Long

      case 'zzzz':
      default:
        return 'GMT' + formatTimezone(timezoneOffset, ':');
    }
  },
  // Seconds timestamp
  t: function t(date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timestamp = Math.floor(originalDate.getTime() / 1000);
    return addLeadingZeros(timestamp, token.length);
  },
  // Milliseconds timestamp
  T: function T(date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timestamp = originalDate.getTime();
    return addLeadingZeros(timestamp, token.length);
  }
};

function formatTimezoneShort(offset, dirtyDelimiter) {
  var sign = offset > 0 ? '-' : '+';
  var absOffset = Math.abs(offset);
  var hours = Math.floor(absOffset / 60);
  var minutes = absOffset % 60;

  if (minutes === 0) {
    return sign + String(hours);
  }

  var delimiter = dirtyDelimiter || '';
  return sign + String(hours) + delimiter + addLeadingZeros(minutes, 2);
}

function formatTimezoneWithOptionalMinutes(offset, dirtyDelimiter) {
  if (offset % 60 === 0) {
    var sign = offset > 0 ? '-' : '+';
    return sign + addLeadingZeros(Math.abs(offset) / 60, 2);
  }

  return formatTimezone(offset, dirtyDelimiter);
}

function formatTimezone(offset, dirtyDelimiter) {
  var delimiter = dirtyDelimiter || '';
  var sign = offset > 0 ? '-' : '+';
  var absOffset = Math.abs(offset);
  var hours = addLeadingZeros(Math.floor(absOffset / 60), 2);
  var minutes = addLeadingZeros(absOffset % 60, 2);
  return sign + hours + delimiter + minutes;
}

var formatters$1 = formatters;

var dateLongFormatter = function dateLongFormatter(pattern, formatLong) {
  switch (pattern) {
    case 'P':
      return formatLong.date({
        width: 'short'
      });

    case 'PP':
      return formatLong.date({
        width: 'medium'
      });

    case 'PPP':
      return formatLong.date({
        width: 'long'
      });

    case 'PPPP':
    default:
      return formatLong.date({
        width: 'full'
      });
  }
};

var timeLongFormatter = function timeLongFormatter(pattern, formatLong) {
  switch (pattern) {
    case 'p':
      return formatLong.time({
        width: 'short'
      });

    case 'pp':
      return formatLong.time({
        width: 'medium'
      });

    case 'ppp':
      return formatLong.time({
        width: 'long'
      });

    case 'pppp':
    default:
      return formatLong.time({
        width: 'full'
      });
  }
};

var dateTimeLongFormatter = function dateTimeLongFormatter(pattern, formatLong) {
  var matchResult = pattern.match(/(P+)(p+)?/) || [];
  var datePattern = matchResult[1];
  var timePattern = matchResult[2];

  if (!timePattern) {
    return dateLongFormatter(pattern, formatLong);
  }

  var dateTimeFormat;

  switch (datePattern) {
    case 'P':
      dateTimeFormat = formatLong.dateTime({
        width: 'short'
      });
      break;

    case 'PP':
      dateTimeFormat = formatLong.dateTime({
        width: 'medium'
      });
      break;

    case 'PPP':
      dateTimeFormat = formatLong.dateTime({
        width: 'long'
      });
      break;

    case 'PPPP':
    default:
      dateTimeFormat = formatLong.dateTime({
        width: 'full'
      });
      break;
  }

  return dateTimeFormat.replace('{{date}}', dateLongFormatter(datePattern, formatLong)).replace('{{time}}', timeLongFormatter(timePattern, formatLong));
};

var longFormatters = {
  p: timeLongFormatter,
  P: dateTimeLongFormatter
};
var longFormatters$1 = longFormatters;

/**
 * Google Chrome as of 67.0.3396.87 introduced timezones with offset that includes seconds.
 * They usually appear for dates that denote time before the timezones were introduced
 * (e.g. for 'Europe/Prague' timezone the offset is GMT+00:57:44 before 1 October 1891
 * and GMT+01:00:00 after that date)
 *
 * Date#getTimezoneOffset returns the offset in minutes and would return 57 for the example above,
 * which would lead to incorrect calculations.
 *
 * This function returns the timezone offset in milliseconds that takes seconds in account.
 */
function getTimezoneOffsetInMilliseconds(date) {
  var utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds()));
  utcDate.setUTCFullYear(date.getFullYear());
  return date.getTime() - utcDate.getTime();
}

var protectedDayOfYearTokens = ['D', 'DD'];
var protectedWeekYearTokens = ['YY', 'YYYY'];
function isProtectedDayOfYearToken(token) {
  return protectedDayOfYearTokens.indexOf(token) !== -1;
}
function isProtectedWeekYearToken(token) {
  return protectedWeekYearTokens.indexOf(token) !== -1;
}
function throwProtectedError(token, format, input) {
  if (token === 'YYYY') {
    throw new RangeError("Use `yyyy` instead of `YYYY` (in `".concat(format, "`) for formatting years to the input `").concat(input, "`; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md"));
  } else if (token === 'YY') {
    throw new RangeError("Use `yy` instead of `YY` (in `".concat(format, "`) for formatting years to the input `").concat(input, "`; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md"));
  } else if (token === 'D') {
    throw new RangeError("Use `d` instead of `D` (in `".concat(format, "`) for formatting days of the month to the input `").concat(input, "`; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md"));
  } else if (token === 'DD') {
    throw new RangeError("Use `dd` instead of `DD` (in `".concat(format, "`) for formatting days of the month to the input `").concat(input, "`; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md"));
  }
}

var formatDistanceLocale = {
  lessThanXSeconds: {
    one: 'less than a second',
    other: 'less than {{count}} seconds'
  },
  xSeconds: {
    one: '1 second',
    other: '{{count}} seconds'
  },
  halfAMinute: 'half a minute',
  lessThanXMinutes: {
    one: 'less than a minute',
    other: 'less than {{count}} minutes'
  },
  xMinutes: {
    one: '1 minute',
    other: '{{count}} minutes'
  },
  aboutXHours: {
    one: 'about 1 hour',
    other: 'about {{count}} hours'
  },
  xHours: {
    one: '1 hour',
    other: '{{count}} hours'
  },
  xDays: {
    one: '1 day',
    other: '{{count}} days'
  },
  aboutXWeeks: {
    one: 'about 1 week',
    other: 'about {{count}} weeks'
  },
  xWeeks: {
    one: '1 week',
    other: '{{count}} weeks'
  },
  aboutXMonths: {
    one: 'about 1 month',
    other: 'about {{count}} months'
  },
  xMonths: {
    one: '1 month',
    other: '{{count}} months'
  },
  aboutXYears: {
    one: 'about 1 year',
    other: 'about {{count}} years'
  },
  xYears: {
    one: '1 year',
    other: '{{count}} years'
  },
  overXYears: {
    one: 'over 1 year',
    other: 'over {{count}} years'
  },
  almostXYears: {
    one: 'almost 1 year',
    other: 'almost {{count}} years'
  }
};

var formatDistance = function formatDistance(token, count, options) {
  var result;
  var tokenValue = formatDistanceLocale[token];

  if (typeof tokenValue === 'string') {
    result = tokenValue;
  } else if (count === 1) {
    result = tokenValue.one;
  } else {
    result = tokenValue.other.replace('{{count}}', count.toString());
  }

  if (options !== null && options !== void 0 && options.addSuffix) {
    if (options.comparison && options.comparison > 0) {
      return 'in ' + result;
    } else {
      return result + ' ago';
    }
  }

  return result;
};

var formatDistance$1 = formatDistance;

function buildFormatLongFn(args) {
  return function () {
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    // TODO: Remove String()
    var width = options.width ? String(options.width) : args.defaultWidth;
    var format = args.formats[width] || args.formats[args.defaultWidth];
    return format;
  };
}

var dateFormats = {
  full: 'EEEE, MMMM do, y',
  long: 'MMMM do, y',
  medium: 'MMM d, y',
  short: 'MM/dd/yyyy'
};
var timeFormats = {
  full: 'h:mm:ss a zzzz',
  long: 'h:mm:ss a z',
  medium: 'h:mm:ss a',
  short: 'h:mm a'
};
var dateTimeFormats = {
  full: "{{date}} 'at' {{time}}",
  long: "{{date}} 'at' {{time}}",
  medium: '{{date}}, {{time}}',
  short: '{{date}}, {{time}}'
};
var formatLong = {
  date: buildFormatLongFn({
    formats: dateFormats,
    defaultWidth: 'full'
  }),
  time: buildFormatLongFn({
    formats: timeFormats,
    defaultWidth: 'full'
  }),
  dateTime: buildFormatLongFn({
    formats: dateTimeFormats,
    defaultWidth: 'full'
  })
};
var formatLong$1 = formatLong;

var formatRelativeLocale = {
  lastWeek: "'last' eeee 'at' p",
  yesterday: "'yesterday at' p",
  today: "'today at' p",
  tomorrow: "'tomorrow at' p",
  nextWeek: "eeee 'at' p",
  other: 'P'
};

var formatRelative = function formatRelative(token, _date, _baseDate, _options) {
  return formatRelativeLocale[token];
};

var formatRelative$1 = formatRelative;

function buildLocalizeFn(args) {
  return function (dirtyIndex, options) {
    var context = options !== null && options !== void 0 && options.context ? String(options.context) : 'standalone';
    var valuesArray;

    if (context === 'formatting' && args.formattingValues) {
      var defaultWidth = args.defaultFormattingWidth || args.defaultWidth;
      var width = options !== null && options !== void 0 && options.width ? String(options.width) : defaultWidth;
      valuesArray = args.formattingValues[width] || args.formattingValues[defaultWidth];
    } else {
      var _defaultWidth = args.defaultWidth;

      var _width = options !== null && options !== void 0 && options.width ? String(options.width) : args.defaultWidth;

      valuesArray = args.values[_width] || args.values[_defaultWidth];
    }

    var index = args.argumentCallback ? args.argumentCallback(dirtyIndex) : dirtyIndex; // @ts-ignore: For some reason TypeScript just don't want to match it, no matter how hard we try. I challenge you to try to remove it!

    return valuesArray[index];
  };
}

var eraValues = {
  narrow: ['B', 'A'],
  abbreviated: ['BC', 'AD'],
  wide: ['Before Christ', 'Anno Domini']
};
var quarterValues = {
  narrow: ['1', '2', '3', '4'],
  abbreviated: ['Q1', 'Q2', 'Q3', 'Q4'],
  wide: ['1st quarter', '2nd quarter', '3rd quarter', '4th quarter']
}; // Note: in English, the names of days of the week and months are capitalized.
// If you are making a new locale based on this one, check if the same is true for the language you're working on.
// Generally, formatted dates should look like they are in the middle of a sentence,
// e.g. in Spanish language the weekdays and months should be in the lowercase.

var monthValues = {
  narrow: ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'],
  abbreviated: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  wide: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
};
var dayValues = {
  narrow: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
  short: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
  abbreviated: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  wide: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
};
var dayPeriodValues = {
  narrow: {
    am: 'a',
    pm: 'p',
    midnight: 'mi',
    noon: 'n',
    morning: 'morning',
    afternoon: 'afternoon',
    evening: 'evening',
    night: 'night'
  },
  abbreviated: {
    am: 'AM',
    pm: 'PM',
    midnight: 'midnight',
    noon: 'noon',
    morning: 'morning',
    afternoon: 'afternoon',
    evening: 'evening',
    night: 'night'
  },
  wide: {
    am: 'a.m.',
    pm: 'p.m.',
    midnight: 'midnight',
    noon: 'noon',
    morning: 'morning',
    afternoon: 'afternoon',
    evening: 'evening',
    night: 'night'
  }
};
var formattingDayPeriodValues = {
  narrow: {
    am: 'a',
    pm: 'p',
    midnight: 'mi',
    noon: 'n',
    morning: 'in the morning',
    afternoon: 'in the afternoon',
    evening: 'in the evening',
    night: 'at night'
  },
  abbreviated: {
    am: 'AM',
    pm: 'PM',
    midnight: 'midnight',
    noon: 'noon',
    morning: 'in the morning',
    afternoon: 'in the afternoon',
    evening: 'in the evening',
    night: 'at night'
  },
  wide: {
    am: 'a.m.',
    pm: 'p.m.',
    midnight: 'midnight',
    noon: 'noon',
    morning: 'in the morning',
    afternoon: 'in the afternoon',
    evening: 'in the evening',
    night: 'at night'
  }
};

var ordinalNumber = function ordinalNumber(dirtyNumber, _options) {
  var number = Number(dirtyNumber); // If ordinal numbers depend on context, for example,
  // if they are different for different grammatical genders,
  // use `options.unit`.
  //
  // `unit` can be 'year', 'quarter', 'month', 'week', 'date', 'dayOfYear',
  // 'day', 'hour', 'minute', 'second'.

  var rem100 = number % 100;

  if (rem100 > 20 || rem100 < 10) {
    switch (rem100 % 10) {
      case 1:
        return number + 'st';

      case 2:
        return number + 'nd';

      case 3:
        return number + 'rd';
    }
  }

  return number + 'th';
};

var localize = {
  ordinalNumber: ordinalNumber,
  era: buildLocalizeFn({
    values: eraValues,
    defaultWidth: 'wide'
  }),
  quarter: buildLocalizeFn({
    values: quarterValues,
    defaultWidth: 'wide',
    argumentCallback: function argumentCallback(quarter) {
      return quarter - 1;
    }
  }),
  month: buildLocalizeFn({
    values: monthValues,
    defaultWidth: 'wide'
  }),
  day: buildLocalizeFn({
    values: dayValues,
    defaultWidth: 'wide'
  }),
  dayPeriod: buildLocalizeFn({
    values: dayPeriodValues,
    defaultWidth: 'wide',
    formattingValues: formattingDayPeriodValues,
    defaultFormattingWidth: 'wide'
  })
};
var localize$1 = localize;

function buildMatchFn(args) {
  return function (string) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var width = options.width;
    var matchPattern = width && args.matchPatterns[width] || args.matchPatterns[args.defaultMatchWidth];
    var matchResult = string.match(matchPattern);

    if (!matchResult) {
      return null;
    }

    var matchedString = matchResult[0];
    var parsePatterns = width && args.parsePatterns[width] || args.parsePatterns[args.defaultParseWidth];
    var key = Array.isArray(parsePatterns) ? findIndex(parsePatterns, function (pattern) {
      return pattern.test(matchedString);
    }) : findKey(parsePatterns, function (pattern) {
      return pattern.test(matchedString);
    });
    var value;
    value = args.valueCallback ? args.valueCallback(key) : key;
    value = options.valueCallback ? options.valueCallback(value) : value;
    var rest = string.slice(matchedString.length);
    return {
      value: value,
      rest: rest
    };
  };
}

function findKey(object, predicate) {
  for (var key in object) {
    if (object.hasOwnProperty(key) && predicate(object[key])) {
      return key;
    }
  }

  return undefined;
}

function findIndex(array, predicate) {
  for (var key = 0; key < array.length; key++) {
    if (predicate(array[key])) {
      return key;
    }
  }

  return undefined;
}

function buildMatchPatternFn(args) {
  return function (string) {
    var options = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
    var matchResult = string.match(args.matchPattern);
    if (!matchResult) return null;
    var matchedString = matchResult[0];
    var parseResult = string.match(args.parsePattern);
    if (!parseResult) return null;
    var value = args.valueCallback ? args.valueCallback(parseResult[0]) : parseResult[0];
    value = options.valueCallback ? options.valueCallback(value) : value;
    var rest = string.slice(matchedString.length);
    return {
      value: value,
      rest: rest
    };
  };
}

var matchOrdinalNumberPattern = /^(\d+)(th|st|nd|rd)?/i;
var parseOrdinalNumberPattern = /\d+/i;
var matchEraPatterns = {
  narrow: /^(b|a)/i,
  abbreviated: /^(b\.?\s?c\.?|b\.?\s?c\.?\s?e\.?|a\.?\s?d\.?|c\.?\s?e\.?)/i,
  wide: /^(before christ|before common era|anno domini|common era)/i
};
var parseEraPatterns = {
  any: [/^b/i, /^(a|c)/i]
};
var matchQuarterPatterns = {
  narrow: /^[1234]/i,
  abbreviated: /^q[1234]/i,
  wide: /^[1234](th|st|nd|rd)? quarter/i
};
var parseQuarterPatterns = {
  any: [/1/i, /2/i, /3/i, /4/i]
};
var matchMonthPatterns = {
  narrow: /^[jfmasond]/i,
  abbreviated: /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i,
  wide: /^(january|february|march|april|may|june|july|august|september|october|november|december)/i
};
var parseMonthPatterns = {
  narrow: [/^j/i, /^f/i, /^m/i, /^a/i, /^m/i, /^j/i, /^j/i, /^a/i, /^s/i, /^o/i, /^n/i, /^d/i],
  any: [/^ja/i, /^f/i, /^mar/i, /^ap/i, /^may/i, /^jun/i, /^jul/i, /^au/i, /^s/i, /^o/i, /^n/i, /^d/i]
};
var matchDayPatterns = {
  narrow: /^[smtwf]/i,
  short: /^(su|mo|tu|we|th|fr|sa)/i,
  abbreviated: /^(sun|mon|tue|wed|thu|fri|sat)/i,
  wide: /^(sunday|monday|tuesday|wednesday|thursday|friday|saturday)/i
};
var parseDayPatterns = {
  narrow: [/^s/i, /^m/i, /^t/i, /^w/i, /^t/i, /^f/i, /^s/i],
  any: [/^su/i, /^m/i, /^tu/i, /^w/i, /^th/i, /^f/i, /^sa/i]
};
var matchDayPeriodPatterns = {
  narrow: /^(a|p|mi|n|(in the|at) (morning|afternoon|evening|night))/i,
  any: /^([ap]\.?\s?m\.?|midnight|noon|(in the|at) (morning|afternoon|evening|night))/i
};
var parseDayPeriodPatterns = {
  any: {
    am: /^a/i,
    pm: /^p/i,
    midnight: /^mi/i,
    noon: /^no/i,
    morning: /morning/i,
    afternoon: /afternoon/i,
    evening: /evening/i,
    night: /night/i
  }
};
var match = {
  ordinalNumber: buildMatchPatternFn({
    matchPattern: matchOrdinalNumberPattern,
    parsePattern: parseOrdinalNumberPattern,
    valueCallback: function valueCallback(value) {
      return parseInt(value, 10);
    }
  }),
  era: buildMatchFn({
    matchPatterns: matchEraPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseEraPatterns,
    defaultParseWidth: 'any'
  }),
  quarter: buildMatchFn({
    matchPatterns: matchQuarterPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseQuarterPatterns,
    defaultParseWidth: 'any',
    valueCallback: function valueCallback(index) {
      return index + 1;
    }
  }),
  month: buildMatchFn({
    matchPatterns: matchMonthPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseMonthPatterns,
    defaultParseWidth: 'any'
  }),
  day: buildMatchFn({
    matchPatterns: matchDayPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseDayPatterns,
    defaultParseWidth: 'any'
  }),
  dayPeriod: buildMatchFn({
    matchPatterns: matchDayPeriodPatterns,
    defaultMatchWidth: 'any',
    parsePatterns: parseDayPeriodPatterns,
    defaultParseWidth: 'any'
  })
};
var match$1 = match;

/**
 * @type {Locale}
 * @category Locales
 * @summary English locale (United States).
 * @language English
 * @iso-639-2 eng
 * @author Sasha Koss [@kossnocorp]{@link https://github.com/kossnocorp}
 * @author Lesha Koss [@leshakoss]{@link https://github.com/leshakoss}
 */
var locale = {
  code: 'en-US',
  formatDistance: formatDistance$1,
  formatLong: formatLong$1,
  formatRelative: formatRelative$1,
  localize: localize$1,
  match: match$1,
  options: {
    weekStartsOn: 0
    /* Sunday */
    ,
    firstWeekContainsDate: 1
  }
};
var defaultLocale = locale;

// - [yYQqMLwIdDecihHKkms]o matches any available ordinal number token
//   (one of the certain letters followed by `o`)
// - (\w)\1* matches any sequences of the same letter
// - '' matches two quote characters in a row
// - '(''|[^'])+('|$) matches anything surrounded by two quote characters ('),
//   except a single quote symbol, which ends the sequence.
//   Two quote characters do not end the sequence.
//   If there is no matching single quote
//   then the sequence will continue until the end of the string.
// - . matches any single character unmatched by previous parts of the RegExps

var formattingTokensRegExp$1 = /[yYQqMLwIdDecihHKkms]o|(\w)\1*|''|'(''|[^'])+('|$)|./g; // This RegExp catches symbols escaped by quotes, and also
// sequences of symbols P, p, and the combinations like `PPPPPPPppppp`

var longFormattingTokensRegExp$1 = /P+p+|P+|p+|''|'(''|[^'])+('|$)|./g;
var escapedStringRegExp$1 = /^'([^]*?)'?$/;
var doubleQuoteRegExp$1 = /''/g;
var unescapedLatinCharacterRegExp$1 = /[a-zA-Z]/;
/**
 * @name format
 * @category Common Helpers
 * @summary Format the date.
 *
 * @description
 * Return the formatted date string in the given format. The result may vary by locale.
 *
 * > ⚠️ Please note that the `format` tokens differ from Moment.js and other libraries.
 * > See: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 *
 * The characters wrapped between two single quotes characters (') are escaped.
 * Two single quotes in a row, whether inside or outside a quoted sequence, represent a 'real' single quote.
 * (see the last example)
 *
 * Format of the string is based on Unicode Technical Standard #35:
 * https://www.unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table
 * with a few additions (see note 7 below the table).
 *
 * Accepted patterns:
 * | Unit                            | Pattern | Result examples                   | Notes |
 * |---------------------------------|---------|-----------------------------------|-------|
 * | Era                             | G..GGG  | AD, BC                            |       |
 * |                                 | GGGG    | Anno Domini, Before Christ        | 2     |
 * |                                 | GGGGG   | A, B                              |       |
 * | Calendar year                   | y       | 44, 1, 1900, 2017                 | 5     |
 * |                                 | yo      | 44th, 1st, 0th, 17th              | 5,7   |
 * |                                 | yy      | 44, 01, 00, 17                    | 5     |
 * |                                 | yyy     | 044, 001, 1900, 2017              | 5     |
 * |                                 | yyyy    | 0044, 0001, 1900, 2017            | 5     |
 * |                                 | yyyyy   | ...                               | 3,5   |
 * | Local week-numbering year       | Y       | 44, 1, 1900, 2017                 | 5     |
 * |                                 | Yo      | 44th, 1st, 1900th, 2017th         | 5,7   |
 * |                                 | YY      | 44, 01, 00, 17                    | 5,8   |
 * |                                 | YYY     | 044, 001, 1900, 2017              | 5     |
 * |                                 | YYYY    | 0044, 0001, 1900, 2017            | 5,8   |
 * |                                 | YYYYY   | ...                               | 3,5   |
 * | ISO week-numbering year         | R       | -43, 0, 1, 1900, 2017             | 5,7   |
 * |                                 | RR      | -43, 00, 01, 1900, 2017           | 5,7   |
 * |                                 | RRR     | -043, 000, 001, 1900, 2017        | 5,7   |
 * |                                 | RRRR    | -0043, 0000, 0001, 1900, 2017     | 5,7   |
 * |                                 | RRRRR   | ...                               | 3,5,7 |
 * | Extended year                   | u       | -43, 0, 1, 1900, 2017             | 5     |
 * |                                 | uu      | -43, 01, 1900, 2017               | 5     |
 * |                                 | uuu     | -043, 001, 1900, 2017             | 5     |
 * |                                 | uuuu    | -0043, 0001, 1900, 2017           | 5     |
 * |                                 | uuuuu   | ...                               | 3,5   |
 * | Quarter (formatting)            | Q       | 1, 2, 3, 4                        |       |
 * |                                 | Qo      | 1st, 2nd, 3rd, 4th                | 7     |
 * |                                 | QQ      | 01, 02, 03, 04                    |       |
 * |                                 | QQQ     | Q1, Q2, Q3, Q4                    |       |
 * |                                 | QQQQ    | 1st quarter, 2nd quarter, ...     | 2     |
 * |                                 | QQQQQ   | 1, 2, 3, 4                        | 4     |
 * | Quarter (stand-alone)           | q       | 1, 2, 3, 4                        |       |
 * |                                 | qo      | 1st, 2nd, 3rd, 4th                | 7     |
 * |                                 | qq      | 01, 02, 03, 04                    |       |
 * |                                 | qqq     | Q1, Q2, Q3, Q4                    |       |
 * |                                 | qqqq    | 1st quarter, 2nd quarter, ...     | 2     |
 * |                                 | qqqqq   | 1, 2, 3, 4                        | 4     |
 * | Month (formatting)              | M       | 1, 2, ..., 12                     |       |
 * |                                 | Mo      | 1st, 2nd, ..., 12th               | 7     |
 * |                                 | MM      | 01, 02, ..., 12                   |       |
 * |                                 | MMM     | Jan, Feb, ..., Dec                |       |
 * |                                 | MMMM    | January, February, ..., December  | 2     |
 * |                                 | MMMMM   | J, F, ..., D                      |       |
 * | Month (stand-alone)             | L       | 1, 2, ..., 12                     |       |
 * |                                 | Lo      | 1st, 2nd, ..., 12th               | 7     |
 * |                                 | LL      | 01, 02, ..., 12                   |       |
 * |                                 | LLL     | Jan, Feb, ..., Dec                |       |
 * |                                 | LLLL    | January, February, ..., December  | 2     |
 * |                                 | LLLLL   | J, F, ..., D                      |       |
 * | Local week of year              | w       | 1, 2, ..., 53                     |       |
 * |                                 | wo      | 1st, 2nd, ..., 53th               | 7     |
 * |                                 | ww      | 01, 02, ..., 53                   |       |
 * | ISO week of year                | I       | 1, 2, ..., 53                     | 7     |
 * |                                 | Io      | 1st, 2nd, ..., 53th               | 7     |
 * |                                 | II      | 01, 02, ..., 53                   | 7     |
 * | Day of month                    | d       | 1, 2, ..., 31                     |       |
 * |                                 | do      | 1st, 2nd, ..., 31st               | 7     |
 * |                                 | dd      | 01, 02, ..., 31                   |       |
 * | Day of year                     | D       | 1, 2, ..., 365, 366               | 9     |
 * |                                 | Do      | 1st, 2nd, ..., 365th, 366th       | 7     |
 * |                                 | DD      | 01, 02, ..., 365, 366             | 9     |
 * |                                 | DDD     | 001, 002, ..., 365, 366           |       |
 * |                                 | DDDD    | ...                               | 3     |
 * | Day of week (formatting)        | E..EEE  | Mon, Tue, Wed, ..., Sun           |       |
 * |                                 | EEEE    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 | EEEEE   | M, T, W, T, F, S, S               |       |
 * |                                 | EEEEEE  | Mo, Tu, We, Th, Fr, Sa, Su        |       |
 * | ISO day of week (formatting)    | i       | 1, 2, 3, ..., 7                   | 7     |
 * |                                 | io      | 1st, 2nd, ..., 7th                | 7     |
 * |                                 | ii      | 01, 02, ..., 07                   | 7     |
 * |                                 | iii     | Mon, Tue, Wed, ..., Sun           | 7     |
 * |                                 | iiii    | Monday, Tuesday, ..., Sunday      | 2,7   |
 * |                                 | iiiii   | M, T, W, T, F, S, S               | 7     |
 * |                                 | iiiiii  | Mo, Tu, We, Th, Fr, Sa, Su        | 7     |
 * | Local day of week (formatting)  | e       | 2, 3, 4, ..., 1                   |       |
 * |                                 | eo      | 2nd, 3rd, ..., 1st                | 7     |
 * |                                 | ee      | 02, 03, ..., 01                   |       |
 * |                                 | eee     | Mon, Tue, Wed, ..., Sun           |       |
 * |                                 | eeee    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 | eeeee   | M, T, W, T, F, S, S               |       |
 * |                                 | eeeeee  | Mo, Tu, We, Th, Fr, Sa, Su        |       |
 * | Local day of week (stand-alone) | c       | 2, 3, 4, ..., 1                   |       |
 * |                                 | co      | 2nd, 3rd, ..., 1st                | 7     |
 * |                                 | cc      | 02, 03, ..., 01                   |       |
 * |                                 | ccc     | Mon, Tue, Wed, ..., Sun           |       |
 * |                                 | cccc    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 | ccccc   | M, T, W, T, F, S, S               |       |
 * |                                 | cccccc  | Mo, Tu, We, Th, Fr, Sa, Su        |       |
 * | AM, PM                          | a..aa   | AM, PM                            |       |
 * |                                 | aaa     | am, pm                            |       |
 * |                                 | aaaa    | a.m., p.m.                        | 2     |
 * |                                 | aaaaa   | a, p                              |       |
 * | AM, PM, noon, midnight          | b..bb   | AM, PM, noon, midnight            |       |
 * |                                 | bbb     | am, pm, noon, midnight            |       |
 * |                                 | bbbb    | a.m., p.m., noon, midnight        | 2     |
 * |                                 | bbbbb   | a, p, n, mi                       |       |
 * | Flexible day period             | B..BBB  | at night, in the morning, ...     |       |
 * |                                 | BBBB    | at night, in the morning, ...     | 2     |
 * |                                 | BBBBB   | at night, in the morning, ...     |       |
 * | Hour [1-12]                     | h       | 1, 2, ..., 11, 12                 |       |
 * |                                 | ho      | 1st, 2nd, ..., 11th, 12th         | 7     |
 * |                                 | hh      | 01, 02, ..., 11, 12               |       |
 * | Hour [0-23]                     | H       | 0, 1, 2, ..., 23                  |       |
 * |                                 | Ho      | 0th, 1st, 2nd, ..., 23rd          | 7     |
 * |                                 | HH      | 00, 01, 02, ..., 23               |       |
 * | Hour [0-11]                     | K       | 1, 2, ..., 11, 0                  |       |
 * |                                 | Ko      | 1st, 2nd, ..., 11th, 0th          | 7     |
 * |                                 | KK      | 01, 02, ..., 11, 00               |       |
 * | Hour [1-24]                     | k       | 24, 1, 2, ..., 23                 |       |
 * |                                 | ko      | 24th, 1st, 2nd, ..., 23rd         | 7     |
 * |                                 | kk      | 24, 01, 02, ..., 23               |       |
 * | Minute                          | m       | 0, 1, ..., 59                     |       |
 * |                                 | mo      | 0th, 1st, ..., 59th               | 7     |
 * |                                 | mm      | 00, 01, ..., 59                   |       |
 * | Second                          | s       | 0, 1, ..., 59                     |       |
 * |                                 | so      | 0th, 1st, ..., 59th               | 7     |
 * |                                 | ss      | 00, 01, ..., 59                   |       |
 * | Fraction of second              | S       | 0, 1, ..., 9                      |       |
 * |                                 | SS      | 00, 01, ..., 99                   |       |
 * |                                 | SSS     | 000, 001, ..., 999                |       |
 * |                                 | SSSS    | ...                               | 3     |
 * | Timezone (ISO-8601 w/ Z)        | X       | -08, +0530, Z                     |       |
 * |                                 | XX      | -0800, +0530, Z                   |       |
 * |                                 | XXX     | -08:00, +05:30, Z                 |       |
 * |                                 | XXXX    | -0800, +0530, Z, +123456          | 2     |
 * |                                 | XXXXX   | -08:00, +05:30, Z, +12:34:56      |       |
 * | Timezone (ISO-8601 w/o Z)       | x       | -08, +0530, +00                   |       |
 * |                                 | xx      | -0800, +0530, +0000               |       |
 * |                                 | xxx     | -08:00, +05:30, +00:00            | 2     |
 * |                                 | xxxx    | -0800, +0530, +0000, +123456      |       |
 * |                                 | xxxxx   | -08:00, +05:30, +00:00, +12:34:56 |       |
 * | Timezone (GMT)                  | O...OOO | GMT-8, GMT+5:30, GMT+0            |       |
 * |                                 | OOOO    | GMT-08:00, GMT+05:30, GMT+00:00   | 2     |
 * | Timezone (specific non-locat.)  | z...zzz | GMT-8, GMT+5:30, GMT+0            | 6     |
 * |                                 | zzzz    | GMT-08:00, GMT+05:30, GMT+00:00   | 2,6   |
 * | Seconds timestamp               | t       | 512969520                         | 7     |
 * |                                 | tt      | ...                               | 3,7   |
 * | Milliseconds timestamp          | T       | 512969520900                      | 7     |
 * |                                 | TT      | ...                               | 3,7   |
 * | Long localized date             | P       | 04/29/1453                        | 7     |
 * |                                 | PP      | Apr 29, 1453                      | 7     |
 * |                                 | PPP     | April 29th, 1453                  | 7     |
 * |                                 | PPPP    | Friday, April 29th, 1453          | 2,7   |
 * | Long localized time             | p       | 12:00 AM                          | 7     |
 * |                                 | pp      | 12:00:00 AM                       | 7     |
 * |                                 | ppp     | 12:00:00 AM GMT+2                 | 7     |
 * |                                 | pppp    | 12:00:00 AM GMT+02:00             | 2,7   |
 * | Combination of date and time    | Pp      | 04/29/1453, 12:00 AM              | 7     |
 * |                                 | PPpp    | Apr 29, 1453, 12:00:00 AM         | 7     |
 * |                                 | PPPppp  | April 29th, 1453 at ...           | 7     |
 * |                                 | PPPPpppp| Friday, April 29th, 1453 at ...   | 2,7   |
 * Notes:
 * 1. "Formatting" units (e.g. formatting quarter) in the default en-US locale
 *    are the same as "stand-alone" units, but are different in some languages.
 *    "Formatting" units are declined according to the rules of the language
 *    in the context of a date. "Stand-alone" units are always nominative singular:
 *
 *    `format(new Date(2017, 10, 6), 'do LLLL', {locale: cs}) //=> '6. listopad'`
 *
 *    `format(new Date(2017, 10, 6), 'do MMMM', {locale: cs}) //=> '6. listopadu'`
 *
 * 2. Any sequence of the identical letters is a pattern, unless it is escaped by
 *    the single quote characters (see below).
 *    If the sequence is longer than listed in table (e.g. `EEEEEEEEEEE`)
 *    the output will be the same as default pattern for this unit, usually
 *    the longest one (in case of ISO weekdays, `EEEE`). Default patterns for units
 *    are marked with "2" in the last column of the table.
 *
 *    `format(new Date(2017, 10, 6), 'MMM') //=> 'Nov'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMM') //=> 'November'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMMM') //=> 'N'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMMMM') //=> 'November'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMMMMM') //=> 'November'`
 *
 * 3. Some patterns could be unlimited length (such as `yyyyyyyy`).
 *    The output will be padded with zeros to match the length of the pattern.
 *
 *    `format(new Date(2017, 10, 6), 'yyyyyyyy') //=> '00002017'`
 *
 * 4. `QQQQQ` and `qqqqq` could be not strictly numerical in some locales.
 *    These tokens represent the shortest form of the quarter.
 *
 * 5. The main difference between `y` and `u` patterns are B.C. years:
 *
 *    | Year | `y` | `u` |
 *    |------|-----|-----|
 *    | AC 1 |   1 |   1 |
 *    | BC 1 |   1 |   0 |
 *    | BC 2 |   2 |  -1 |
 *
 *    Also `yy` always returns the last two digits of a year,
 *    while `uu` pads single digit years to 2 characters and returns other years unchanged:
 *
 *    | Year | `yy` | `uu` |
 *    |------|------|------|
 *    | 1    |   01 |   01 |
 *    | 14   |   14 |   14 |
 *    | 376  |   76 |  376 |
 *    | 1453 |   53 | 1453 |
 *
 *    The same difference is true for local and ISO week-numbering years (`Y` and `R`),
 *    except local week-numbering years are dependent on `options.weekStartsOn`
 *    and `options.firstWeekContainsDate` (compare [getISOWeekYear]{@link https://date-fns.org/docs/getISOWeekYear}
 *    and [getWeekYear]{@link https://date-fns.org/docs/getWeekYear}).
 *
 * 6. Specific non-location timezones are currently unavailable in `date-fns`,
 *    so right now these tokens fall back to GMT timezones.
 *
 * 7. These patterns are not in the Unicode Technical Standard #35:
 *    - `i`: ISO day of week
 *    - `I`: ISO week of year
 *    - `R`: ISO week-numbering year
 *    - `t`: seconds timestamp
 *    - `T`: milliseconds timestamp
 *    - `o`: ordinal number modifier
 *    - `P`: long localized date
 *    - `p`: long localized time
 *
 * 8. `YY` and `YYYY` tokens represent week-numbering years but they are often confused with years.
 *    You should enable `options.useAdditionalWeekYearTokens` to use them. See: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 *
 * 9. `D` and `DD` tokens represent days of the year but they are often confused with days of the month.
 *    You should enable `options.useAdditionalDayOfYearTokens` to use them. See: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 *
 * @param {Date|Number} date - the original date
 * @param {String} format - the string of tokens
 * @param {Object} [options] - an object with options.
 * @param {Locale} [options.locale=defaultLocale] - the locale object. See [Locale]{@link https://date-fns.org/docs/Locale}
 * @param {0|1|2|3|4|5|6} [options.weekStartsOn=0] - the index of the first day of the week (0 - Sunday)
 * @param {Number} [options.firstWeekContainsDate=1] - the day of January, which is
 * @param {Boolean} [options.useAdditionalWeekYearTokens=false] - if true, allows usage of the week-numbering year tokens `YY` and `YYYY`;
 *   see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 * @param {Boolean} [options.useAdditionalDayOfYearTokens=false] - if true, allows usage of the day of year tokens `D` and `DD`;
 *   see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 * @returns {String} the formatted date string
 * @throws {TypeError} 2 arguments required
 * @throws {RangeError} `date` must not be Invalid Date
 * @throws {RangeError} `options.locale` must contain `localize` property
 * @throws {RangeError} `options.locale` must contain `formatLong` property
 * @throws {RangeError} `options.weekStartsOn` must be between 0 and 6
 * @throws {RangeError} `options.firstWeekContainsDate` must be between 1 and 7
 * @throws {RangeError} use `yyyy` instead of `YYYY` for formatting years using [format provided] to the input [input provided]; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 * @throws {RangeError} use `yy` instead of `YY` for formatting years using [format provided] to the input [input provided]; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 * @throws {RangeError} use `d` instead of `D` for formatting days of the month using [format provided] to the input [input provided]; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 * @throws {RangeError} use `dd` instead of `DD` for formatting days of the month using [format provided] to the input [input provided]; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 * @throws {RangeError} format string contains an unescaped latin alphabet character
 *
 * @example
 * // Represent 11 February 2014 in middle-endian format:
 * const result = format(new Date(2014, 1, 11), 'MM/dd/yyyy')
 * //=> '02/11/2014'
 *
 * @example
 * // Represent 2 July 2014 in Esperanto:
 * import { eoLocale } from 'date-fns/locale/eo'
 * const result = format(new Date(2014, 6, 2), "do 'de' MMMM yyyy", {
 *   locale: eoLocale
 * })
 * //=> '2-a de julio 2014'
 *
 * @example
 * // Escape string by single quote characters:
 * const result = format(new Date(2014, 6, 2, 15), "h 'o''clock'")
 * //=> "3 o'clock"
 */

function format(dirtyDate, dirtyFormatStr, options) {
  var _ref, _options$locale, _ref2, _ref3, _ref4, _options$firstWeekCon, _options$locale2, _options$locale2$opti, _defaultOptions$local, _defaultOptions$local2, _ref5, _ref6, _ref7, _options$weekStartsOn, _options$locale3, _options$locale3$opti, _defaultOptions$local3, _defaultOptions$local4;

  requiredArgs(2, arguments);
  var formatStr = String(dirtyFormatStr);
  var defaultOptions = getDefaultOptions();
  var locale = (_ref = (_options$locale = options === null || options === void 0 ? void 0 : options.locale) !== null && _options$locale !== void 0 ? _options$locale : defaultOptions.locale) !== null && _ref !== void 0 ? _ref : defaultLocale;
  var firstWeekContainsDate = toInteger((_ref2 = (_ref3 = (_ref4 = (_options$firstWeekCon = options === null || options === void 0 ? void 0 : options.firstWeekContainsDate) !== null && _options$firstWeekCon !== void 0 ? _options$firstWeekCon : options === null || options === void 0 ? void 0 : (_options$locale2 = options.locale) === null || _options$locale2 === void 0 ? void 0 : (_options$locale2$opti = _options$locale2.options) === null || _options$locale2$opti === void 0 ? void 0 : _options$locale2$opti.firstWeekContainsDate) !== null && _ref4 !== void 0 ? _ref4 : defaultOptions.firstWeekContainsDate) !== null && _ref3 !== void 0 ? _ref3 : (_defaultOptions$local = defaultOptions.locale) === null || _defaultOptions$local === void 0 ? void 0 : (_defaultOptions$local2 = _defaultOptions$local.options) === null || _defaultOptions$local2 === void 0 ? void 0 : _defaultOptions$local2.firstWeekContainsDate) !== null && _ref2 !== void 0 ? _ref2 : 1); // Test if weekStartsOn is between 1 and 7 _and_ is not NaN

  if (!(firstWeekContainsDate >= 1 && firstWeekContainsDate <= 7)) {
    throw new RangeError('firstWeekContainsDate must be between 1 and 7 inclusively');
  }

  var weekStartsOn = toInteger((_ref5 = (_ref6 = (_ref7 = (_options$weekStartsOn = options === null || options === void 0 ? void 0 : options.weekStartsOn) !== null && _options$weekStartsOn !== void 0 ? _options$weekStartsOn : options === null || options === void 0 ? void 0 : (_options$locale3 = options.locale) === null || _options$locale3 === void 0 ? void 0 : (_options$locale3$opti = _options$locale3.options) === null || _options$locale3$opti === void 0 ? void 0 : _options$locale3$opti.weekStartsOn) !== null && _ref7 !== void 0 ? _ref7 : defaultOptions.weekStartsOn) !== null && _ref6 !== void 0 ? _ref6 : (_defaultOptions$local3 = defaultOptions.locale) === null || _defaultOptions$local3 === void 0 ? void 0 : (_defaultOptions$local4 = _defaultOptions$local3.options) === null || _defaultOptions$local4 === void 0 ? void 0 : _defaultOptions$local4.weekStartsOn) !== null && _ref5 !== void 0 ? _ref5 : 0); // Test if weekStartsOn is between 0 and 6 _and_ is not NaN

  if (!(weekStartsOn >= 0 && weekStartsOn <= 6)) {
    throw new RangeError('weekStartsOn must be between 0 and 6 inclusively');
  }

  if (!locale.localize) {
    throw new RangeError('locale must contain localize property');
  }

  if (!locale.formatLong) {
    throw new RangeError('locale must contain formatLong property');
  }

  var originalDate = toDate(dirtyDate);

  if (!isValid(originalDate)) {
    throw new RangeError('Invalid time value');
  } // Convert the date in system timezone to the same date in UTC+00:00 timezone.
  // This ensures that when UTC functions will be implemented, locales will be compatible with them.
  // See an issue about UTC functions: https://github.com/date-fns/date-fns/issues/376


  var timezoneOffset = getTimezoneOffsetInMilliseconds(originalDate);
  var utcDate = subMilliseconds(originalDate, timezoneOffset);
  var formatterOptions = {
    firstWeekContainsDate: firstWeekContainsDate,
    weekStartsOn: weekStartsOn,
    locale: locale,
    _originalDate: originalDate
  };
  var result = formatStr.match(longFormattingTokensRegExp$1).map(function (substring) {
    var firstCharacter = substring[0];

    if (firstCharacter === 'p' || firstCharacter === 'P') {
      var longFormatter = longFormatters$1[firstCharacter];
      return longFormatter(substring, locale.formatLong);
    }

    return substring;
  }).join('').match(formattingTokensRegExp$1).map(function (substring) {
    // Replace two single quote characters with one single quote character
    if (substring === "''") {
      return "'";
    }

    var firstCharacter = substring[0];

    if (firstCharacter === "'") {
      return cleanEscapedString$1(substring);
    }

    var formatter = formatters$1[firstCharacter];

    if (formatter) {
      if (!(options !== null && options !== void 0 && options.useAdditionalWeekYearTokens) && isProtectedWeekYearToken(substring)) {
        throwProtectedError(substring, dirtyFormatStr, String(dirtyDate));
      }

      if (!(options !== null && options !== void 0 && options.useAdditionalDayOfYearTokens) && isProtectedDayOfYearToken(substring)) {
        throwProtectedError(substring, dirtyFormatStr, String(dirtyDate));
      }

      return formatter(utcDate, substring, locale.localize, formatterOptions);
    }

    if (firstCharacter.match(unescapedLatinCharacterRegExp$1)) {
      throw new RangeError('Format string contains an unescaped latin alphabet character `' + firstCharacter + '`');
    }

    return substring;
  }).join('');
  return result;
}

function cleanEscapedString$1(input) {
  var matched = input.match(escapedStringRegExp$1);

  if (!matched) {
    return input;
  }

  return matched[1].replace(doubleQuoteRegExp$1, "'");
}

function assign(target, object) {
  if (target == null) {
    throw new TypeError('assign requires that input parameter not be null or undefined');
  }

  for (var property in object) {
    if (Object.prototype.hasOwnProperty.call(object, property)) {
      target[property] = object[property];
    }
  }

  return target;
}

function _typeof$w(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$w = function _typeof(obj) { return typeof obj; }; } else { _typeof$w = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$w(obj); }

function _inherits$v(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf$v(subClass, superClass); }

function _setPrototypeOf$v(o, p) { _setPrototypeOf$v = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf$v(o, p); }

function _createSuper$v(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct$v(); return function _createSuperInternal() { var Super = _getPrototypeOf$v(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf$v(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn$v(this, result); }; }

function _possibleConstructorReturn$v(self, call) { if (call && (_typeof$w(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized$v(self); }

function _assertThisInitialized$v(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct$v() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf$v(o) { _getPrototypeOf$v = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf$v(o); }

function _classCallCheck$w(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$w(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$w(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$w(Constructor.prototype, protoProps); if (staticProps) _defineProperties$w(Constructor, staticProps); return Constructor; }

function _defineProperty$v(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var TIMEZONE_UNIT_PRIORITY = 10;
var Setter = /*#__PURE__*/function () {
  function Setter() {
    _classCallCheck$w(this, Setter);

    _defineProperty$v(this, "subPriority", 0);
  }

  _createClass$w(Setter, [{
    key: "validate",
    value: function validate(_utcDate, _options) {
      return true;
    }
  }]);

  return Setter;
}();
var ValueSetter = /*#__PURE__*/function (_Setter) {
  _inherits$v(ValueSetter, _Setter);

  var _super = _createSuper$v(ValueSetter);

  function ValueSetter(value, validateValue, setValue, priority, subPriority) {
    var _this;

    _classCallCheck$w(this, ValueSetter);

    _this = _super.call(this);
    _this.value = value;
    _this.validateValue = validateValue;
    _this.setValue = setValue;
    _this.priority = priority;

    if (subPriority) {
      _this.subPriority = subPriority;
    }

    return _this;
  }

  _createClass$w(ValueSetter, [{
    key: "validate",
    value: function validate(utcDate, options) {
      return this.validateValue(utcDate, this.value, options);
    }
  }, {
    key: "set",
    value: function set(utcDate, flags, options) {
      return this.setValue(utcDate, flags, this.value, options);
    }
  }]);

  return ValueSetter;
}(Setter);
var DateToSystemTimezoneSetter = /*#__PURE__*/function (_Setter2) {
  _inherits$v(DateToSystemTimezoneSetter, _Setter2);

  var _super2 = _createSuper$v(DateToSystemTimezoneSetter);

  function DateToSystemTimezoneSetter() {
    var _this2;

    _classCallCheck$w(this, DateToSystemTimezoneSetter);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this2 = _super2.call.apply(_super2, [this].concat(args));

    _defineProperty$v(_assertThisInitialized$v(_this2), "priority", TIMEZONE_UNIT_PRIORITY);

    _defineProperty$v(_assertThisInitialized$v(_this2), "subPriority", -1);

    return _this2;
  }

  _createClass$w(DateToSystemTimezoneSetter, [{
    key: "set",
    value: function set(date, flags) {
      if (flags.timestampIsSet) {
        return date;
      }

      var convertedDate = new Date(0);
      convertedDate.setFullYear(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
      convertedDate.setHours(date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds(), date.getUTCMilliseconds());
      return convertedDate;
    }
  }]);

  return DateToSystemTimezoneSetter;
}(Setter);

function _classCallCheck$v(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$v(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$v(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$v(Constructor.prototype, protoProps); if (staticProps) _defineProperties$v(Constructor, staticProps); return Constructor; }
var Parser = /*#__PURE__*/function () {
  function Parser() {
    _classCallCheck$v(this, Parser);
  }

  _createClass$v(Parser, [{
    key: "run",
    value: function run(dateString, token, match, options) {
      var result = this.parse(dateString, token, match, options);

      if (!result) {
        return null;
      }

      return {
        setter: new ValueSetter(result.value, this.validate, this.set, this.priority, this.subPriority),
        rest: result.rest
      };
    }
  }, {
    key: "validate",
    value: function validate(_utcDate, _value, _options) {
      return true;
    }
  }]);

  return Parser;
}();

function _typeof$v(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$v = function _typeof(obj) { return typeof obj; }; } else { _typeof$v = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$v(obj); }

function _classCallCheck$u(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$u(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$u(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$u(Constructor.prototype, protoProps); if (staticProps) _defineProperties$u(Constructor, staticProps); return Constructor; }

function _inherits$u(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf$u(subClass, superClass); }

function _setPrototypeOf$u(o, p) { _setPrototypeOf$u = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf$u(o, p); }

function _createSuper$u(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct$u(); return function _createSuperInternal() { var Super = _getPrototypeOf$u(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf$u(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn$u(this, result); }; }

function _possibleConstructorReturn$u(self, call) { if (call && (_typeof$v(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized$u(self); }

function _assertThisInitialized$u(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct$u() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf$u(o) { _getPrototypeOf$u = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf$u(o); }

function _defineProperty$u(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
var EraParser = /*#__PURE__*/function (_Parser) {
  _inherits$u(EraParser, _Parser);

  var _super = _createSuper$u(EraParser);

  function EraParser() {
    var _this;

    _classCallCheck$u(this, EraParser);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty$u(_assertThisInitialized$u(_this), "priority", 140);

    _defineProperty$u(_assertThisInitialized$u(_this), "incompatibleTokens", ['R', 'u', 't', 'T']);

    return _this;
  }

  _createClass$u(EraParser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      switch (token) {
        // AD, BC
        case 'G':
        case 'GG':
        case 'GGG':
          return match.era(dateString, {
            width: 'abbreviated'
          }) || match.era(dateString, {
            width: 'narrow'
          });
        // A, B

        case 'GGGGG':
          return match.era(dateString, {
            width: 'narrow'
          });
        // Anno Domini, Before Christ

        case 'GGGG':
        default:
          return match.era(dateString, {
            width: 'wide'
          }) || match.era(dateString, {
            width: 'abbreviated'
          }) || match.era(dateString, {
            width: 'narrow'
          });
      }
    }
  }, {
    key: "set",
    value: function set(date, flags, value) {
      flags.era = value;
      date.setUTCFullYear(value, 0, 1);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    }
  }]);

  return EraParser;
}(Parser);

/**
 * Days in 1 week.
 *
 * @name daysInWeek
 * @constant
 * @type {number}
 * @default
 */
/**
 * Milliseconds in 1 minute
 *
 * @name millisecondsInMinute
 * @constant
 * @type {number}
 * @default
 */

var millisecondsInMinute = 60000;
/**
 * Milliseconds in 1 hour
 *
 * @name millisecondsInHour
 * @constant
 * @type {number}
 * @default
 */

var millisecondsInHour = 3600000;
/**
 * Milliseconds in 1 second
 *
 * @name millisecondsInSecond
 * @constant
 * @type {number}
 * @default
 */

var millisecondsInSecond = 1000;

var numericPatterns = {
  month: /^(1[0-2]|0?\d)/,
  // 0 to 12
  date: /^(3[0-1]|[0-2]?\d)/,
  // 0 to 31
  dayOfYear: /^(36[0-6]|3[0-5]\d|[0-2]?\d?\d)/,
  // 0 to 366
  week: /^(5[0-3]|[0-4]?\d)/,
  // 0 to 53
  hour23h: /^(2[0-3]|[0-1]?\d)/,
  // 0 to 23
  hour24h: /^(2[0-4]|[0-1]?\d)/,
  // 0 to 24
  hour11h: /^(1[0-1]|0?\d)/,
  // 0 to 11
  hour12h: /^(1[0-2]|0?\d)/,
  // 0 to 12
  minute: /^[0-5]?\d/,
  // 0 to 59
  second: /^[0-5]?\d/,
  // 0 to 59
  singleDigit: /^\d/,
  // 0 to 9
  twoDigits: /^\d{1,2}/,
  // 0 to 99
  threeDigits: /^\d{1,3}/,
  // 0 to 999
  fourDigits: /^\d{1,4}/,
  // 0 to 9999
  anyDigitsSigned: /^-?\d+/,
  singleDigitSigned: /^-?\d/,
  // 0 to 9, -0 to -9
  twoDigitsSigned: /^-?\d{1,2}/,
  // 0 to 99, -0 to -99
  threeDigitsSigned: /^-?\d{1,3}/,
  // 0 to 999, -0 to -999
  fourDigitsSigned: /^-?\d{1,4}/ // 0 to 9999, -0 to -9999

};
var timezonePatterns = {
  basicOptionalMinutes: /^([+-])(\d{2})(\d{2})?|Z/,
  basic: /^([+-])(\d{2})(\d{2})|Z/,
  basicOptionalSeconds: /^([+-])(\d{2})(\d{2})((\d{2}))?|Z/,
  extended: /^([+-])(\d{2}):(\d{2})|Z/,
  extendedOptionalSeconds: /^([+-])(\d{2}):(\d{2})(:(\d{2}))?|Z/
};

function mapValue(parseFnResult, mapFn) {
  if (!parseFnResult) {
    return parseFnResult;
  }

  return {
    value: mapFn(parseFnResult.value),
    rest: parseFnResult.rest
  };
}
function parseNumericPattern(pattern, dateString) {
  var matchResult = dateString.match(pattern);

  if (!matchResult) {
    return null;
  }

  return {
    value: parseInt(matchResult[0], 10),
    rest: dateString.slice(matchResult[0].length)
  };
}
function parseTimezonePattern(pattern, dateString) {
  var matchResult = dateString.match(pattern);

  if (!matchResult) {
    return null;
  } // Input is 'Z'


  if (matchResult[0] === 'Z') {
    return {
      value: 0,
      rest: dateString.slice(1)
    };
  }

  var sign = matchResult[1] === '+' ? 1 : -1;
  var hours = matchResult[2] ? parseInt(matchResult[2], 10) : 0;
  var minutes = matchResult[3] ? parseInt(matchResult[3], 10) : 0;
  var seconds = matchResult[5] ? parseInt(matchResult[5], 10) : 0;
  return {
    value: sign * (hours * millisecondsInHour + minutes * millisecondsInMinute + seconds * millisecondsInSecond),
    rest: dateString.slice(matchResult[0].length)
  };
}
function parseAnyDigitsSigned(dateString) {
  return parseNumericPattern(numericPatterns.anyDigitsSigned, dateString);
}
function parseNDigits(n, dateString) {
  switch (n) {
    case 1:
      return parseNumericPattern(numericPatterns.singleDigit, dateString);

    case 2:
      return parseNumericPattern(numericPatterns.twoDigits, dateString);

    case 3:
      return parseNumericPattern(numericPatterns.threeDigits, dateString);

    case 4:
      return parseNumericPattern(numericPatterns.fourDigits, dateString);

    default:
      return parseNumericPattern(new RegExp('^\\d{1,' + n + '}'), dateString);
  }
}
function parseNDigitsSigned(n, dateString) {
  switch (n) {
    case 1:
      return parseNumericPattern(numericPatterns.singleDigitSigned, dateString);

    case 2:
      return parseNumericPattern(numericPatterns.twoDigitsSigned, dateString);

    case 3:
      return parseNumericPattern(numericPatterns.threeDigitsSigned, dateString);

    case 4:
      return parseNumericPattern(numericPatterns.fourDigitsSigned, dateString);

    default:
      return parseNumericPattern(new RegExp('^-?\\d{1,' + n + '}'), dateString);
  }
}
function dayPeriodEnumToHours(dayPeriod) {
  switch (dayPeriod) {
    case 'morning':
      return 4;

    case 'evening':
      return 17;

    case 'pm':
    case 'noon':
    case 'afternoon':
      return 12;

    case 'am':
    case 'midnight':
    case 'night':
    default:
      return 0;
  }
}
function normalizeTwoDigitYear(twoDigitYear, currentYear) {
  var isCommonEra = currentYear > 0; // Absolute number of the current year:
  // 1 -> 1 AC
  // 0 -> 1 BC
  // -1 -> 2 BC

  var absCurrentYear = isCommonEra ? currentYear : 1 - currentYear;
  var result;

  if (absCurrentYear <= 50) {
    result = twoDigitYear || 100;
  } else {
    var rangeEnd = absCurrentYear + 50;
    var rangeEndCentury = Math.floor(rangeEnd / 100) * 100;
    var isPreviousCentury = twoDigitYear >= rangeEnd % 100;
    result = twoDigitYear + rangeEndCentury - (isPreviousCentury ? 100 : 0);
  }

  return isCommonEra ? result : 1 - result;
}
function isLeapYearIndex(year) {
  return year % 400 === 0 || year % 4 === 0 && year % 100 !== 0;
}

function _typeof$u(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$u = function _typeof(obj) { return typeof obj; }; } else { _typeof$u = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$u(obj); }

function _classCallCheck$t(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$t(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$t(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$t(Constructor.prototype, protoProps); if (staticProps) _defineProperties$t(Constructor, staticProps); return Constructor; }

function _inherits$t(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf$t(subClass, superClass); }

function _setPrototypeOf$t(o, p) { _setPrototypeOf$t = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf$t(o, p); }

function _createSuper$t(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct$t(); return function _createSuperInternal() { var Super = _getPrototypeOf$t(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf$t(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn$t(this, result); }; }

function _possibleConstructorReturn$t(self, call) { if (call && (_typeof$u(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized$t(self); }

function _assertThisInitialized$t(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct$t() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf$t(o) { _getPrototypeOf$t = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf$t(o); }

function _defineProperty$t(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
// From http://www.unicode.org/reports/tr35/tr35-31/tr35-dates.html#Date_Format_Patterns
// | Year     |     y | yy |   yyy |  yyyy | yyyyy |
// |----------|-------|----|-------|-------|-------|
// | AD 1     |     1 | 01 |   001 |  0001 | 00001 |
// | AD 12    |    12 | 12 |   012 |  0012 | 00012 |
// | AD 123   |   123 | 23 |   123 |  0123 | 00123 |
// | AD 1234  |  1234 | 34 |  1234 |  1234 | 01234 |
// | AD 12345 | 12345 | 45 | 12345 | 12345 | 12345 |
var YearParser = /*#__PURE__*/function (_Parser) {
  _inherits$t(YearParser, _Parser);

  var _super = _createSuper$t(YearParser);

  function YearParser() {
    var _this;

    _classCallCheck$t(this, YearParser);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty$t(_assertThisInitialized$t(_this), "priority", 130);

    _defineProperty$t(_assertThisInitialized$t(_this), "incompatibleTokens", ['Y', 'R', 'u', 'w', 'I', 'i', 'e', 'c', 't', 'T']);

    return _this;
  }

  _createClass$t(YearParser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      var valueCallback = function valueCallback(year) {
        return {
          year: year,
          isTwoDigitYear: token === 'yy'
        };
      };

      switch (token) {
        case 'y':
          return mapValue(parseNDigits(4, dateString), valueCallback);

        case 'yo':
          return mapValue(match.ordinalNumber(dateString, {
            unit: 'year'
          }), valueCallback);

        default:
          return mapValue(parseNDigits(token.length, dateString), valueCallback);
      }
    }
  }, {
    key: "validate",
    value: function validate(_date, value) {
      return value.isTwoDigitYear || value.year > 0;
    }
  }, {
    key: "set",
    value: function set(date, flags, value) {
      var currentYear = date.getUTCFullYear();

      if (value.isTwoDigitYear) {
        var normalizedTwoDigitYear = normalizeTwoDigitYear(value.year, currentYear);
        date.setUTCFullYear(normalizedTwoDigitYear, 0, 1);
        date.setUTCHours(0, 0, 0, 0);
        return date;
      }

      var year = !('era' in flags) || flags.era === 1 ? value.year : 1 - value.year;
      date.setUTCFullYear(year, 0, 1);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    }
  }]);

  return YearParser;
}(Parser);

function _typeof$t(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$t = function _typeof(obj) { return typeof obj; }; } else { _typeof$t = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$t(obj); }

function _classCallCheck$s(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$s(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$s(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$s(Constructor.prototype, protoProps); if (staticProps) _defineProperties$s(Constructor, staticProps); return Constructor; }

function _inherits$s(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf$s(subClass, superClass); }

function _setPrototypeOf$s(o, p) { _setPrototypeOf$s = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf$s(o, p); }

function _createSuper$s(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct$s(); return function _createSuperInternal() { var Super = _getPrototypeOf$s(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf$s(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn$s(this, result); }; }

function _possibleConstructorReturn$s(self, call) { if (call && (_typeof$t(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized$s(self); }

function _assertThisInitialized$s(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct$s() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf$s(o) { _getPrototypeOf$s = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf$s(o); }

function _defineProperty$s(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
// Local week-numbering year
var LocalWeekYearParser = /*#__PURE__*/function (_Parser) {
  _inherits$s(LocalWeekYearParser, _Parser);

  var _super = _createSuper$s(LocalWeekYearParser);

  function LocalWeekYearParser() {
    var _this;

    _classCallCheck$s(this, LocalWeekYearParser);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty$s(_assertThisInitialized$s(_this), "priority", 130);

    _defineProperty$s(_assertThisInitialized$s(_this), "incompatibleTokens", ['y', 'R', 'u', 'Q', 'q', 'M', 'L', 'I', 'd', 'D', 'i', 't', 'T']);

    return _this;
  }

  _createClass$s(LocalWeekYearParser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      var valueCallback = function valueCallback(year) {
        return {
          year: year,
          isTwoDigitYear: token === 'YY'
        };
      };

      switch (token) {
        case 'Y':
          return mapValue(parseNDigits(4, dateString), valueCallback);

        case 'Yo':
          return mapValue(match.ordinalNumber(dateString, {
            unit: 'year'
          }), valueCallback);

        default:
          return mapValue(parseNDigits(token.length, dateString), valueCallback);
      }
    }
  }, {
    key: "validate",
    value: function validate(_date, value) {
      return value.isTwoDigitYear || value.year > 0;
    }
  }, {
    key: "set",
    value: function set(date, flags, value, options) {
      var currentYear = getUTCWeekYear(date, options);

      if (value.isTwoDigitYear) {
        var normalizedTwoDigitYear = normalizeTwoDigitYear(value.year, currentYear);
        date.setUTCFullYear(normalizedTwoDigitYear, 0, options.firstWeekContainsDate);
        date.setUTCHours(0, 0, 0, 0);
        return startOfUTCWeek(date, options);
      }

      var year = !('era' in flags) || flags.era === 1 ? value.year : 1 - value.year;
      date.setUTCFullYear(year, 0, options.firstWeekContainsDate);
      date.setUTCHours(0, 0, 0, 0);
      return startOfUTCWeek(date, options);
    }
  }]);

  return LocalWeekYearParser;
}(Parser);

function _typeof$s(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$s = function _typeof(obj) { return typeof obj; }; } else { _typeof$s = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$s(obj); }

function _classCallCheck$r(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$r(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$r(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$r(Constructor.prototype, protoProps); if (staticProps) _defineProperties$r(Constructor, staticProps); return Constructor; }

function _inherits$r(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf$r(subClass, superClass); }

function _setPrototypeOf$r(o, p) { _setPrototypeOf$r = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf$r(o, p); }

function _createSuper$r(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct$r(); return function _createSuperInternal() { var Super = _getPrototypeOf$r(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf$r(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn$r(this, result); }; }

function _possibleConstructorReturn$r(self, call) { if (call && (_typeof$s(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized$r(self); }

function _assertThisInitialized$r(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct$r() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf$r(o) { _getPrototypeOf$r = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf$r(o); }

function _defineProperty$r(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var ISOWeekYearParser = /*#__PURE__*/function (_Parser) {
  _inherits$r(ISOWeekYearParser, _Parser);

  var _super = _createSuper$r(ISOWeekYearParser);

  function ISOWeekYearParser() {
    var _this;

    _classCallCheck$r(this, ISOWeekYearParser);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty$r(_assertThisInitialized$r(_this), "priority", 130);

    _defineProperty$r(_assertThisInitialized$r(_this), "incompatibleTokens", ['G', 'y', 'Y', 'u', 'Q', 'q', 'M', 'L', 'w', 'd', 'D', 'e', 'c', 't', 'T']);

    return _this;
  }

  _createClass$r(ISOWeekYearParser, [{
    key: "parse",
    value: function parse(dateString, token) {
      if (token === 'R') {
        return parseNDigitsSigned(4, dateString);
      }

      return parseNDigitsSigned(token.length, dateString);
    }
  }, {
    key: "set",
    value: function set(_date, _flags, value) {
      var firstWeekOfYear = new Date(0);
      firstWeekOfYear.setUTCFullYear(value, 0, 4);
      firstWeekOfYear.setUTCHours(0, 0, 0, 0);
      return startOfUTCISOWeek(firstWeekOfYear);
    }
  }]);

  return ISOWeekYearParser;
}(Parser);

function _typeof$r(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$r = function _typeof(obj) { return typeof obj; }; } else { _typeof$r = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$r(obj); }

function _classCallCheck$q(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$q(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$q(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$q(Constructor.prototype, protoProps); if (staticProps) _defineProperties$q(Constructor, staticProps); return Constructor; }

function _inherits$q(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf$q(subClass, superClass); }

function _setPrototypeOf$q(o, p) { _setPrototypeOf$q = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf$q(o, p); }

function _createSuper$q(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct$q(); return function _createSuperInternal() { var Super = _getPrototypeOf$q(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf$q(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn$q(this, result); }; }

function _possibleConstructorReturn$q(self, call) { if (call && (_typeof$r(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized$q(self); }

function _assertThisInitialized$q(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct$q() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf$q(o) { _getPrototypeOf$q = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf$q(o); }

function _defineProperty$q(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
var ExtendedYearParser = /*#__PURE__*/function (_Parser) {
  _inherits$q(ExtendedYearParser, _Parser);

  var _super = _createSuper$q(ExtendedYearParser);

  function ExtendedYearParser() {
    var _this;

    _classCallCheck$q(this, ExtendedYearParser);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty$q(_assertThisInitialized$q(_this), "priority", 130);

    _defineProperty$q(_assertThisInitialized$q(_this), "incompatibleTokens", ['G', 'y', 'Y', 'R', 'w', 'I', 'i', 'e', 'c', 't', 'T']);

    return _this;
  }

  _createClass$q(ExtendedYearParser, [{
    key: "parse",
    value: function parse(dateString, token) {
      if (token === 'u') {
        return parseNDigitsSigned(4, dateString);
      }

      return parseNDigitsSigned(token.length, dateString);
    }
  }, {
    key: "set",
    value: function set(date, _flags, value) {
      date.setUTCFullYear(value, 0, 1);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    }
  }]);

  return ExtendedYearParser;
}(Parser);

function _typeof$q(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$q = function _typeof(obj) { return typeof obj; }; } else { _typeof$q = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$q(obj); }

function _classCallCheck$p(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$p(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$p(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$p(Constructor.prototype, protoProps); if (staticProps) _defineProperties$p(Constructor, staticProps); return Constructor; }

function _inherits$p(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf$p(subClass, superClass); }

function _setPrototypeOf$p(o, p) { _setPrototypeOf$p = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf$p(o, p); }

function _createSuper$p(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct$p(); return function _createSuperInternal() { var Super = _getPrototypeOf$p(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf$p(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn$p(this, result); }; }

function _possibleConstructorReturn$p(self, call) { if (call && (_typeof$q(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized$p(self); }

function _assertThisInitialized$p(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct$p() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf$p(o) { _getPrototypeOf$p = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf$p(o); }

function _defineProperty$p(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
var QuarterParser = /*#__PURE__*/function (_Parser) {
  _inherits$p(QuarterParser, _Parser);

  var _super = _createSuper$p(QuarterParser);

  function QuarterParser() {
    var _this;

    _classCallCheck$p(this, QuarterParser);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty$p(_assertThisInitialized$p(_this), "priority", 120);

    _defineProperty$p(_assertThisInitialized$p(_this), "incompatibleTokens", ['Y', 'R', 'q', 'M', 'L', 'w', 'I', 'd', 'D', 'i', 'e', 'c', 't', 'T']);

    return _this;
  }

  _createClass$p(QuarterParser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      switch (token) {
        // 1, 2, 3, 4
        case 'Q':
        case 'QQ':
          // 01, 02, 03, 04
          return parseNDigits(token.length, dateString);
        // 1st, 2nd, 3rd, 4th

        case 'Qo':
          return match.ordinalNumber(dateString, {
            unit: 'quarter'
          });
        // Q1, Q2, Q3, Q4

        case 'QQQ':
          return match.quarter(dateString, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.quarter(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
        // 1, 2, 3, 4 (narrow quarter; could be not numerical)

        case 'QQQQQ':
          return match.quarter(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
        // 1st quarter, 2nd quarter, ...

        case 'QQQQ':
        default:
          return match.quarter(dateString, {
            width: 'wide',
            context: 'formatting'
          }) || match.quarter(dateString, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.quarter(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
      }
    }
  }, {
    key: "validate",
    value: function validate(_date, value) {
      return value >= 1 && value <= 4;
    }
  }, {
    key: "set",
    value: function set(date, _flags, value) {
      date.setUTCMonth((value - 1) * 3, 1);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    }
  }]);

  return QuarterParser;
}(Parser);

function _typeof$p(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$p = function _typeof(obj) { return typeof obj; }; } else { _typeof$p = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$p(obj); }

function _classCallCheck$o(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$o(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$o(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$o(Constructor.prototype, protoProps); if (staticProps) _defineProperties$o(Constructor, staticProps); return Constructor; }

function _inherits$o(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf$o(subClass, superClass); }

function _setPrototypeOf$o(o, p) { _setPrototypeOf$o = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf$o(o, p); }

function _createSuper$o(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct$o(); return function _createSuperInternal() { var Super = _getPrototypeOf$o(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf$o(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn$o(this, result); }; }

function _possibleConstructorReturn$o(self, call) { if (call && (_typeof$p(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized$o(self); }

function _assertThisInitialized$o(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct$o() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf$o(o) { _getPrototypeOf$o = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf$o(o); }

function _defineProperty$o(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
var StandAloneQuarterParser = /*#__PURE__*/function (_Parser) {
  _inherits$o(StandAloneQuarterParser, _Parser);

  var _super = _createSuper$o(StandAloneQuarterParser);

  function StandAloneQuarterParser() {
    var _this;

    _classCallCheck$o(this, StandAloneQuarterParser);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty$o(_assertThisInitialized$o(_this), "priority", 120);

    _defineProperty$o(_assertThisInitialized$o(_this), "incompatibleTokens", ['Y', 'R', 'Q', 'M', 'L', 'w', 'I', 'd', 'D', 'i', 'e', 'c', 't', 'T']);

    return _this;
  }

  _createClass$o(StandAloneQuarterParser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      switch (token) {
        // 1, 2, 3, 4
        case 'q':
        case 'qq':
          // 01, 02, 03, 04
          return parseNDigits(token.length, dateString);
        // 1st, 2nd, 3rd, 4th

        case 'qo':
          return match.ordinalNumber(dateString, {
            unit: 'quarter'
          });
        // Q1, Q2, Q3, Q4

        case 'qqq':
          return match.quarter(dateString, {
            width: 'abbreviated',
            context: 'standalone'
          }) || match.quarter(dateString, {
            width: 'narrow',
            context: 'standalone'
          });
        // 1, 2, 3, 4 (narrow quarter; could be not numerical)

        case 'qqqqq':
          return match.quarter(dateString, {
            width: 'narrow',
            context: 'standalone'
          });
        // 1st quarter, 2nd quarter, ...

        case 'qqqq':
        default:
          return match.quarter(dateString, {
            width: 'wide',
            context: 'standalone'
          }) || match.quarter(dateString, {
            width: 'abbreviated',
            context: 'standalone'
          }) || match.quarter(dateString, {
            width: 'narrow',
            context: 'standalone'
          });
      }
    }
  }, {
    key: "validate",
    value: function validate(_date, value) {
      return value >= 1 && value <= 4;
    }
  }, {
    key: "set",
    value: function set(date, _flags, value) {
      date.setUTCMonth((value - 1) * 3, 1);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    }
  }]);

  return StandAloneQuarterParser;
}(Parser);

function _typeof$o(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$o = function _typeof(obj) { return typeof obj; }; } else { _typeof$o = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$o(obj); }

function _classCallCheck$n(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$n(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$n(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$n(Constructor.prototype, protoProps); if (staticProps) _defineProperties$n(Constructor, staticProps); return Constructor; }

function _inherits$n(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf$n(subClass, superClass); }

function _setPrototypeOf$n(o, p) { _setPrototypeOf$n = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf$n(o, p); }

function _createSuper$n(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct$n(); return function _createSuperInternal() { var Super = _getPrototypeOf$n(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf$n(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn$n(this, result); }; }

function _possibleConstructorReturn$n(self, call) { if (call && (_typeof$o(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized$n(self); }

function _assertThisInitialized$n(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct$n() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf$n(o) { _getPrototypeOf$n = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf$n(o); }

function _defineProperty$n(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
var MonthParser = /*#__PURE__*/function (_Parser) {
  _inherits$n(MonthParser, _Parser);

  var _super = _createSuper$n(MonthParser);

  function MonthParser() {
    var _this;

    _classCallCheck$n(this, MonthParser);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty$n(_assertThisInitialized$n(_this), "incompatibleTokens", ['Y', 'R', 'q', 'Q', 'L', 'w', 'I', 'D', 'i', 'e', 'c', 't', 'T']);

    _defineProperty$n(_assertThisInitialized$n(_this), "priority", 110);

    return _this;
  }

  _createClass$n(MonthParser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      var valueCallback = function valueCallback(value) {
        return value - 1;
      };

      switch (token) {
        // 1, 2, ..., 12
        case 'M':
          return mapValue(parseNumericPattern(numericPatterns.month, dateString), valueCallback);
        // 01, 02, ..., 12

        case 'MM':
          return mapValue(parseNDigits(2, dateString), valueCallback);
        // 1st, 2nd, ..., 12th

        case 'Mo':
          return mapValue(match.ordinalNumber(dateString, {
            unit: 'month'
          }), valueCallback);
        // Jan, Feb, ..., Dec

        case 'MMM':
          return match.month(dateString, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.month(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
        // J, F, ..., D

        case 'MMMMM':
          return match.month(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
        // January, February, ..., December

        case 'MMMM':
        default:
          return match.month(dateString, {
            width: 'wide',
            context: 'formatting'
          }) || match.month(dateString, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.month(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
      }
    }
  }, {
    key: "validate",
    value: function validate(_date, value) {
      return value >= 0 && value <= 11;
    }
  }, {
    key: "set",
    value: function set(date, _flags, value) {
      date.setUTCMonth(value, 1);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    }
  }]);

  return MonthParser;
}(Parser);

function _typeof$n(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$n = function _typeof(obj) { return typeof obj; }; } else { _typeof$n = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$n(obj); }

function _classCallCheck$m(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$m(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$m(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$m(Constructor.prototype, protoProps); if (staticProps) _defineProperties$m(Constructor, staticProps); return Constructor; }

function _inherits$m(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf$m(subClass, superClass); }

function _setPrototypeOf$m(o, p) { _setPrototypeOf$m = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf$m(o, p); }

function _createSuper$m(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct$m(); return function _createSuperInternal() { var Super = _getPrototypeOf$m(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf$m(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn$m(this, result); }; }

function _possibleConstructorReturn$m(self, call) { if (call && (_typeof$n(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized$m(self); }

function _assertThisInitialized$m(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct$m() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf$m(o) { _getPrototypeOf$m = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf$m(o); }

function _defineProperty$m(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
var StandAloneMonthParser = /*#__PURE__*/function (_Parser) {
  _inherits$m(StandAloneMonthParser, _Parser);

  var _super = _createSuper$m(StandAloneMonthParser);

  function StandAloneMonthParser() {
    var _this;

    _classCallCheck$m(this, StandAloneMonthParser);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty$m(_assertThisInitialized$m(_this), "priority", 110);

    _defineProperty$m(_assertThisInitialized$m(_this), "incompatibleTokens", ['Y', 'R', 'q', 'Q', 'M', 'w', 'I', 'D', 'i', 'e', 'c', 't', 'T']);

    return _this;
  }

  _createClass$m(StandAloneMonthParser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      var valueCallback = function valueCallback(value) {
        return value - 1;
      };

      switch (token) {
        // 1, 2, ..., 12
        case 'L':
          return mapValue(parseNumericPattern(numericPatterns.month, dateString), valueCallback);
        // 01, 02, ..., 12

        case 'LL':
          return mapValue(parseNDigits(2, dateString), valueCallback);
        // 1st, 2nd, ..., 12th

        case 'Lo':
          return mapValue(match.ordinalNumber(dateString, {
            unit: 'month'
          }), valueCallback);
        // Jan, Feb, ..., Dec

        case 'LLL':
          return match.month(dateString, {
            width: 'abbreviated',
            context: 'standalone'
          }) || match.month(dateString, {
            width: 'narrow',
            context: 'standalone'
          });
        // J, F, ..., D

        case 'LLLLL':
          return match.month(dateString, {
            width: 'narrow',
            context: 'standalone'
          });
        // January, February, ..., December

        case 'LLLL':
        default:
          return match.month(dateString, {
            width: 'wide',
            context: 'standalone'
          }) || match.month(dateString, {
            width: 'abbreviated',
            context: 'standalone'
          }) || match.month(dateString, {
            width: 'narrow',
            context: 'standalone'
          });
      }
    }
  }, {
    key: "validate",
    value: function validate(_date, value) {
      return value >= 0 && value <= 11;
    }
  }, {
    key: "set",
    value: function set(date, _flags, value) {
      date.setUTCMonth(value, 1);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    }
  }]);

  return StandAloneMonthParser;
}(Parser);

function setUTCWeek(dirtyDate, dirtyWeek, options) {
  requiredArgs(2, arguments);
  var date = toDate(dirtyDate);
  var week = toInteger(dirtyWeek);
  var diff = getUTCWeek(date, options) - week;
  date.setUTCDate(date.getUTCDate() - diff * 7);
  return date;
}

function _typeof$m(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$m = function _typeof(obj) { return typeof obj; }; } else { _typeof$m = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$m(obj); }

function _classCallCheck$l(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$l(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$l(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$l(Constructor.prototype, protoProps); if (staticProps) _defineProperties$l(Constructor, staticProps); return Constructor; }

function _inherits$l(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf$l(subClass, superClass); }

function _setPrototypeOf$l(o, p) { _setPrototypeOf$l = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf$l(o, p); }

function _createSuper$l(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct$l(); return function _createSuperInternal() { var Super = _getPrototypeOf$l(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf$l(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn$l(this, result); }; }

function _possibleConstructorReturn$l(self, call) { if (call && (_typeof$m(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized$l(self); }

function _assertThisInitialized$l(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct$l() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf$l(o) { _getPrototypeOf$l = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf$l(o); }

function _defineProperty$l(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var LocalWeekParser = /*#__PURE__*/function (_Parser) {
  _inherits$l(LocalWeekParser, _Parser);

  var _super = _createSuper$l(LocalWeekParser);

  function LocalWeekParser() {
    var _this;

    _classCallCheck$l(this, LocalWeekParser);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty$l(_assertThisInitialized$l(_this), "priority", 100);

    _defineProperty$l(_assertThisInitialized$l(_this), "incompatibleTokens", ['y', 'R', 'u', 'q', 'Q', 'M', 'L', 'I', 'd', 'D', 'i', 't', 'T']);

    return _this;
  }

  _createClass$l(LocalWeekParser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      switch (token) {
        case 'w':
          return parseNumericPattern(numericPatterns.week, dateString);

        case 'wo':
          return match.ordinalNumber(dateString, {
            unit: 'week'
          });

        default:
          return parseNDigits(token.length, dateString);
      }
    }
  }, {
    key: "validate",
    value: function validate(_date, value) {
      return value >= 1 && value <= 53;
    }
  }, {
    key: "set",
    value: function set(date, _flags, value, options) {
      return startOfUTCWeek(setUTCWeek(date, value, options), options);
    }
  }]);

  return LocalWeekParser;
}(Parser);

function setUTCISOWeek(dirtyDate, dirtyISOWeek) {
  requiredArgs(2, arguments);
  var date = toDate(dirtyDate);
  var isoWeek = toInteger(dirtyISOWeek);
  var diff = getUTCISOWeek(date) - isoWeek;
  date.setUTCDate(date.getUTCDate() - diff * 7);
  return date;
}

function _typeof$l(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$l = function _typeof(obj) { return typeof obj; }; } else { _typeof$l = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$l(obj); }

function _classCallCheck$k(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$k(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$k(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$k(Constructor.prototype, protoProps); if (staticProps) _defineProperties$k(Constructor, staticProps); return Constructor; }

function _inherits$k(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf$k(subClass, superClass); }

function _setPrototypeOf$k(o, p) { _setPrototypeOf$k = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf$k(o, p); }

function _createSuper$k(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct$k(); return function _createSuperInternal() { var Super = _getPrototypeOf$k(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf$k(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn$k(this, result); }; }

function _possibleConstructorReturn$k(self, call) { if (call && (_typeof$l(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized$k(self); }

function _assertThisInitialized$k(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct$k() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf$k(o) { _getPrototypeOf$k = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf$k(o); }

function _defineProperty$k(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var ISOWeekParser = /*#__PURE__*/function (_Parser) {
  _inherits$k(ISOWeekParser, _Parser);

  var _super = _createSuper$k(ISOWeekParser);

  function ISOWeekParser() {
    var _this;

    _classCallCheck$k(this, ISOWeekParser);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty$k(_assertThisInitialized$k(_this), "priority", 100);

    _defineProperty$k(_assertThisInitialized$k(_this), "incompatibleTokens", ['y', 'Y', 'u', 'q', 'Q', 'M', 'L', 'w', 'd', 'D', 'e', 'c', 't', 'T']);

    return _this;
  }

  _createClass$k(ISOWeekParser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      switch (token) {
        case 'I':
          return parseNumericPattern(numericPatterns.week, dateString);

        case 'Io':
          return match.ordinalNumber(dateString, {
            unit: 'week'
          });

        default:
          return parseNDigits(token.length, dateString);
      }
    }
  }, {
    key: "validate",
    value: function validate(_date, value) {
      return value >= 1 && value <= 53;
    }
  }, {
    key: "set",
    value: function set(date, _flags, value) {
      return startOfUTCISOWeek(setUTCISOWeek(date, value));
    }
  }]);

  return ISOWeekParser;
}(Parser);

function _typeof$k(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$k = function _typeof(obj) { return typeof obj; }; } else { _typeof$k = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$k(obj); }

function _classCallCheck$j(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$j(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$j(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$j(Constructor.prototype, protoProps); if (staticProps) _defineProperties$j(Constructor, staticProps); return Constructor; }

function _inherits$j(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf$j(subClass, superClass); }

function _setPrototypeOf$j(o, p) { _setPrototypeOf$j = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf$j(o, p); }

function _createSuper$j(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct$j(); return function _createSuperInternal() { var Super = _getPrototypeOf$j(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf$j(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn$j(this, result); }; }

function _possibleConstructorReturn$j(self, call) { if (call && (_typeof$k(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized$j(self); }

function _assertThisInitialized$j(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct$j() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf$j(o) { _getPrototypeOf$j = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf$j(o); }

function _defineProperty$j(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
var DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
var DAYS_IN_MONTH_LEAP_YEAR = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]; // Day of the month

var DateParser = /*#__PURE__*/function (_Parser) {
  _inherits$j(DateParser, _Parser);

  var _super = _createSuper$j(DateParser);

  function DateParser() {
    var _this;

    _classCallCheck$j(this, DateParser);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty$j(_assertThisInitialized$j(_this), "priority", 90);

    _defineProperty$j(_assertThisInitialized$j(_this), "subPriority", 1);

    _defineProperty$j(_assertThisInitialized$j(_this), "incompatibleTokens", ['Y', 'R', 'q', 'Q', 'w', 'I', 'D', 'i', 'e', 'c', 't', 'T']);

    return _this;
  }

  _createClass$j(DateParser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      switch (token) {
        case 'd':
          return parseNumericPattern(numericPatterns.date, dateString);

        case 'do':
          return match.ordinalNumber(dateString, {
            unit: 'date'
          });

        default:
          return parseNDigits(token.length, dateString);
      }
    }
  }, {
    key: "validate",
    value: function validate(date, value) {
      var year = date.getUTCFullYear();
      var isLeapYear = isLeapYearIndex(year);
      var month = date.getUTCMonth();

      if (isLeapYear) {
        return value >= 1 && value <= DAYS_IN_MONTH_LEAP_YEAR[month];
      } else {
        return value >= 1 && value <= DAYS_IN_MONTH[month];
      }
    }
  }, {
    key: "set",
    value: function set(date, _flags, value) {
      date.setUTCDate(value);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    }
  }]);

  return DateParser;
}(Parser);

function _typeof$j(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$j = function _typeof(obj) { return typeof obj; }; } else { _typeof$j = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$j(obj); }

function _classCallCheck$i(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$i(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$i(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$i(Constructor.prototype, protoProps); if (staticProps) _defineProperties$i(Constructor, staticProps); return Constructor; }

function _inherits$i(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf$i(subClass, superClass); }

function _setPrototypeOf$i(o, p) { _setPrototypeOf$i = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf$i(o, p); }

function _createSuper$i(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct$i(); return function _createSuperInternal() { var Super = _getPrototypeOf$i(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf$i(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn$i(this, result); }; }

function _possibleConstructorReturn$i(self, call) { if (call && (_typeof$j(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized$i(self); }

function _assertThisInitialized$i(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct$i() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf$i(o) { _getPrototypeOf$i = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf$i(o); }

function _defineProperty$i(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
var DayOfYearParser = /*#__PURE__*/function (_Parser) {
  _inherits$i(DayOfYearParser, _Parser);

  var _super = _createSuper$i(DayOfYearParser);

  function DayOfYearParser() {
    var _this;

    _classCallCheck$i(this, DayOfYearParser);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty$i(_assertThisInitialized$i(_this), "priority", 90);

    _defineProperty$i(_assertThisInitialized$i(_this), "subpriority", 1);

    _defineProperty$i(_assertThisInitialized$i(_this), "incompatibleTokens", ['Y', 'R', 'q', 'Q', 'M', 'L', 'w', 'I', 'd', 'E', 'i', 'e', 'c', 't', 'T']);

    return _this;
  }

  _createClass$i(DayOfYearParser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      switch (token) {
        case 'D':
        case 'DD':
          return parseNumericPattern(numericPatterns.dayOfYear, dateString);

        case 'Do':
          return match.ordinalNumber(dateString, {
            unit: 'date'
          });

        default:
          return parseNDigits(token.length, dateString);
      }
    }
  }, {
    key: "validate",
    value: function validate(date, value) {
      var year = date.getUTCFullYear();
      var isLeapYear = isLeapYearIndex(year);

      if (isLeapYear) {
        return value >= 1 && value <= 366;
      } else {
        return value >= 1 && value <= 365;
      }
    }
  }, {
    key: "set",
    value: function set(date, _flags, value) {
      date.setUTCMonth(0, value);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    }
  }]);

  return DayOfYearParser;
}(Parser);

function setUTCDay(dirtyDate, dirtyDay, options) {
  var _ref, _ref2, _ref3, _options$weekStartsOn, _options$locale, _options$locale$optio, _defaultOptions$local, _defaultOptions$local2;

  requiredArgs(2, arguments);
  var defaultOptions = getDefaultOptions();
  var weekStartsOn = toInteger((_ref = (_ref2 = (_ref3 = (_options$weekStartsOn = options === null || options === void 0 ? void 0 : options.weekStartsOn) !== null && _options$weekStartsOn !== void 0 ? _options$weekStartsOn : options === null || options === void 0 ? void 0 : (_options$locale = options.locale) === null || _options$locale === void 0 ? void 0 : (_options$locale$optio = _options$locale.options) === null || _options$locale$optio === void 0 ? void 0 : _options$locale$optio.weekStartsOn) !== null && _ref3 !== void 0 ? _ref3 : defaultOptions.weekStartsOn) !== null && _ref2 !== void 0 ? _ref2 : (_defaultOptions$local = defaultOptions.locale) === null || _defaultOptions$local === void 0 ? void 0 : (_defaultOptions$local2 = _defaultOptions$local.options) === null || _defaultOptions$local2 === void 0 ? void 0 : _defaultOptions$local2.weekStartsOn) !== null && _ref !== void 0 ? _ref : 0); // Test if weekStartsOn is between 0 and 6 _and_ is not NaN

  if (!(weekStartsOn >= 0 && weekStartsOn <= 6)) {
    throw new RangeError('weekStartsOn must be between 0 and 6 inclusively');
  }

  var date = toDate(dirtyDate);
  var day = toInteger(dirtyDay);
  var currentDay = date.getUTCDay();
  var remainder = day % 7;
  var dayIndex = (remainder + 7) % 7;
  var diff = (dayIndex < weekStartsOn ? 7 : 0) + day - currentDay;
  date.setUTCDate(date.getUTCDate() + diff);
  return date;
}

function _typeof$i(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$i = function _typeof(obj) { return typeof obj; }; } else { _typeof$i = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$i(obj); }

function _classCallCheck$h(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$h(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$h(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$h(Constructor.prototype, protoProps); if (staticProps) _defineProperties$h(Constructor, staticProps); return Constructor; }

function _inherits$h(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf$h(subClass, superClass); }

function _setPrototypeOf$h(o, p) { _setPrototypeOf$h = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf$h(o, p); }

function _createSuper$h(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct$h(); return function _createSuperInternal() { var Super = _getPrototypeOf$h(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf$h(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn$h(this, result); }; }

function _possibleConstructorReturn$h(self, call) { if (call && (_typeof$i(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized$h(self); }

function _assertThisInitialized$h(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct$h() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf$h(o) { _getPrototypeOf$h = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf$h(o); }

function _defineProperty$h(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var DayParser = /*#__PURE__*/function (_Parser) {
  _inherits$h(DayParser, _Parser);

  var _super = _createSuper$h(DayParser);

  function DayParser() {
    var _this;

    _classCallCheck$h(this, DayParser);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty$h(_assertThisInitialized$h(_this), "priority", 90);

    _defineProperty$h(_assertThisInitialized$h(_this), "incompatibleTokens", ['D', 'i', 'e', 'c', 't', 'T']);

    return _this;
  }

  _createClass$h(DayParser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      switch (token) {
        // Tue
        case 'E':
        case 'EE':
        case 'EEE':
          return match.day(dateString, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.day(dateString, {
            width: 'short',
            context: 'formatting'
          }) || match.day(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
        // T

        case 'EEEEE':
          return match.day(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
        // Tu

        case 'EEEEEE':
          return match.day(dateString, {
            width: 'short',
            context: 'formatting'
          }) || match.day(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
        // Tuesday

        case 'EEEE':
        default:
          return match.day(dateString, {
            width: 'wide',
            context: 'formatting'
          }) || match.day(dateString, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.day(dateString, {
            width: 'short',
            context: 'formatting'
          }) || match.day(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
      }
    }
  }, {
    key: "validate",
    value: function validate(_date, value) {
      return value >= 0 && value <= 6;
    }
  }, {
    key: "set",
    value: function set(date, _flags, value, options) {
      date = setUTCDay(date, value, options);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    }
  }]);

  return DayParser;
}(Parser);

function _typeof$h(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$h = function _typeof(obj) { return typeof obj; }; } else { _typeof$h = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$h(obj); }

function _classCallCheck$g(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$g(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$g(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$g(Constructor.prototype, protoProps); if (staticProps) _defineProperties$g(Constructor, staticProps); return Constructor; }

function _inherits$g(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf$g(subClass, superClass); }

function _setPrototypeOf$g(o, p) { _setPrototypeOf$g = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf$g(o, p); }

function _createSuper$g(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct$g(); return function _createSuperInternal() { var Super = _getPrototypeOf$g(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf$g(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn$g(this, result); }; }

function _possibleConstructorReturn$g(self, call) { if (call && (_typeof$h(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized$g(self); }

function _assertThisInitialized$g(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct$g() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf$g(o) { _getPrototypeOf$g = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf$g(o); }

function _defineProperty$g(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var LocalDayParser = /*#__PURE__*/function (_Parser) {
  _inherits$g(LocalDayParser, _Parser);

  var _super = _createSuper$g(LocalDayParser);

  function LocalDayParser() {
    var _this;

    _classCallCheck$g(this, LocalDayParser);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty$g(_assertThisInitialized$g(_this), "priority", 90);

    _defineProperty$g(_assertThisInitialized$g(_this), "incompatibleTokens", ['y', 'R', 'u', 'q', 'Q', 'M', 'L', 'I', 'd', 'D', 'E', 'i', 'c', 't', 'T']);

    return _this;
  }

  _createClass$g(LocalDayParser, [{
    key: "parse",
    value: function parse(dateString, token, match, options) {
      var valueCallback = function valueCallback(value) {
        var wholeWeekDays = Math.floor((value - 1) / 7) * 7;
        return (value + options.weekStartsOn + 6) % 7 + wholeWeekDays;
      };

      switch (token) {
        // 3
        case 'e':
        case 'ee':
          // 03
          return mapValue(parseNDigits(token.length, dateString), valueCallback);
        // 3rd

        case 'eo':
          return mapValue(match.ordinalNumber(dateString, {
            unit: 'day'
          }), valueCallback);
        // Tue

        case 'eee':
          return match.day(dateString, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.day(dateString, {
            width: 'short',
            context: 'formatting'
          }) || match.day(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
        // T

        case 'eeeee':
          return match.day(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
        // Tu

        case 'eeeeee':
          return match.day(dateString, {
            width: 'short',
            context: 'formatting'
          }) || match.day(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
        // Tuesday

        case 'eeee':
        default:
          return match.day(dateString, {
            width: 'wide',
            context: 'formatting'
          }) || match.day(dateString, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.day(dateString, {
            width: 'short',
            context: 'formatting'
          }) || match.day(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
      }
    }
  }, {
    key: "validate",
    value: function validate(_date, value) {
      return value >= 0 && value <= 6;
    }
  }, {
    key: "set",
    value: function set(date, _flags, value, options) {
      date = setUTCDay(date, value, options);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    }
  }]);

  return LocalDayParser;
}(Parser);

function _typeof$g(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$g = function _typeof(obj) { return typeof obj; }; } else { _typeof$g = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$g(obj); }

function _classCallCheck$f(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$f(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$f(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$f(Constructor.prototype, protoProps); if (staticProps) _defineProperties$f(Constructor, staticProps); return Constructor; }

function _inherits$f(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf$f(subClass, superClass); }

function _setPrototypeOf$f(o, p) { _setPrototypeOf$f = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf$f(o, p); }

function _createSuper$f(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct$f(); return function _createSuperInternal() { var Super = _getPrototypeOf$f(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf$f(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn$f(this, result); }; }

function _possibleConstructorReturn$f(self, call) { if (call && (_typeof$g(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized$f(self); }

function _assertThisInitialized$f(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct$f() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf$f(o) { _getPrototypeOf$f = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf$f(o); }

function _defineProperty$f(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var StandAloneLocalDayParser = /*#__PURE__*/function (_Parser) {
  _inherits$f(StandAloneLocalDayParser, _Parser);

  var _super = _createSuper$f(StandAloneLocalDayParser);

  function StandAloneLocalDayParser() {
    var _this;

    _classCallCheck$f(this, StandAloneLocalDayParser);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty$f(_assertThisInitialized$f(_this), "priority", 90);

    _defineProperty$f(_assertThisInitialized$f(_this), "incompatibleTokens", ['y', 'R', 'u', 'q', 'Q', 'M', 'L', 'I', 'd', 'D', 'E', 'i', 'e', 't', 'T']);

    return _this;
  }

  _createClass$f(StandAloneLocalDayParser, [{
    key: "parse",
    value: function parse(dateString, token, match, options) {
      var valueCallback = function valueCallback(value) {
        var wholeWeekDays = Math.floor((value - 1) / 7) * 7;
        return (value + options.weekStartsOn + 6) % 7 + wholeWeekDays;
      };

      switch (token) {
        // 3
        case 'c':
        case 'cc':
          // 03
          return mapValue(parseNDigits(token.length, dateString), valueCallback);
        // 3rd

        case 'co':
          return mapValue(match.ordinalNumber(dateString, {
            unit: 'day'
          }), valueCallback);
        // Tue

        case 'ccc':
          return match.day(dateString, {
            width: 'abbreviated',
            context: 'standalone'
          }) || match.day(dateString, {
            width: 'short',
            context: 'standalone'
          }) || match.day(dateString, {
            width: 'narrow',
            context: 'standalone'
          });
        // T

        case 'ccccc':
          return match.day(dateString, {
            width: 'narrow',
            context: 'standalone'
          });
        // Tu

        case 'cccccc':
          return match.day(dateString, {
            width: 'short',
            context: 'standalone'
          }) || match.day(dateString, {
            width: 'narrow',
            context: 'standalone'
          });
        // Tuesday

        case 'cccc':
        default:
          return match.day(dateString, {
            width: 'wide',
            context: 'standalone'
          }) || match.day(dateString, {
            width: 'abbreviated',
            context: 'standalone'
          }) || match.day(dateString, {
            width: 'short',
            context: 'standalone'
          }) || match.day(dateString, {
            width: 'narrow',
            context: 'standalone'
          });
      }
    }
  }, {
    key: "validate",
    value: function validate(_date, value) {
      return value >= 0 && value <= 6;
    }
  }, {
    key: "set",
    value: function set(date, _flags, value, options) {
      date = setUTCDay(date, value, options);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    }
  }]);

  return StandAloneLocalDayParser;
}(Parser);

function setUTCISODay(dirtyDate, dirtyDay) {
  requiredArgs(2, arguments);
  var day = toInteger(dirtyDay);

  if (day % 7 === 0) {
    day = day - 7;
  }

  var weekStartsOn = 1;
  var date = toDate(dirtyDate);
  var currentDay = date.getUTCDay();
  var remainder = day % 7;
  var dayIndex = (remainder + 7) % 7;
  var diff = (dayIndex < weekStartsOn ? 7 : 0) + day - currentDay;
  date.setUTCDate(date.getUTCDate() + diff);
  return date;
}

function _typeof$f(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$f = function _typeof(obj) { return typeof obj; }; } else { _typeof$f = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$f(obj); }

function _classCallCheck$e(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$e(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$e(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$e(Constructor.prototype, protoProps); if (staticProps) _defineProperties$e(Constructor, staticProps); return Constructor; }

function _inherits$e(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf$e(subClass, superClass); }

function _setPrototypeOf$e(o, p) { _setPrototypeOf$e = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf$e(o, p); }

function _createSuper$e(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct$e(); return function _createSuperInternal() { var Super = _getPrototypeOf$e(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf$e(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn$e(this, result); }; }

function _possibleConstructorReturn$e(self, call) { if (call && (_typeof$f(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized$e(self); }

function _assertThisInitialized$e(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct$e() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf$e(o) { _getPrototypeOf$e = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf$e(o); }

function _defineProperty$e(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var ISODayParser = /*#__PURE__*/function (_Parser) {
  _inherits$e(ISODayParser, _Parser);

  var _super = _createSuper$e(ISODayParser);

  function ISODayParser() {
    var _this;

    _classCallCheck$e(this, ISODayParser);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty$e(_assertThisInitialized$e(_this), "priority", 90);

    _defineProperty$e(_assertThisInitialized$e(_this), "incompatibleTokens", ['y', 'Y', 'u', 'q', 'Q', 'M', 'L', 'w', 'd', 'D', 'E', 'e', 'c', 't', 'T']);

    return _this;
  }

  _createClass$e(ISODayParser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      var valueCallback = function valueCallback(value) {
        if (value === 0) {
          return 7;
        }

        return value;
      };

      switch (token) {
        // 2
        case 'i':
        case 'ii':
          // 02
          return parseNDigits(token.length, dateString);
        // 2nd

        case 'io':
          return match.ordinalNumber(dateString, {
            unit: 'day'
          });
        // Tue

        case 'iii':
          return mapValue(match.day(dateString, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.day(dateString, {
            width: 'short',
            context: 'formatting'
          }) || match.day(dateString, {
            width: 'narrow',
            context: 'formatting'
          }), valueCallback);
        // T

        case 'iiiii':
          return mapValue(match.day(dateString, {
            width: 'narrow',
            context: 'formatting'
          }), valueCallback);
        // Tu

        case 'iiiiii':
          return mapValue(match.day(dateString, {
            width: 'short',
            context: 'formatting'
          }) || match.day(dateString, {
            width: 'narrow',
            context: 'formatting'
          }), valueCallback);
        // Tuesday

        case 'iiii':
        default:
          return mapValue(match.day(dateString, {
            width: 'wide',
            context: 'formatting'
          }) || match.day(dateString, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.day(dateString, {
            width: 'short',
            context: 'formatting'
          }) || match.day(dateString, {
            width: 'narrow',
            context: 'formatting'
          }), valueCallback);
      }
    }
  }, {
    key: "validate",
    value: function validate(_date, value) {
      return value >= 1 && value <= 7;
    }
  }, {
    key: "set",
    value: function set(date, _flags, value) {
      date = setUTCISODay(date, value);
      date.setUTCHours(0, 0, 0, 0);
      return date;
    }
  }]);

  return ISODayParser;
}(Parser);

function _typeof$e(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$e = function _typeof(obj) { return typeof obj; }; } else { _typeof$e = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$e(obj); }

function _classCallCheck$d(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$d(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$d(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$d(Constructor.prototype, protoProps); if (staticProps) _defineProperties$d(Constructor, staticProps); return Constructor; }

function _inherits$d(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf$d(subClass, superClass); }

function _setPrototypeOf$d(o, p) { _setPrototypeOf$d = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf$d(o, p); }

function _createSuper$d(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct$d(); return function _createSuperInternal() { var Super = _getPrototypeOf$d(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf$d(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn$d(this, result); }; }

function _possibleConstructorReturn$d(self, call) { if (call && (_typeof$e(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized$d(self); }

function _assertThisInitialized$d(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct$d() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf$d(o) { _getPrototypeOf$d = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf$d(o); }

function _defineProperty$d(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
var AMPMParser = /*#__PURE__*/function (_Parser) {
  _inherits$d(AMPMParser, _Parser);

  var _super = _createSuper$d(AMPMParser);

  function AMPMParser() {
    var _this;

    _classCallCheck$d(this, AMPMParser);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty$d(_assertThisInitialized$d(_this), "priority", 80);

    _defineProperty$d(_assertThisInitialized$d(_this), "incompatibleTokens", ['b', 'B', 'H', 'k', 't', 'T']);

    return _this;
  }

  _createClass$d(AMPMParser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      switch (token) {
        case 'a':
        case 'aa':
        case 'aaa':
          return match.dayPeriod(dateString, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.dayPeriod(dateString, {
            width: 'narrow',
            context: 'formatting'
          });

        case 'aaaaa':
          return match.dayPeriod(dateString, {
            width: 'narrow',
            context: 'formatting'
          });

        case 'aaaa':
        default:
          return match.dayPeriod(dateString, {
            width: 'wide',
            context: 'formatting'
          }) || match.dayPeriod(dateString, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.dayPeriod(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
      }
    }
  }, {
    key: "set",
    value: function set(date, _flags, value) {
      date.setUTCHours(dayPeriodEnumToHours(value), 0, 0, 0);
      return date;
    }
  }]);

  return AMPMParser;
}(Parser);

function _typeof$d(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$d = function _typeof(obj) { return typeof obj; }; } else { _typeof$d = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$d(obj); }

function _classCallCheck$c(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$c(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$c(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$c(Constructor.prototype, protoProps); if (staticProps) _defineProperties$c(Constructor, staticProps); return Constructor; }

function _inherits$c(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf$c(subClass, superClass); }

function _setPrototypeOf$c(o, p) { _setPrototypeOf$c = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf$c(o, p); }

function _createSuper$c(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct$c(); return function _createSuperInternal() { var Super = _getPrototypeOf$c(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf$c(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn$c(this, result); }; }

function _possibleConstructorReturn$c(self, call) { if (call && (_typeof$d(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized$c(self); }

function _assertThisInitialized$c(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct$c() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf$c(o) { _getPrototypeOf$c = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf$c(o); }

function _defineProperty$c(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
var AMPMMidnightParser = /*#__PURE__*/function (_Parser) {
  _inherits$c(AMPMMidnightParser, _Parser);

  var _super = _createSuper$c(AMPMMidnightParser);

  function AMPMMidnightParser() {
    var _this;

    _classCallCheck$c(this, AMPMMidnightParser);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty$c(_assertThisInitialized$c(_this), "priority", 80);

    _defineProperty$c(_assertThisInitialized$c(_this), "incompatibleTokens", ['a', 'B', 'H', 'k', 't', 'T']);

    return _this;
  }

  _createClass$c(AMPMMidnightParser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      switch (token) {
        case 'b':
        case 'bb':
        case 'bbb':
          return match.dayPeriod(dateString, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.dayPeriod(dateString, {
            width: 'narrow',
            context: 'formatting'
          });

        case 'bbbbb':
          return match.dayPeriod(dateString, {
            width: 'narrow',
            context: 'formatting'
          });

        case 'bbbb':
        default:
          return match.dayPeriod(dateString, {
            width: 'wide',
            context: 'formatting'
          }) || match.dayPeriod(dateString, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.dayPeriod(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
      }
    }
  }, {
    key: "set",
    value: function set(date, _flags, value) {
      date.setUTCHours(dayPeriodEnumToHours(value), 0, 0, 0);
      return date;
    }
  }]);

  return AMPMMidnightParser;
}(Parser);

function _typeof$c(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$c = function _typeof(obj) { return typeof obj; }; } else { _typeof$c = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$c(obj); }

function _classCallCheck$b(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$b(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$b(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$b(Constructor.prototype, protoProps); if (staticProps) _defineProperties$b(Constructor, staticProps); return Constructor; }

function _inherits$b(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf$b(subClass, superClass); }

function _setPrototypeOf$b(o, p) { _setPrototypeOf$b = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf$b(o, p); }

function _createSuper$b(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct$b(); return function _createSuperInternal() { var Super = _getPrototypeOf$b(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf$b(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn$b(this, result); }; }

function _possibleConstructorReturn$b(self, call) { if (call && (_typeof$c(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized$b(self); }

function _assertThisInitialized$b(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct$b() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf$b(o) { _getPrototypeOf$b = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf$b(o); }

function _defineProperty$b(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var DayPeriodParser = /*#__PURE__*/function (_Parser) {
  _inherits$b(DayPeriodParser, _Parser);

  var _super = _createSuper$b(DayPeriodParser);

  function DayPeriodParser() {
    var _this;

    _classCallCheck$b(this, DayPeriodParser);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty$b(_assertThisInitialized$b(_this), "priority", 80);

    _defineProperty$b(_assertThisInitialized$b(_this), "incompatibleTokens", ['a', 'b', 't', 'T']);

    return _this;
  }

  _createClass$b(DayPeriodParser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      switch (token) {
        case 'B':
        case 'BB':
        case 'BBB':
          return match.dayPeriod(dateString, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.dayPeriod(dateString, {
            width: 'narrow',
            context: 'formatting'
          });

        case 'BBBBB':
          return match.dayPeriod(dateString, {
            width: 'narrow',
            context: 'formatting'
          });

        case 'BBBB':
        default:
          return match.dayPeriod(dateString, {
            width: 'wide',
            context: 'formatting'
          }) || match.dayPeriod(dateString, {
            width: 'abbreviated',
            context: 'formatting'
          }) || match.dayPeriod(dateString, {
            width: 'narrow',
            context: 'formatting'
          });
      }
    }
  }, {
    key: "set",
    value: function set(date, _flags, value) {
      date.setUTCHours(dayPeriodEnumToHours(value), 0, 0, 0);
      return date;
    }
  }]);

  return DayPeriodParser;
}(Parser);

function _typeof$b(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$b = function _typeof(obj) { return typeof obj; }; } else { _typeof$b = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$b(obj); }

function _classCallCheck$a(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$a(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$a(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$a(Constructor.prototype, protoProps); if (staticProps) _defineProperties$a(Constructor, staticProps); return Constructor; }

function _inherits$a(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf$a(subClass, superClass); }

function _setPrototypeOf$a(o, p) { _setPrototypeOf$a = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf$a(o, p); }

function _createSuper$a(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct$a(); return function _createSuperInternal() { var Super = _getPrototypeOf$a(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf$a(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn$a(this, result); }; }

function _possibleConstructorReturn$a(self, call) { if (call && (_typeof$b(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized$a(self); }

function _assertThisInitialized$a(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct$a() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf$a(o) { _getPrototypeOf$a = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf$a(o); }

function _defineProperty$a(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
var Hour1to12Parser = /*#__PURE__*/function (_Parser) {
  _inherits$a(Hour1to12Parser, _Parser);

  var _super = _createSuper$a(Hour1to12Parser);

  function Hour1to12Parser() {
    var _this;

    _classCallCheck$a(this, Hour1to12Parser);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty$a(_assertThisInitialized$a(_this), "priority", 70);

    _defineProperty$a(_assertThisInitialized$a(_this), "incompatibleTokens", ['H', 'K', 'k', 't', 'T']);

    return _this;
  }

  _createClass$a(Hour1to12Parser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      switch (token) {
        case 'h':
          return parseNumericPattern(numericPatterns.hour12h, dateString);

        case 'ho':
          return match.ordinalNumber(dateString, {
            unit: 'hour'
          });

        default:
          return parseNDigits(token.length, dateString);
      }
    }
  }, {
    key: "validate",
    value: function validate(_date, value) {
      return value >= 1 && value <= 12;
    }
  }, {
    key: "set",
    value: function set(date, _flags, value) {
      var isPM = date.getUTCHours() >= 12;

      if (isPM && value < 12) {
        date.setUTCHours(value + 12, 0, 0, 0);
      } else if (!isPM && value === 12) {
        date.setUTCHours(0, 0, 0, 0);
      } else {
        date.setUTCHours(value, 0, 0, 0);
      }

      return date;
    }
  }]);

  return Hour1to12Parser;
}(Parser);

function _typeof$a(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$a = function _typeof(obj) { return typeof obj; }; } else { _typeof$a = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$a(obj); }

function _classCallCheck$9(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$9(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$9(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$9(Constructor.prototype, protoProps); if (staticProps) _defineProperties$9(Constructor, staticProps); return Constructor; }

function _inherits$9(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf$9(subClass, superClass); }

function _setPrototypeOf$9(o, p) { _setPrototypeOf$9 = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf$9(o, p); }

function _createSuper$9(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct$9(); return function _createSuperInternal() { var Super = _getPrototypeOf$9(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf$9(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn$9(this, result); }; }

function _possibleConstructorReturn$9(self, call) { if (call && (_typeof$a(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized$9(self); }

function _assertThisInitialized$9(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct$9() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf$9(o) { _getPrototypeOf$9 = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf$9(o); }

function _defineProperty$9(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
var Hour0to23Parser = /*#__PURE__*/function (_Parser) {
  _inherits$9(Hour0to23Parser, _Parser);

  var _super = _createSuper$9(Hour0to23Parser);

  function Hour0to23Parser() {
    var _this;

    _classCallCheck$9(this, Hour0to23Parser);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty$9(_assertThisInitialized$9(_this), "priority", 70);

    _defineProperty$9(_assertThisInitialized$9(_this), "incompatibleTokens", ['a', 'b', 'h', 'K', 'k', 't', 'T']);

    return _this;
  }

  _createClass$9(Hour0to23Parser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      switch (token) {
        case 'H':
          return parseNumericPattern(numericPatterns.hour23h, dateString);

        case 'Ho':
          return match.ordinalNumber(dateString, {
            unit: 'hour'
          });

        default:
          return parseNDigits(token.length, dateString);
      }
    }
  }, {
    key: "validate",
    value: function validate(_date, value) {
      return value >= 0 && value <= 23;
    }
  }, {
    key: "set",
    value: function set(date, _flags, value) {
      date.setUTCHours(value, 0, 0, 0);
      return date;
    }
  }]);

  return Hour0to23Parser;
}(Parser);

function _typeof$9(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$9 = function _typeof(obj) { return typeof obj; }; } else { _typeof$9 = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$9(obj); }

function _classCallCheck$8(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$8(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$8(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$8(Constructor.prototype, protoProps); if (staticProps) _defineProperties$8(Constructor, staticProps); return Constructor; }

function _inherits$8(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf$8(subClass, superClass); }

function _setPrototypeOf$8(o, p) { _setPrototypeOf$8 = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf$8(o, p); }

function _createSuper$8(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct$8(); return function _createSuperInternal() { var Super = _getPrototypeOf$8(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf$8(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn$8(this, result); }; }

function _possibleConstructorReturn$8(self, call) { if (call && (_typeof$9(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized$8(self); }

function _assertThisInitialized$8(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct$8() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf$8(o) { _getPrototypeOf$8 = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf$8(o); }

function _defineProperty$8(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
var Hour0To11Parser = /*#__PURE__*/function (_Parser) {
  _inherits$8(Hour0To11Parser, _Parser);

  var _super = _createSuper$8(Hour0To11Parser);

  function Hour0To11Parser() {
    var _this;

    _classCallCheck$8(this, Hour0To11Parser);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty$8(_assertThisInitialized$8(_this), "priority", 70);

    _defineProperty$8(_assertThisInitialized$8(_this), "incompatibleTokens", ['h', 'H', 'k', 't', 'T']);

    return _this;
  }

  _createClass$8(Hour0To11Parser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      switch (token) {
        case 'K':
          return parseNumericPattern(numericPatterns.hour11h, dateString);

        case 'Ko':
          return match.ordinalNumber(dateString, {
            unit: 'hour'
          });

        default:
          return parseNDigits(token.length, dateString);
      }
    }
  }, {
    key: "validate",
    value: function validate(_date, value) {
      return value >= 0 && value <= 11;
    }
  }, {
    key: "set",
    value: function set(date, _flags, value) {
      var isPM = date.getUTCHours() >= 12;

      if (isPM && value < 12) {
        date.setUTCHours(value + 12, 0, 0, 0);
      } else {
        date.setUTCHours(value, 0, 0, 0);
      }

      return date;
    }
  }]);

  return Hour0To11Parser;
}(Parser);

function _typeof$8(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$8 = function _typeof(obj) { return typeof obj; }; } else { _typeof$8 = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$8(obj); }

function _classCallCheck$7(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$7(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$7(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$7(Constructor.prototype, protoProps); if (staticProps) _defineProperties$7(Constructor, staticProps); return Constructor; }

function _inherits$7(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf$7(subClass, superClass); }

function _setPrototypeOf$7(o, p) { _setPrototypeOf$7 = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf$7(o, p); }

function _createSuper$7(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct$7(); return function _createSuperInternal() { var Super = _getPrototypeOf$7(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf$7(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn$7(this, result); }; }

function _possibleConstructorReturn$7(self, call) { if (call && (_typeof$8(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized$7(self); }

function _assertThisInitialized$7(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct$7() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf$7(o) { _getPrototypeOf$7 = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf$7(o); }

function _defineProperty$7(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
var Hour1To24Parser = /*#__PURE__*/function (_Parser) {
  _inherits$7(Hour1To24Parser, _Parser);

  var _super = _createSuper$7(Hour1To24Parser);

  function Hour1To24Parser() {
    var _this;

    _classCallCheck$7(this, Hour1To24Parser);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty$7(_assertThisInitialized$7(_this), "priority", 70);

    _defineProperty$7(_assertThisInitialized$7(_this), "incompatibleTokens", ['a', 'b', 'h', 'H', 'K', 't', 'T']);

    return _this;
  }

  _createClass$7(Hour1To24Parser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      switch (token) {
        case 'k':
          return parseNumericPattern(numericPatterns.hour24h, dateString);

        case 'ko':
          return match.ordinalNumber(dateString, {
            unit: 'hour'
          });

        default:
          return parseNDigits(token.length, dateString);
      }
    }
  }, {
    key: "validate",
    value: function validate(_date, value) {
      return value >= 1 && value <= 24;
    }
  }, {
    key: "set",
    value: function set(date, _flags, value) {
      var hours = value <= 24 ? value % 24 : value;
      date.setUTCHours(hours, 0, 0, 0);
      return date;
    }
  }]);

  return Hour1To24Parser;
}(Parser);

function _typeof$7(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$7 = function _typeof(obj) { return typeof obj; }; } else { _typeof$7 = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$7(obj); }

function _classCallCheck$6(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$6(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$6(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$6(Constructor.prototype, protoProps); if (staticProps) _defineProperties$6(Constructor, staticProps); return Constructor; }

function _inherits$6(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf$6(subClass, superClass); }

function _setPrototypeOf$6(o, p) { _setPrototypeOf$6 = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf$6(o, p); }

function _createSuper$6(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct$6(); return function _createSuperInternal() { var Super = _getPrototypeOf$6(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf$6(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn$6(this, result); }; }

function _possibleConstructorReturn$6(self, call) { if (call && (_typeof$7(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized$6(self); }

function _assertThisInitialized$6(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct$6() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf$6(o) { _getPrototypeOf$6 = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf$6(o); }

function _defineProperty$6(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
var MinuteParser = /*#__PURE__*/function (_Parser) {
  _inherits$6(MinuteParser, _Parser);

  var _super = _createSuper$6(MinuteParser);

  function MinuteParser() {
    var _this;

    _classCallCheck$6(this, MinuteParser);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty$6(_assertThisInitialized$6(_this), "priority", 60);

    _defineProperty$6(_assertThisInitialized$6(_this), "incompatibleTokens", ['t', 'T']);

    return _this;
  }

  _createClass$6(MinuteParser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      switch (token) {
        case 'm':
          return parseNumericPattern(numericPatterns.minute, dateString);

        case 'mo':
          return match.ordinalNumber(dateString, {
            unit: 'minute'
          });

        default:
          return parseNDigits(token.length, dateString);
      }
    }
  }, {
    key: "validate",
    value: function validate(_date, value) {
      return value >= 0 && value <= 59;
    }
  }, {
    key: "set",
    value: function set(date, _flags, value) {
      date.setUTCMinutes(value, 0, 0);
      return date;
    }
  }]);

  return MinuteParser;
}(Parser);

function _typeof$6(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$6 = function _typeof(obj) { return typeof obj; }; } else { _typeof$6 = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$6(obj); }

function _classCallCheck$5(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$5(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$5(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$5(Constructor.prototype, protoProps); if (staticProps) _defineProperties$5(Constructor, staticProps); return Constructor; }

function _inherits$5(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf$5(subClass, superClass); }

function _setPrototypeOf$5(o, p) { _setPrototypeOf$5 = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf$5(o, p); }

function _createSuper$5(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct$5(); return function _createSuperInternal() { var Super = _getPrototypeOf$5(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf$5(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn$5(this, result); }; }

function _possibleConstructorReturn$5(self, call) { if (call && (_typeof$6(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized$5(self); }

function _assertThisInitialized$5(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct$5() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf$5(o) { _getPrototypeOf$5 = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf$5(o); }

function _defineProperty$5(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
var SecondParser = /*#__PURE__*/function (_Parser) {
  _inherits$5(SecondParser, _Parser);

  var _super = _createSuper$5(SecondParser);

  function SecondParser() {
    var _this;

    _classCallCheck$5(this, SecondParser);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty$5(_assertThisInitialized$5(_this), "priority", 50);

    _defineProperty$5(_assertThisInitialized$5(_this), "incompatibleTokens", ['t', 'T']);

    return _this;
  }

  _createClass$5(SecondParser, [{
    key: "parse",
    value: function parse(dateString, token, match) {
      switch (token) {
        case 's':
          return parseNumericPattern(numericPatterns.second, dateString);

        case 'so':
          return match.ordinalNumber(dateString, {
            unit: 'second'
          });

        default:
          return parseNDigits(token.length, dateString);
      }
    }
  }, {
    key: "validate",
    value: function validate(_date, value) {
      return value >= 0 && value <= 59;
    }
  }, {
    key: "set",
    value: function set(date, _flags, value) {
      date.setUTCSeconds(value, 0);
      return date;
    }
  }]);

  return SecondParser;
}(Parser);

function _typeof$5(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$5 = function _typeof(obj) { return typeof obj; }; } else { _typeof$5 = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$5(obj); }

function _classCallCheck$4(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$4(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$4(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$4(Constructor.prototype, protoProps); if (staticProps) _defineProperties$4(Constructor, staticProps); return Constructor; }

function _inherits$4(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf$4(subClass, superClass); }

function _setPrototypeOf$4(o, p) { _setPrototypeOf$4 = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf$4(o, p); }

function _createSuper$4(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct$4(); return function _createSuperInternal() { var Super = _getPrototypeOf$4(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf$4(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn$4(this, result); }; }

function _possibleConstructorReturn$4(self, call) { if (call && (_typeof$5(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized$4(self); }

function _assertThisInitialized$4(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct$4() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf$4(o) { _getPrototypeOf$4 = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf$4(o); }

function _defineProperty$4(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
var FractionOfSecondParser = /*#__PURE__*/function (_Parser) {
  _inherits$4(FractionOfSecondParser, _Parser);

  var _super = _createSuper$4(FractionOfSecondParser);

  function FractionOfSecondParser() {
    var _this;

    _classCallCheck$4(this, FractionOfSecondParser);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty$4(_assertThisInitialized$4(_this), "priority", 30);

    _defineProperty$4(_assertThisInitialized$4(_this), "incompatibleTokens", ['t', 'T']);

    return _this;
  }

  _createClass$4(FractionOfSecondParser, [{
    key: "parse",
    value: function parse(dateString, token) {
      var valueCallback = function valueCallback(value) {
        return Math.floor(value * Math.pow(10, -token.length + 3));
      };

      return mapValue(parseNDigits(token.length, dateString), valueCallback);
    }
  }, {
    key: "set",
    value: function set(date, _flags, value) {
      date.setUTCMilliseconds(value);
      return date;
    }
  }]);

  return FractionOfSecondParser;
}(Parser);

function _typeof$4(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$4 = function _typeof(obj) { return typeof obj; }; } else { _typeof$4 = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$4(obj); }

function _classCallCheck$3(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$3(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$3(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$3(Constructor.prototype, protoProps); if (staticProps) _defineProperties$3(Constructor, staticProps); return Constructor; }

function _inherits$3(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf$3(subClass, superClass); }

function _setPrototypeOf$3(o, p) { _setPrototypeOf$3 = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf$3(o, p); }

function _createSuper$3(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct$3(); return function _createSuperInternal() { var Super = _getPrototypeOf$3(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf$3(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn$3(this, result); }; }

function _possibleConstructorReturn$3(self, call) { if (call && (_typeof$4(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized$3(self); }

function _assertThisInitialized$3(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct$3() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf$3(o) { _getPrototypeOf$3 = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf$3(o); }

function _defineProperty$3(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var ISOTimezoneWithZParser = /*#__PURE__*/function (_Parser) {
  _inherits$3(ISOTimezoneWithZParser, _Parser);

  var _super = _createSuper$3(ISOTimezoneWithZParser);

  function ISOTimezoneWithZParser() {
    var _this;

    _classCallCheck$3(this, ISOTimezoneWithZParser);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty$3(_assertThisInitialized$3(_this), "priority", 10);

    _defineProperty$3(_assertThisInitialized$3(_this), "incompatibleTokens", ['t', 'T', 'x']);

    return _this;
  }

  _createClass$3(ISOTimezoneWithZParser, [{
    key: "parse",
    value: function parse(dateString, token) {
      switch (token) {
        case 'X':
          return parseTimezonePattern(timezonePatterns.basicOptionalMinutes, dateString);

        case 'XX':
          return parseTimezonePattern(timezonePatterns.basic, dateString);

        case 'XXXX':
          return parseTimezonePattern(timezonePatterns.basicOptionalSeconds, dateString);

        case 'XXXXX':
          return parseTimezonePattern(timezonePatterns.extendedOptionalSeconds, dateString);

        case 'XXX':
        default:
          return parseTimezonePattern(timezonePatterns.extended, dateString);
      }
    }
  }, {
    key: "set",
    value: function set(date, flags, value) {
      if (flags.timestampIsSet) {
        return date;
      }

      return new Date(date.getTime() - value);
    }
  }]);

  return ISOTimezoneWithZParser;
}(Parser);

function _typeof$3(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$3 = function _typeof(obj) { return typeof obj; }; } else { _typeof$3 = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$3(obj); }

function _classCallCheck$2(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$2(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$2(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$2(Constructor.prototype, protoProps); if (staticProps) _defineProperties$2(Constructor, staticProps); return Constructor; }

function _inherits$2(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf$2(subClass, superClass); }

function _setPrototypeOf$2(o, p) { _setPrototypeOf$2 = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf$2(o, p); }

function _createSuper$2(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct$2(); return function _createSuperInternal() { var Super = _getPrototypeOf$2(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf$2(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn$2(this, result); }; }

function _possibleConstructorReturn$2(self, call) { if (call && (_typeof$3(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized$2(self); }

function _assertThisInitialized$2(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct$2() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf$2(o) { _getPrototypeOf$2 = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf$2(o); }

function _defineProperty$2(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }

var ISOTimezoneParser = /*#__PURE__*/function (_Parser) {
  _inherits$2(ISOTimezoneParser, _Parser);

  var _super = _createSuper$2(ISOTimezoneParser);

  function ISOTimezoneParser() {
    var _this;

    _classCallCheck$2(this, ISOTimezoneParser);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty$2(_assertThisInitialized$2(_this), "priority", 10);

    _defineProperty$2(_assertThisInitialized$2(_this), "incompatibleTokens", ['t', 'T', 'X']);

    return _this;
  }

  _createClass$2(ISOTimezoneParser, [{
    key: "parse",
    value: function parse(dateString, token) {
      switch (token) {
        case 'x':
          return parseTimezonePattern(timezonePatterns.basicOptionalMinutes, dateString);

        case 'xx':
          return parseTimezonePattern(timezonePatterns.basic, dateString);

        case 'xxxx':
          return parseTimezonePattern(timezonePatterns.basicOptionalSeconds, dateString);

        case 'xxxxx':
          return parseTimezonePattern(timezonePatterns.extendedOptionalSeconds, dateString);

        case 'xxx':
        default:
          return parseTimezonePattern(timezonePatterns.extended, dateString);
      }
    }
  }, {
    key: "set",
    value: function set(date, flags, value) {
      if (flags.timestampIsSet) {
        return date;
      }

      return new Date(date.getTime() - value);
    }
  }]);

  return ISOTimezoneParser;
}(Parser);

function _typeof$2(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$2 = function _typeof(obj) { return typeof obj; }; } else { _typeof$2 = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$2(obj); }

function _classCallCheck$1(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties$1(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass$1(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties$1(Constructor.prototype, protoProps); if (staticProps) _defineProperties$1(Constructor, staticProps); return Constructor; }

function _inherits$1(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf$1(subClass, superClass); }

function _setPrototypeOf$1(o, p) { _setPrototypeOf$1 = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf$1(o, p); }

function _createSuper$1(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct$1(); return function _createSuperInternal() { var Super = _getPrototypeOf$1(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf$1(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn$1(this, result); }; }

function _possibleConstructorReturn$1(self, call) { if (call && (_typeof$2(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized$1(self); }

function _assertThisInitialized$1(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct$1() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf$1(o) { _getPrototypeOf$1 = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf$1(o); }

function _defineProperty$1(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
var TimestampSecondsParser = /*#__PURE__*/function (_Parser) {
  _inherits$1(TimestampSecondsParser, _Parser);

  var _super = _createSuper$1(TimestampSecondsParser);

  function TimestampSecondsParser() {
    var _this;

    _classCallCheck$1(this, TimestampSecondsParser);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty$1(_assertThisInitialized$1(_this), "priority", 40);

    _defineProperty$1(_assertThisInitialized$1(_this), "incompatibleTokens", '*');

    return _this;
  }

  _createClass$1(TimestampSecondsParser, [{
    key: "parse",
    value: function parse(dateString) {
      return parseAnyDigitsSigned(dateString);
    }
  }, {
    key: "set",
    value: function set(_date, _flags, value) {
      return [new Date(value * 1000), {
        timestampIsSet: true
      }];
    }
  }]);

  return TimestampSecondsParser;
}(Parser);

function _typeof$1(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof$1 = function _typeof(obj) { return typeof obj; }; } else { _typeof$1 = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof$1(obj); }

function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties(Constructor.prototype, protoProps); if (staticProps) _defineProperties(Constructor, staticProps); return Constructor; }

function _inherits(subClass, superClass) { if (typeof superClass !== "function" && superClass !== null) { throw new TypeError("Super expression must either be null or a function"); } subClass.prototype = Object.create(superClass && superClass.prototype, { constructor: { value: subClass, writable: true, configurable: true } }); if (superClass) _setPrototypeOf(subClass, superClass); }

function _setPrototypeOf(o, p) { _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) { o.__proto__ = p; return o; }; return _setPrototypeOf(o, p); }

function _createSuper(Derived) { var hasNativeReflectConstruct = _isNativeReflectConstruct(); return function _createSuperInternal() { var Super = _getPrototypeOf(Derived), result; if (hasNativeReflectConstruct) { var NewTarget = _getPrototypeOf(this).constructor; result = Reflect.construct(Super, arguments, NewTarget); } else { result = Super.apply(this, arguments); } return _possibleConstructorReturn(this, result); }; }

function _possibleConstructorReturn(self, call) { if (call && (_typeof$1(call) === "object" || typeof call === "function")) { return call; } return _assertThisInitialized(self); }

function _assertThisInitialized(self) { if (self === void 0) { throw new ReferenceError("this hasn't been initialised - super() hasn't been called"); } return self; }

function _isNativeReflectConstruct() { if (typeof Reflect === "undefined" || !Reflect.construct) return false; if (Reflect.construct.sham) return false; if (typeof Proxy === "function") return true; try { Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {})); return true; } catch (e) { return false; } }

function _getPrototypeOf(o) { _getPrototypeOf = Object.setPrototypeOf ? Object.getPrototypeOf : function _getPrototypeOf(o) { return o.__proto__ || Object.getPrototypeOf(o); }; return _getPrototypeOf(o); }

function _defineProperty(obj, key, value) { if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
var TimestampMillisecondsParser = /*#__PURE__*/function (_Parser) {
  _inherits(TimestampMillisecondsParser, _Parser);

  var _super = _createSuper(TimestampMillisecondsParser);

  function TimestampMillisecondsParser() {
    var _this;

    _classCallCheck(this, TimestampMillisecondsParser);

    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }

    _this = _super.call.apply(_super, [this].concat(args));

    _defineProperty(_assertThisInitialized(_this), "priority", 20);

    _defineProperty(_assertThisInitialized(_this), "incompatibleTokens", '*');

    return _this;
  }

  _createClass(TimestampMillisecondsParser, [{
    key: "parse",
    value: function parse(dateString) {
      return parseAnyDigitsSigned(dateString);
    }
  }, {
    key: "set",
    value: function set(_date, _flags, value) {
      return [new Date(value), {
        timestampIsSet: true
      }];
    }
  }]);

  return TimestampMillisecondsParser;
}(Parser);

/*
 * |     | Unit                           |     | Unit                           |
 * |-----|--------------------------------|-----|--------------------------------|
 * |  a  | AM, PM                         |  A* | Milliseconds in day            |
 * |  b  | AM, PM, noon, midnight         |  B  | Flexible day period            |
 * |  c  | Stand-alone local day of week  |  C* | Localized hour w/ day period   |
 * |  d  | Day of month                   |  D  | Day of year                    |
 * |  e  | Local day of week              |  E  | Day of week                    |
 * |  f  |                                |  F* | Day of week in month           |
 * |  g* | Modified Julian day            |  G  | Era                            |
 * |  h  | Hour [1-12]                    |  H  | Hour [0-23]                    |
 * |  i! | ISO day of week                |  I! | ISO week of year               |
 * |  j* | Localized hour w/ day period   |  J* | Localized hour w/o day period  |
 * |  k  | Hour [1-24]                    |  K  | Hour [0-11]                    |
 * |  l* | (deprecated)                   |  L  | Stand-alone month              |
 * |  m  | Minute                         |  M  | Month                          |
 * |  n  |                                |  N  |                                |
 * |  o! | Ordinal number modifier        |  O* | Timezone (GMT)                 |
 * |  p  |                                |  P  |                                |
 * |  q  | Stand-alone quarter            |  Q  | Quarter                        |
 * |  r* | Related Gregorian year         |  R! | ISO week-numbering year        |
 * |  s  | Second                         |  S  | Fraction of second             |
 * |  t! | Seconds timestamp              |  T! | Milliseconds timestamp         |
 * |  u  | Extended year                  |  U* | Cyclic year                    |
 * |  v* | Timezone (generic non-locat.)  |  V* | Timezone (location)            |
 * |  w  | Local week of year             |  W* | Week of month                  |
 * |  x  | Timezone (ISO-8601 w/o Z)      |  X  | Timezone (ISO-8601)            |
 * |  y  | Year (abs)                     |  Y  | Local week-numbering year      |
 * |  z* | Timezone (specific non-locat.) |  Z* | Timezone (aliases)             |
 *
 * Letters marked by * are not implemented but reserved by Unicode standard.
 *
 * Letters marked by ! are non-standard, but implemented by date-fns:
 * - `o` modifies the previous token to turn it into an ordinal (see `parse` docs)
 * - `i` is ISO day of week. For `i` and `ii` is returns numeric ISO week days,
 *   i.e. 7 for Sunday, 1 for Monday, etc.
 * - `I` is ISO week of year, as opposed to `w` which is local week of year.
 * - `R` is ISO week-numbering year, as opposed to `Y` which is local week-numbering year.
 *   `R` is supposed to be used in conjunction with `I` and `i`
 *   for universal ISO week-numbering date, whereas
 *   `Y` is supposed to be used in conjunction with `w` and `e`
 *   for week-numbering date specific to the locale.
 */

var parsers = {
  G: new EraParser(),
  y: new YearParser(),
  Y: new LocalWeekYearParser(),
  R: new ISOWeekYearParser(),
  u: new ExtendedYearParser(),
  Q: new QuarterParser(),
  q: new StandAloneQuarterParser(),
  M: new MonthParser(),
  L: new StandAloneMonthParser(),
  w: new LocalWeekParser(),
  I: new ISOWeekParser(),
  d: new DateParser(),
  D: new DayOfYearParser(),
  E: new DayParser(),
  e: new LocalDayParser(),
  c: new StandAloneLocalDayParser(),
  i: new ISODayParser(),
  a: new AMPMParser(),
  b: new AMPMMidnightParser(),
  B: new DayPeriodParser(),
  h: new Hour1to12Parser(),
  H: new Hour0to23Parser(),
  K: new Hour0To11Parser(),
  k: new Hour1To24Parser(),
  m: new MinuteParser(),
  s: new SecondParser(),
  S: new FractionOfSecondParser(),
  X: new ISOTimezoneWithZParser(),
  x: new ISOTimezoneParser(),
  t: new TimestampSecondsParser(),
  T: new TimestampMillisecondsParser()
};

function _typeof(obj) { "@babel/helpers - typeof"; if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") { _typeof = function _typeof(obj) { return typeof obj; }; } else { _typeof = function _typeof(obj) { return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj; }; } return _typeof(obj); }

function _createForOfIteratorHelper(o, allowArrayLike) { var it; if (typeof Symbol === "undefined" || o[Symbol.iterator] == null) { if (Array.isArray(o) || (it = _unsupportedIterableToArray(o)) || allowArrayLike && o && typeof o.length === "number") { if (it) o = it; var i = 0; var F = function F() {}; return { s: F, n: function n() { if (i >= o.length) return { done: true }; return { done: false, value: o[i++] }; }, e: function e(_e) { throw _e; }, f: F }; } throw new TypeError("Invalid attempt to iterate non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); } var normalCompletion = true, didErr = false, err; return { s: function s() { it = o[Symbol.iterator](); }, n: function n() { var step = it.next(); normalCompletion = step.done; return step; }, e: function e(_e2) { didErr = true; err = _e2; }, f: function f() { try { if (!normalCompletion && it.return != null) it.return(); } finally { if (didErr) throw err; } } }; }

function _unsupportedIterableToArray(o, minLen) { if (!o) return; if (typeof o === "string") return _arrayLikeToArray(o, minLen); var n = Object.prototype.toString.call(o).slice(8, -1); if (n === "Object" && o.constructor) n = o.constructor.name; if (n === "Map" || n === "Set") return Array.from(o); if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen); }

function _arrayLikeToArray(arr, len) { if (len == null || len > arr.length) len = arr.length; for (var i = 0, arr2 = new Array(len); i < len; i++) { arr2[i] = arr[i]; } return arr2; }
// - [yYQqMLwIdDecihHKkms]o matches any available ordinal number token
//   (one of the certain letters followed by `o`)
// - (\w)\1* matches any sequences of the same letter
// - '' matches two quote characters in a row
// - '(''|[^'])+('|$) matches anything surrounded by two quote characters ('),
//   except a single quote symbol, which ends the sequence.
//   Two quote characters do not end the sequence.
//   If there is no matching single quote
//   then the sequence will continue until the end of the string.
// - . matches any single character unmatched by previous parts of the RegExps

var formattingTokensRegExp = /[yYQqMLwIdDecihHKkms]o|(\w)\1*|''|'(''|[^'])+('|$)|./g; // This RegExp catches symbols escaped by quotes, and also
// sequences of symbols P, p, and the combinations like `PPPPPPPppppp`

var longFormattingTokensRegExp = /P+p+|P+|p+|''|'(''|[^'])+('|$)|./g;
var escapedStringRegExp = /^'([^]*?)'?$/;
var doubleQuoteRegExp = /''/g;
var notWhitespaceRegExp = /\S/;
var unescapedLatinCharacterRegExp = /[a-zA-Z]/;
/**
 * @name parse
 * @category Common Helpers
 * @summary Parse the date.
 *
 * @description
 * Return the date parsed from string using the given format string.
 *
 * > ⚠️ Please note that the `format` tokens differ from Moment.js and other libraries.
 * > See: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 *
 * The characters in the format string wrapped between two single quotes characters (') are escaped.
 * Two single quotes in a row, whether inside or outside a quoted sequence, represent a 'real' single quote.
 *
 * Format of the format string is based on Unicode Technical Standard #35:
 * https://www.unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table
 * with a few additions (see note 5 below the table).
 *
 * Not all tokens are compatible. Combinations that don't make sense or could lead to bugs are prohibited
 * and will throw `RangeError`. For example usage of 24-hour format token with AM/PM token will throw an exception:
 *
 * ```javascript
 * parse('23 AM', 'HH a', new Date())
 * //=> RangeError: The format string mustn't contain `HH` and `a` at the same time
 * ```
 *
 * See the compatibility table: https://docs.google.com/spreadsheets/d/e/2PACX-1vQOPU3xUhplll6dyoMmVUXHKl_8CRDs6_ueLmex3SoqwhuolkuN3O05l4rqx5h1dKX8eb46Ul-CCSrq/pubhtml?gid=0&single=true
 *
 * Accepted format string patterns:
 * | Unit                            |Prior| Pattern | Result examples                   | Notes |
 * |---------------------------------|-----|---------|-----------------------------------|-------|
 * | Era                             | 140 | G..GGG  | AD, BC                            |       |
 * |                                 |     | GGGG    | Anno Domini, Before Christ        | 2     |
 * |                                 |     | GGGGG   | A, B                              |       |
 * | Calendar year                   | 130 | y       | 44, 1, 1900, 2017, 9999           | 4     |
 * |                                 |     | yo      | 44th, 1st, 1900th, 9999999th      | 4,5   |
 * |                                 |     | yy      | 44, 01, 00, 17                    | 4     |
 * |                                 |     | yyy     | 044, 001, 123, 999                | 4     |
 * |                                 |     | yyyy    | 0044, 0001, 1900, 2017            | 4     |
 * |                                 |     | yyyyy   | ...                               | 2,4   |
 * | Local week-numbering year       | 130 | Y       | 44, 1, 1900, 2017, 9000           | 4     |
 * |                                 |     | Yo      | 44th, 1st, 1900th, 9999999th      | 4,5   |
 * |                                 |     | YY      | 44, 01, 00, 17                    | 4,6   |
 * |                                 |     | YYY     | 044, 001, 123, 999                | 4     |
 * |                                 |     | YYYY    | 0044, 0001, 1900, 2017            | 4,6   |
 * |                                 |     | YYYYY   | ...                               | 2,4   |
 * | ISO week-numbering year         | 130 | R       | -43, 1, 1900, 2017, 9999, -9999   | 4,5   |
 * |                                 |     | RR      | -43, 01, 00, 17                   | 4,5   |
 * |                                 |     | RRR     | -043, 001, 123, 999, -999         | 4,5   |
 * |                                 |     | RRRR    | -0043, 0001, 2017, 9999, -9999    | 4,5   |
 * |                                 |     | RRRRR   | ...                               | 2,4,5 |
 * | Extended year                   | 130 | u       | -43, 1, 1900, 2017, 9999, -999    | 4     |
 * |                                 |     | uu      | -43, 01, 99, -99                  | 4     |
 * |                                 |     | uuu     | -043, 001, 123, 999, -999         | 4     |
 * |                                 |     | uuuu    | -0043, 0001, 2017, 9999, -9999    | 4     |
 * |                                 |     | uuuuu   | ...                               | 2,4   |
 * | Quarter (formatting)            | 120 | Q       | 1, 2, 3, 4                        |       |
 * |                                 |     | Qo      | 1st, 2nd, 3rd, 4th                | 5     |
 * |                                 |     | QQ      | 01, 02, 03, 04                    |       |
 * |                                 |     | QQQ     | Q1, Q2, Q3, Q4                    |       |
 * |                                 |     | QQQQ    | 1st quarter, 2nd quarter, ...     | 2     |
 * |                                 |     | QQQQQ   | 1, 2, 3, 4                        | 4     |
 * | Quarter (stand-alone)           | 120 | q       | 1, 2, 3, 4                        |       |
 * |                                 |     | qo      | 1st, 2nd, 3rd, 4th                | 5     |
 * |                                 |     | qq      | 01, 02, 03, 04                    |       |
 * |                                 |     | qqq     | Q1, Q2, Q3, Q4                    |       |
 * |                                 |     | qqqq    | 1st quarter, 2nd quarter, ...     | 2     |
 * |                                 |     | qqqqq   | 1, 2, 3, 4                        | 3     |
 * | Month (formatting)              | 110 | M       | 1, 2, ..., 12                     |       |
 * |                                 |     | Mo      | 1st, 2nd, ..., 12th               | 5     |
 * |                                 |     | MM      | 01, 02, ..., 12                   |       |
 * |                                 |     | MMM     | Jan, Feb, ..., Dec                |       |
 * |                                 |     | MMMM    | January, February, ..., December  | 2     |
 * |                                 |     | MMMMM   | J, F, ..., D                      |       |
 * | Month (stand-alone)             | 110 | L       | 1, 2, ..., 12                     |       |
 * |                                 |     | Lo      | 1st, 2nd, ..., 12th               | 5     |
 * |                                 |     | LL      | 01, 02, ..., 12                   |       |
 * |                                 |     | LLL     | Jan, Feb, ..., Dec                |       |
 * |                                 |     | LLLL    | January, February, ..., December  | 2     |
 * |                                 |     | LLLLL   | J, F, ..., D                      |       |
 * | Local week of year              | 100 | w       | 1, 2, ..., 53                     |       |
 * |                                 |     | wo      | 1st, 2nd, ..., 53th               | 5     |
 * |                                 |     | ww      | 01, 02, ..., 53                   |       |
 * | ISO week of year                | 100 | I       | 1, 2, ..., 53                     | 5     |
 * |                                 |     | Io      | 1st, 2nd, ..., 53th               | 5     |
 * |                                 |     | II      | 01, 02, ..., 53                   | 5     |
 * | Day of month                    |  90 | d       | 1, 2, ..., 31                     |       |
 * |                                 |     | do      | 1st, 2nd, ..., 31st               | 5     |
 * |                                 |     | dd      | 01, 02, ..., 31                   |       |
 * | Day of year                     |  90 | D       | 1, 2, ..., 365, 366               | 7     |
 * |                                 |     | Do      | 1st, 2nd, ..., 365th, 366th       | 5     |
 * |                                 |     | DD      | 01, 02, ..., 365, 366             | 7     |
 * |                                 |     | DDD     | 001, 002, ..., 365, 366           |       |
 * |                                 |     | DDDD    | ...                               | 2     |
 * | Day of week (formatting)        |  90 | E..EEE  | Mon, Tue, Wed, ..., Sun           |       |
 * |                                 |     | EEEE    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 |     | EEEEE   | M, T, W, T, F, S, S               |       |
 * |                                 |     | EEEEEE  | Mo, Tu, We, Th, Fr, Sa, Su        |       |
 * | ISO day of week (formatting)    |  90 | i       | 1, 2, 3, ..., 7                   | 5     |
 * |                                 |     | io      | 1st, 2nd, ..., 7th                | 5     |
 * |                                 |     | ii      | 01, 02, ..., 07                   | 5     |
 * |                                 |     | iii     | Mon, Tue, Wed, ..., Sun           | 5     |
 * |                                 |     | iiii    | Monday, Tuesday, ..., Sunday      | 2,5   |
 * |                                 |     | iiiii   | M, T, W, T, F, S, S               | 5     |
 * |                                 |     | iiiiii  | Mo, Tu, We, Th, Fr, Sa, Su        | 5     |
 * | Local day of week (formatting)  |  90 | e       | 2, 3, 4, ..., 1                   |       |
 * |                                 |     | eo      | 2nd, 3rd, ..., 1st                | 5     |
 * |                                 |     | ee      | 02, 03, ..., 01                   |       |
 * |                                 |     | eee     | Mon, Tue, Wed, ..., Sun           |       |
 * |                                 |     | eeee    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 |     | eeeee   | M, T, W, T, F, S, S               |       |
 * |                                 |     | eeeeee  | Mo, Tu, We, Th, Fr, Sa, Su        |       |
 * | Local day of week (stand-alone) |  90 | c       | 2, 3, 4, ..., 1                   |       |
 * |                                 |     | co      | 2nd, 3rd, ..., 1st                | 5     |
 * |                                 |     | cc      | 02, 03, ..., 01                   |       |
 * |                                 |     | ccc     | Mon, Tue, Wed, ..., Sun           |       |
 * |                                 |     | cccc    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 |     | ccccc   | M, T, W, T, F, S, S               |       |
 * |                                 |     | cccccc  | Mo, Tu, We, Th, Fr, Sa, Su        |       |
 * | AM, PM                          |  80 | a..aaa  | AM, PM                            |       |
 * |                                 |     | aaaa    | a.m., p.m.                        | 2     |
 * |                                 |     | aaaaa   | a, p                              |       |
 * | AM, PM, noon, midnight          |  80 | b..bbb  | AM, PM, noon, midnight            |       |
 * |                                 |     | bbbb    | a.m., p.m., noon, midnight        | 2     |
 * |                                 |     | bbbbb   | a, p, n, mi                       |       |
 * | Flexible day period             |  80 | B..BBB  | at night, in the morning, ...     |       |
 * |                                 |     | BBBB    | at night, in the morning, ...     | 2     |
 * |                                 |     | BBBBB   | at night, in the morning, ...     |       |
 * | Hour [1-12]                     |  70 | h       | 1, 2, ..., 11, 12                 |       |
 * |                                 |     | ho      | 1st, 2nd, ..., 11th, 12th         | 5     |
 * |                                 |     | hh      | 01, 02, ..., 11, 12               |       |
 * | Hour [0-23]                     |  70 | H       | 0, 1, 2, ..., 23                  |       |
 * |                                 |     | Ho      | 0th, 1st, 2nd, ..., 23rd          | 5     |
 * |                                 |     | HH      | 00, 01, 02, ..., 23               |       |
 * | Hour [0-11]                     |  70 | K       | 1, 2, ..., 11, 0                  |       |
 * |                                 |     | Ko      | 1st, 2nd, ..., 11th, 0th          | 5     |
 * |                                 |     | KK      | 01, 02, ..., 11, 00               |       |
 * | Hour [1-24]                     |  70 | k       | 24, 1, 2, ..., 23                 |       |
 * |                                 |     | ko      | 24th, 1st, 2nd, ..., 23rd         | 5     |
 * |                                 |     | kk      | 24, 01, 02, ..., 23               |       |
 * | Minute                          |  60 | m       | 0, 1, ..., 59                     |       |
 * |                                 |     | mo      | 0th, 1st, ..., 59th               | 5     |
 * |                                 |     | mm      | 00, 01, ..., 59                   |       |
 * | Second                          |  50 | s       | 0, 1, ..., 59                     |       |
 * |                                 |     | so      | 0th, 1st, ..., 59th               | 5     |
 * |                                 |     | ss      | 00, 01, ..., 59                   |       |
 * | Seconds timestamp               |  40 | t       | 512969520                         |       |
 * |                                 |     | tt      | ...                               | 2     |
 * | Fraction of second              |  30 | S       | 0, 1, ..., 9                      |       |
 * |                                 |     | SS      | 00, 01, ..., 99                   |       |
 * |                                 |     | SSS     | 000, 001, ..., 999                |       |
 * |                                 |     | SSSS    | ...                               | 2     |
 * | Milliseconds timestamp          |  20 | T       | 512969520900                      |       |
 * |                                 |     | TT      | ...                               | 2     |
 * | Timezone (ISO-8601 w/ Z)        |  10 | X       | -08, +0530, Z                     |       |
 * |                                 |     | XX      | -0800, +0530, Z                   |       |
 * |                                 |     | XXX     | -08:00, +05:30, Z                 |       |
 * |                                 |     | XXXX    | -0800, +0530, Z, +123456          | 2     |
 * |                                 |     | XXXXX   | -08:00, +05:30, Z, +12:34:56      |       |
 * | Timezone (ISO-8601 w/o Z)       |  10 | x       | -08, +0530, +00                   |       |
 * |                                 |     | xx      | -0800, +0530, +0000               |       |
 * |                                 |     | xxx     | -08:00, +05:30, +00:00            | 2     |
 * |                                 |     | xxxx    | -0800, +0530, +0000, +123456      |       |
 * |                                 |     | xxxxx   | -08:00, +05:30, +00:00, +12:34:56 |       |
 * | Long localized date             |  NA | P       | 05/29/1453                        | 5,8   |
 * |                                 |     | PP      | May 29, 1453                      |       |
 * |                                 |     | PPP     | May 29th, 1453                    |       |
 * |                                 |     | PPPP    | Sunday, May 29th, 1453            | 2,5,8 |
 * | Long localized time             |  NA | p       | 12:00 AM                          | 5,8   |
 * |                                 |     | pp      | 12:00:00 AM                       |       |
 * | Combination of date and time    |  NA | Pp      | 05/29/1453, 12:00 AM              |       |
 * |                                 |     | PPpp    | May 29, 1453, 12:00:00 AM         |       |
 * |                                 |     | PPPpp   | May 29th, 1453 at ...             |       |
 * |                                 |     | PPPPpp  | Sunday, May 29th, 1453 at ...     | 2,5,8 |
 * Notes:
 * 1. "Formatting" units (e.g. formatting quarter) in the default en-US locale
 *    are the same as "stand-alone" units, but are different in some languages.
 *    "Formatting" units are declined according to the rules of the language
 *    in the context of a date. "Stand-alone" units are always nominative singular.
 *    In `format` function, they will produce different result:
 *
 *    `format(new Date(2017, 10, 6), 'do LLLL', {locale: cs}) //=> '6. listopad'`
 *
 *    `format(new Date(2017, 10, 6), 'do MMMM', {locale: cs}) //=> '6. listopadu'`
 *
 *    `parse` will try to match both formatting and stand-alone units interchangably.
 *
 * 2. Any sequence of the identical letters is a pattern, unless it is escaped by
 *    the single quote characters (see below).
 *    If the sequence is longer than listed in table:
 *    - for numerical units (`yyyyyyyy`) `parse` will try to match a number
 *      as wide as the sequence
 *    - for text units (`MMMMMMMM`) `parse` will try to match the widest variation of the unit.
 *      These variations are marked with "2" in the last column of the table.
 *
 * 3. `QQQQQ` and `qqqqq` could be not strictly numerical in some locales.
 *    These tokens represent the shortest form of the quarter.
 *
 * 4. The main difference between `y` and `u` patterns are B.C. years:
 *
 *    | Year | `y` | `u` |
 *    |------|-----|-----|
 *    | AC 1 |   1 |   1 |
 *    | BC 1 |   1 |   0 |
 *    | BC 2 |   2 |  -1 |
 *
 *    Also `yy` will try to guess the century of two digit year by proximity with `referenceDate`:
 *
 *    `parse('50', 'yy', new Date(2018, 0, 1)) //=> Sat Jan 01 2050 00:00:00`
 *
 *    `parse('75', 'yy', new Date(2018, 0, 1)) //=> Wed Jan 01 1975 00:00:00`
 *
 *    while `uu` will just assign the year as is:
 *
 *    `parse('50', 'uu', new Date(2018, 0, 1)) //=> Sat Jan 01 0050 00:00:00`
 *
 *    `parse('75', 'uu', new Date(2018, 0, 1)) //=> Tue Jan 01 0075 00:00:00`
 *
 *    The same difference is true for local and ISO week-numbering years (`Y` and `R`),
 *    except local week-numbering years are dependent on `options.weekStartsOn`
 *    and `options.firstWeekContainsDate` (compare [setISOWeekYear]{@link https://date-fns.org/docs/setISOWeekYear}
 *    and [setWeekYear]{@link https://date-fns.org/docs/setWeekYear}).
 *
 * 5. These patterns are not in the Unicode Technical Standard #35:
 *    - `i`: ISO day of week
 *    - `I`: ISO week of year
 *    - `R`: ISO week-numbering year
 *    - `o`: ordinal number modifier
 *    - `P`: long localized date
 *    - `p`: long localized time
 *
 * 6. `YY` and `YYYY` tokens represent week-numbering years but they are often confused with years.
 *    You should enable `options.useAdditionalWeekYearTokens` to use them. See: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 *
 * 7. `D` and `DD` tokens represent days of the year but they are ofthen confused with days of the month.
 *    You should enable `options.useAdditionalDayOfYearTokens` to use them. See: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 *
 * 8. `P+` tokens do not have a defined priority since they are merely aliases to other tokens based
 *    on the given locale.
 *
 *    using `en-US` locale: `P` => `MM/dd/yyyy`
 *    using `en-US` locale: `p` => `hh:mm a`
 *    using `pt-BR` locale: `P` => `dd/MM/yyyy`
 *    using `pt-BR` locale: `p` => `HH:mm`
 *
 * Values will be assigned to the date in the descending order of its unit's priority.
 * Units of an equal priority overwrite each other in the order of appearance.
 *
 * If no values of higher priority are parsed (e.g. when parsing string 'January 1st' without a year),
 * the values will be taken from 3rd argument `referenceDate` which works as a context of parsing.
 *
 * `referenceDate` must be passed for correct work of the function.
 * If you're not sure which `referenceDate` to supply, create a new instance of Date:
 * `parse('02/11/2014', 'MM/dd/yyyy', new Date())`
 * In this case parsing will be done in the context of the current date.
 * If `referenceDate` is `Invalid Date` or a value not convertible to valid `Date`,
 * then `Invalid Date` will be returned.
 *
 * The result may vary by locale.
 *
 * If `formatString` matches with `dateString` but does not provides tokens, `referenceDate` will be returned.
 *
 * If parsing failed, `Invalid Date` will be returned.
 * Invalid Date is a Date, whose time value is NaN.
 * Time value of Date: http://es5.github.io/#x15.9.1.1
 *
 * @param {String} dateString - the string to parse
 * @param {String} formatString - the string of tokens
 * @param {Date|Number} referenceDate - defines values missing from the parsed dateString
 * @param {Object} [options] - an object with options.
 * @param {Locale} [options.locale=defaultLocale] - the locale object. See [Locale]{@link https://date-fns.org/docs/Locale}
 * @param {0|1|2|3|4|5|6} [options.weekStartsOn=0] - the index of the first day of the week (0 - Sunday)
 * @param {1|2|3|4|5|6|7} [options.firstWeekContainsDate=1] - the day of January, which is always in the first week of the year
 * @param {Boolean} [options.useAdditionalWeekYearTokens=false] - if true, allows usage of the week-numbering year tokens `YY` and `YYYY`;
 *   see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 * @param {Boolean} [options.useAdditionalDayOfYearTokens=false] - if true, allows usage of the day of year tokens `D` and `DD`;
 *   see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 * @returns {Date} the parsed date
 * @throws {TypeError} 3 arguments required
 * @throws {RangeError} `options.weekStartsOn` must be between 0 and 6
 * @throws {RangeError} `options.firstWeekContainsDate` must be between 1 and 7
 * @throws {RangeError} `options.locale` must contain `match` property
 * @throws {RangeError} use `yyyy` instead of `YYYY` for formatting years using [format provided] to the input [input provided]; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 * @throws {RangeError} use `yy` instead of `YY` for formatting years using [format provided] to the input [input provided]; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 * @throws {RangeError} use `d` instead of `D` for formatting days of the month using [format provided] to the input [input provided]; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 * @throws {RangeError} use `dd` instead of `DD` for formatting days of the month using [format provided] to the input [input provided]; see: https://github.com/date-fns/date-fns/blob/master/docs/unicodeTokens.md
 * @throws {RangeError} format string contains an unescaped latin alphabet character
 *
 * @example
 * // Parse 11 February 2014 from middle-endian format:
 * var result = parse('02/11/2014', 'MM/dd/yyyy', new Date())
 * //=> Tue Feb 11 2014 00:00:00
 *
 * @example
 * // Parse 28th of February in Esperanto locale in the context of 2010 year:
 * import eo from 'date-fns/locale/eo'
 * var result = parse('28-a de februaro', "do 'de' MMMM", new Date(2010, 0, 1), {
 *   locale: eo
 * })
 * //=> Sun Feb 28 2010 00:00:00
 */

function parse(dirtyDateString, dirtyFormatString, dirtyReferenceDate, options) {
  var _ref, _options$locale, _ref2, _ref3, _ref4, _options$firstWeekCon, _options$locale2, _options$locale2$opti, _defaultOptions$local, _defaultOptions$local2, _ref5, _ref6, _ref7, _options$weekStartsOn, _options$locale3, _options$locale3$opti, _defaultOptions$local3, _defaultOptions$local4;

  requiredArgs(3, arguments);
  var dateString = String(dirtyDateString);
  var formatString = String(dirtyFormatString);
  var defaultOptions = getDefaultOptions();
  var locale = (_ref = (_options$locale = options === null || options === void 0 ? void 0 : options.locale) !== null && _options$locale !== void 0 ? _options$locale : defaultOptions.locale) !== null && _ref !== void 0 ? _ref : defaultLocale;

  if (!locale.match) {
    throw new RangeError('locale must contain match property');
  }

  var firstWeekContainsDate = toInteger((_ref2 = (_ref3 = (_ref4 = (_options$firstWeekCon = options === null || options === void 0 ? void 0 : options.firstWeekContainsDate) !== null && _options$firstWeekCon !== void 0 ? _options$firstWeekCon : options === null || options === void 0 ? void 0 : (_options$locale2 = options.locale) === null || _options$locale2 === void 0 ? void 0 : (_options$locale2$opti = _options$locale2.options) === null || _options$locale2$opti === void 0 ? void 0 : _options$locale2$opti.firstWeekContainsDate) !== null && _ref4 !== void 0 ? _ref4 : defaultOptions.firstWeekContainsDate) !== null && _ref3 !== void 0 ? _ref3 : (_defaultOptions$local = defaultOptions.locale) === null || _defaultOptions$local === void 0 ? void 0 : (_defaultOptions$local2 = _defaultOptions$local.options) === null || _defaultOptions$local2 === void 0 ? void 0 : _defaultOptions$local2.firstWeekContainsDate) !== null && _ref2 !== void 0 ? _ref2 : 1); // Test if weekStartsOn is between 1 and 7 _and_ is not NaN

  if (!(firstWeekContainsDate >= 1 && firstWeekContainsDate <= 7)) {
    throw new RangeError('firstWeekContainsDate must be between 1 and 7 inclusively');
  }

  var weekStartsOn = toInteger((_ref5 = (_ref6 = (_ref7 = (_options$weekStartsOn = options === null || options === void 0 ? void 0 : options.weekStartsOn) !== null && _options$weekStartsOn !== void 0 ? _options$weekStartsOn : options === null || options === void 0 ? void 0 : (_options$locale3 = options.locale) === null || _options$locale3 === void 0 ? void 0 : (_options$locale3$opti = _options$locale3.options) === null || _options$locale3$opti === void 0 ? void 0 : _options$locale3$opti.weekStartsOn) !== null && _ref7 !== void 0 ? _ref7 : defaultOptions.weekStartsOn) !== null && _ref6 !== void 0 ? _ref6 : (_defaultOptions$local3 = defaultOptions.locale) === null || _defaultOptions$local3 === void 0 ? void 0 : (_defaultOptions$local4 = _defaultOptions$local3.options) === null || _defaultOptions$local4 === void 0 ? void 0 : _defaultOptions$local4.weekStartsOn) !== null && _ref5 !== void 0 ? _ref5 : 0); // Test if weekStartsOn is between 0 and 6 _and_ is not NaN

  if (!(weekStartsOn >= 0 && weekStartsOn <= 6)) {
    throw new RangeError('weekStartsOn must be between 0 and 6 inclusively');
  }

  if (formatString === '') {
    if (dateString === '') {
      return toDate(dirtyReferenceDate);
    } else {
      return new Date(NaN);
    }
  }

  var subFnOptions = {
    firstWeekContainsDate: firstWeekContainsDate,
    weekStartsOn: weekStartsOn,
    locale: locale
  }; // If timezone isn't specified, it will be set to the system timezone

  var setters = [new DateToSystemTimezoneSetter()];
  var tokens = formatString.match(longFormattingTokensRegExp).map(function (substring) {
    var firstCharacter = substring[0];

    if (firstCharacter in longFormatters$1) {
      var longFormatter = longFormatters$1[firstCharacter];
      return longFormatter(substring, locale.formatLong);
    }

    return substring;
  }).join('').match(formattingTokensRegExp);
  var usedTokens = [];

  var _iterator = _createForOfIteratorHelper(tokens),
      _step;

  try {
    var _loop = function _loop() {
      var token = _step.value;

      if (!(options !== null && options !== void 0 && options.useAdditionalWeekYearTokens) && isProtectedWeekYearToken(token)) {
        throwProtectedError(token, formatString, dirtyDateString);
      }

      if (!(options !== null && options !== void 0 && options.useAdditionalDayOfYearTokens) && isProtectedDayOfYearToken(token)) {
        throwProtectedError(token, formatString, dirtyDateString);
      }

      var firstCharacter = token[0];
      var parser = parsers[firstCharacter];

      if (parser) {
        var incompatibleTokens = parser.incompatibleTokens;

        if (Array.isArray(incompatibleTokens)) {
          var incompatibleToken = usedTokens.find(function (usedToken) {
            return incompatibleTokens.includes(usedToken.token) || usedToken.token === firstCharacter;
          });

          if (incompatibleToken) {
            throw new RangeError("The format string mustn't contain `".concat(incompatibleToken.fullToken, "` and `").concat(token, "` at the same time"));
          }
        } else if (parser.incompatibleTokens === '*' && usedTokens.length > 0) {
          throw new RangeError("The format string mustn't contain `".concat(token, "` and any other token at the same time"));
        }

        usedTokens.push({
          token: firstCharacter,
          fullToken: token
        });
        var parseResult = parser.run(dateString, token, locale.match, subFnOptions);

        if (!parseResult) {
          return {
            v: new Date(NaN)
          };
        }

        setters.push(parseResult.setter);
        dateString = parseResult.rest;
      } else {
        if (firstCharacter.match(unescapedLatinCharacterRegExp)) {
          throw new RangeError('Format string contains an unescaped latin alphabet character `' + firstCharacter + '`');
        } // Replace two single quote characters with one single quote character


        if (token === "''") {
          token = "'";
        } else if (firstCharacter === "'") {
          token = cleanEscapedString(token);
        } // Cut token from string, or, if string doesn't match the token, return Invalid Date


        if (dateString.indexOf(token) === 0) {
          dateString = dateString.slice(token.length);
        } else {
          return {
            v: new Date(NaN)
          };
        }
      }
    };

    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var _ret = _loop();

      if (_typeof(_ret) === "object") return _ret.v;
    } // Check if the remaining input contains something other than whitespace

  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }

  if (dateString.length > 0 && notWhitespaceRegExp.test(dateString)) {
    return new Date(NaN);
  }

  var uniquePrioritySetters = setters.map(function (setter) {
    return setter.priority;
  }).sort(function (a, b) {
    return b - a;
  }).filter(function (priority, index, array) {
    return array.indexOf(priority) === index;
  }).map(function (priority) {
    return setters.filter(function (setter) {
      return setter.priority === priority;
    }).sort(function (a, b) {
      return b.subPriority - a.subPriority;
    });
  }).map(function (setterArray) {
    return setterArray[0];
  });
  var date = toDate(dirtyReferenceDate);

  if (isNaN(date.getTime())) {
    return new Date(NaN);
  } // Convert the date in system timezone to the same date in UTC+00:00 timezone.


  var utcDate = subMilliseconds(date, getTimezoneOffsetInMilliseconds(date));
  var flags = {};

  var _iterator2 = _createForOfIteratorHelper(uniquePrioritySetters),
      _step2;

  try {
    for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
      var setter = _step2.value;

      if (!setter.validate(utcDate, subFnOptions)) {
        return new Date(NaN);
      }

      var result = setter.set(utcDate, flags, subFnOptions); // Result is tuple (date, flags)

      if (Array.isArray(result)) {
        utcDate = result[0];
        assign(flags, result[1]); // Result is date
      } else {
        utcDate = result;
      }
    }
  } catch (err) {
    _iterator2.e(err);
  } finally {
    _iterator2.f();
  }

  return utcDate;
}

function cleanEscapedString(input) {
  return input.match(escapedStringRegExp)[1].replace(doubleQuoteRegExp, "'");
}

/**
 * @name startOfDay
 * @category Day Helpers
 * @summary Return the start of a day for the given date.
 *
 * @description
 * Return the start of a day for the given date.
 * The result will be in the local timezone.
 *
 * @param {Date|Number} date - the original date
 * @returns {Date} the start of a day
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // The start of a day for 2 September 2014 11:55:00:
 * const result = startOfDay(new Date(2014, 8, 2, 11, 55, 0))
 * //=> Tue Sep 02 2014 00:00:00
 */

function startOfDay(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  date.setHours(0, 0, 0, 0);
  return date;
}

var MILLISECONDS_IN_DAY = 86400000;
/**
 * @name differenceInCalendarDays
 * @category Day Helpers
 * @summary Get the number of calendar days between the given dates.
 *
 * @description
 * Get the number of calendar days between the given dates. This means that the times are removed
 * from the dates and then the difference in days is calculated.
 *
 * @param {Date|Number} dateLeft - the later date
 * @param {Date|Number} dateRight - the earlier date
 * @returns {Number} the number of calendar days
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // How many calendar days are between
 * // 2 July 2011 23:00:00 and 2 July 2012 00:00:00?
 * const result = differenceInCalendarDays(
 *   new Date(2012, 6, 2, 0, 0),
 *   new Date(2011, 6, 2, 23, 0)
 * )
 * //=> 366
 * // How many calendar days are between
 * // 2 July 2011 23:59:00 and 3 July 2011 00:01:00?
 * const result = differenceInCalendarDays(
 *   new Date(2011, 6, 3, 0, 1),
 *   new Date(2011, 6, 2, 23, 59)
 * )
 * //=> 1
 */

function differenceInCalendarDays(dirtyDateLeft, dirtyDateRight) {
  requiredArgs(2, arguments);
  var startOfDayLeft = startOfDay(dirtyDateLeft);
  var startOfDayRight = startOfDay(dirtyDateRight);
  var timestampLeft = startOfDayLeft.getTime() - getTimezoneOffsetInMilliseconds(startOfDayLeft);
  var timestampRight = startOfDayRight.getTime() - getTimezoneOffsetInMilliseconds(startOfDayRight); // Round the number of days to the nearest integer
  // because the number of milliseconds in a day is not constant
  // (e.g. it's different in the day of the daylight saving time clock shift)

  return Math.round((timestampLeft - timestampRight) / MILLISECONDS_IN_DAY);
}

/**
 * @name addDays
 * @category Day Helpers
 * @summary Add the specified number of days to the given date.
 *
 * @description
 * Add the specified number of days to the given date.
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Number} amount - the amount of days to be added. Positive decimals will be rounded using `Math.floor`, decimals less than zero will be rounded using `Math.ceil`.
 * @returns {Date} - the new date with the days added
 * @throws {TypeError} - 2 arguments required
 *
 * @example
 * // Add 10 days to 1 September 2014:
 * const result = addDays(new Date(2014, 8, 1), 10)
 * //=> Thu Sep 11 2014 00:00:00
 */

function addDays(dirtyDate, dirtyAmount) {
  requiredArgs(2, arguments);
  var date = toDate(dirtyDate);
  var amount = toInteger(dirtyAmount);

  if (isNaN(amount)) {
    return new Date(NaN);
  }

  if (!amount) {
    // If 0 days, no-op to avoid changing times in the hour before end of DST
    return date;
  }

  date.setDate(date.getDate() + amount);
  return date;
}

var MILLISECONDS_IN_MINUTE = 60000;
/**
 * @name addMinutes
 * @category Minute Helpers
 * @summary Add the specified number of minutes to the given date.
 *
 * @description
 * Add the specified number of minutes to the given date.
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Number} amount - the amount of minutes to be added. Positive decimals will be rounded using `Math.floor`, decimals less than zero will be rounded using `Math.ceil`.
 * @returns {Date} the new date with the minutes added
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Add 30 minutes to 10 July 2014 12:00:00:
 * const result = addMinutes(new Date(2014, 6, 10, 12, 0), 30)
 * //=> Thu Jul 10 2014 12:30:00
 */

function addMinutes(dirtyDate, dirtyAmount) {
  requiredArgs(2, arguments);
  var amount = toInteger(dirtyAmount);
  return addMilliseconds(dirtyDate, amount * MILLISECONDS_IN_MINUTE);
}

/**
 * @name addMonths
 * @category Month Helpers
 * @summary Add the specified number of months to the given date.
 *
 * @description
 * Add the specified number of months to the given date.
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Number} amount - the amount of months to be added. Positive decimals will be rounded using `Math.floor`, decimals less than zero will be rounded using `Math.ceil`.
 * @returns {Date} the new date with the months added
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Add 5 months to 1 September 2014:
 * const result = addMonths(new Date(2014, 8, 1), 5)
 * //=> Sun Feb 01 2015 00:00:00
 */

function addMonths(dirtyDate, dirtyAmount) {
  requiredArgs(2, arguments);
  var date = toDate(dirtyDate);
  var amount = toInteger(dirtyAmount);

  if (isNaN(amount)) {
    return new Date(NaN);
  }

  if (!amount) {
    // If 0 months, no-op to avoid changing times in the hour before end of DST
    return date;
  }

  var dayOfMonth = date.getDate(); // The JS Date object supports date math by accepting out-of-bounds values for
  // month, day, etc. For example, new Date(2020, 0, 0) returns 31 Dec 2019 and
  // new Date(2020, 13, 1) returns 1 Feb 2021.  This is *almost* the behavior we
  // want except that dates will wrap around the end of a month, meaning that
  // new Date(2020, 13, 31) will return 3 Mar 2021 not 28 Feb 2021 as desired. So
  // we'll default to the end of the desired month by adding 1 to the desired
  // month and using a date of 0 to back up one day to the end of the desired
  // month.

  var endOfDesiredMonth = new Date(date.getTime());
  endOfDesiredMonth.setMonth(date.getMonth() + amount + 1, 0);
  var daysInMonth = endOfDesiredMonth.getDate();

  if (dayOfMonth >= daysInMonth) {
    // If we're already at the end of the month, then this is the correct date
    // and we're done.
    return endOfDesiredMonth;
  } else {
    // Otherwise, we now know that setting the original day-of-month value won't
    // cause an overflow, so set the desired day-of-month. Note that we can't
    // just set the date of `endOfDesiredMonth` because that object may have had
    // its time changed in the unusual case where where a DST transition was on
    // the last day of the month and its local time was in the hour skipped or
    // repeated next to a DST transition.  So we use `date` instead which is
    // guaranteed to still have the original time.
    date.setFullYear(endOfDesiredMonth.getFullYear(), endOfDesiredMonth.getMonth(), dayOfMonth);
    return date;
  }
}

/**
 * Regex to match a time value in the format of `HH:mm` (e.g. 14:30, 2:0).
 */
const time$1 = /^\d{1,2}:\d{1,2}$/;

/**
 * Regex to match each component of a time value.
 *
 * @example
 * // returns ['14', '30']
 * const [hours, minutes] = '14:30'.match(regex.timeComponents);
 */
const timeComponents = /\w{1,2}/g;

var _this = undefined;

/**
 * Check that `date` is a valid Javascript date object. Even though `Invalid Date` is a Javascript
 * date object, it would not be considered valid.
 *
 * @param {Date | Null} date A Javascript date object or `null`
 * @returns {boolean} A flag that indicates whether or not the given date was valid
 */
const isDateValid = date => {
  // if `date.getTime()` returns `NaN`, the date is an `Invalid Date`
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return false;
  }
  return true;
};

/**
 * Given a string that represents a time, return a boolean that indicates whether or not the value
 * is a valid time.
 *
 * @param {string} time A string that represents a time in the `HH:mm` format
 * @returns {boolean} A flag that indicates whether or not the given time was valid
 */
const isTimeValid = time => {
  const [hours, minutes] = time.match(timeComponents);
  return typeof time === 'string' && hours > -1 && hours < 24 && minutes > -1 && minutes < 61;
};

/**
 * Get an "accurate" date given a `year`, `month` and optionally a `day`. The built in Date
 * constructor for Javascript would convert `new Date(1, 0, 1)` to be 1/1/1900. In order to more
 * accurately work with Dates prior to 1900, we need to preformat the arguments going into the Date
 * constructor to generate dates prior to 1900.
 *
 * **NOTE 1**: This function will not work for dates from before the year 1. Currently the Calendar
 * component does not support these dates.
 *
 * **NOTE 2**: Despite generating dates closer to what you may expect, there are still problems
 * associated with this. Dates prior to the year 1900 are not reliable. e.g. in the year 1582,
 * October 5th - 14th were skipped. Javascript will not recognize this.
 *
 * @param {number} year the year to create a new date with
 * @param {number} month the month to create a new date with
 * @param {number} [day] the day to create a new date with
 * @param {number} [hours=0] the hours to create a new date with
 * @param {number} [minutes=0] the minutes to create a new date with
 * @returns {Date} a new date created with `year`, `month` and `day`
 */
const getAccurateDate = function (year, month, day, hours, minutes) {
  if (hours === void 0) {
    hours = 0;
  }
  if (minutes === void 0) {
    minutes = 0;
  }
  // dates prior to 1900 can't be created normally
  if (year > 1899) {
    return new Date(year, month, day, hours, minutes);
  }
  const accurateDate = new Date();
  accurateDate.setFullYear(year);
  accurateDate.setMonth(month);
  accurateDate.setDate(day);
  accurateDate.setHours(hours);
  accurateDate.setMinutes(minutes);

  // some weird edge cases can arise where seconds/milliseconds are added
  accurateDate.setSeconds(0);
  accurateDate.setMilliseconds(0);
  return accurateDate;
};

/**
 * Given a date, create a new date based off of the given date, but with the given time. If no time
 * is supplied, the time will be set to 00:00 (12:00 AM)
 *
 * 12/01/2022, 4:31PM => 12/01/2022, 12:00AM.
 *
 * @param {Date | null} date Date with a time value that should be sanitized
 * @param {string} [time='00:00'] A 24 hour time formatted HH:mm
 * @returns {Date | null} A Javascript date object with a time value
 */
const getDateWithTime = function (date, time) {
  if (time === void 0) {
    time = '00:00';
  }
  if (!isDateValid(date)) return null;
  const [hours = '', minutes = ''] = time.match(timeComponents) || [];
  return getAccurateDate(date.getFullYear(), date.getMonth(), date.getDate(), Number(hours), Number(minutes));
};

/**
 * Given a date string and a Javascript date object, check to see if both values represent the same
 * exact date.
 *
 * **NOTE:** This comparison ignores the time value in `date`. We assume that we don't care about
 * the time value because the calendar's input doesn't handle time values. All time values are
 * handled by the `Calendar.TimeInput` component.
 *
 * @param {Date | null} date A Javascript date object
 * @param {string} dateString A string that may or may not represent a valid Javascript date object
 * @param {string} dateFormat A date format string
 * @returns {boolean}
 */
const isDateStringEqualToDate = (date, dateString, dateFormat) => {
  // date is null, if `dateString` is `''`, then return true
  if (!date) return !dateString;
  return getDateWithTime(date).getTime() === parse(dateString, dateFormat, new Date()).getTime();
};

/**
 * Given a time, convert the time to the number of minutes in the given time.
 *
 * @param {string} time time to convert to minutes
 * @returns {number} number of minutes in given time
 */
const getTimeInMinutes = time => {
  if (!time) return 0;
  const [hours = '', minutes = ''] = time.match(timeComponents) || [];
  return Number(hours) * 60 + Number(minutes);
};

/**
 * An object containing utilities to format a date into 12 hour or 24 hour strings.
 */
const getTimeString = Object.freeze({
  /**
    * Given a number that represents a time in minutes, convert that number into a string.
    *
    * @param {number} timeNumber A number that represents a time in minutes
    * @returns {string} The time as a string
    */
  fromNumber: timeNumber => {
    const hours = Math.floor(timeNumber / 60);
    const minutes = timeNumber % 60;
    return `${hours > 9 ? hours : `0${hours}`}:${minutes > 9 ? minutes : `0${minutes}`}`;
  },
  /**
   * Given a date, return the time value as a string with the format `HH:mm a`.
   *
   * @param {Date} date date containing information for generating time string
   * @returns {string} formatted string representing a time (12hrs) in the time select menu
   */
  twelveHour: date => {
    const hours24 = date.getHours();
    const hours12 = hours24 % 12;
    const minutes = date.getMinutes();
    const formattedHours = hours12 > 0 && hours12 < 10 ? `0${hours12}` : hours12 || 12;
    const formattedMinutes = minutes < 10 ? `0${minutes}` : minutes;
    return `${formattedHours}:${formattedMinutes} ${hours24 < 12 ? 'AM' : 'PM'}`;
  },
  /**
   * Given a date, return the time value as a string with the format `HH:mm`.
   *
   * @param {Date} date date containing information for generating time string
   * @returns {string} formatted string representing a time (24hrs) in the time select menu
   */
  twentyFourHour: date => {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    return `${hours > 9 ? hours : `0${hours}`}:${minutes > 9 ? minutes : `0${minutes}`}`;
  }
});

/**
 * Given a Javascript date object, determine whether or not the date object is within a valid range.
 * If it is, return true, if not, return false. `null` is considered valid.
 *
 * @param {Date | null} date A Javascript date object
 * @param {Date} minDate A Javascript date object signifying the start of the range
 * @param {Date} maxDate A Javascript date object signifying the end of the range
 * @returns {boolean} A flag that indicates whether or not the date is within range.
 */
const isDateWithinRange = (date, minDate, maxDate) => {
  // null is ok
  if (!date) return true;
  const sanitizedTimeStamp = date >= minDate && date <= maxDate ? date?.getTime() : null;
  return date.getTime() === sanitizedTimeStamp;
};

/**
 * Checks if a given date is within view of the calendar.
 *
 * @param {Date | null} date A Javascript date object
 * @param {0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11} month The month currently being
 * displayed in the calendar
 * @param {number} year The year currently being displayed in the calendar
 * @param {1 | 2} numberOfMonths The number of months displayed in the calendar
 * @returns {boolean}
 */
const isDateOutsideOfView = (date, month, year, numberOfMonths) => {
  // we don't have to update the month/year of the calendar for a null value
  if (date === null) return false;
  const currentCalendarDate = getAccurateDate(year, month, 1);
  const dateIsBeforeFirstMonthInCalendar = date < currentCalendarDate;
  const dateIsAfterLastMonthInCalendar = date > addDays(addMonths(currentCalendarDate, numberOfMonths), -1);
  return dateIsBeforeFirstMonthInCalendar || dateIsAfterLastMonthInCalendar;
};

/**
 * Given a date, determine whether or not that date is disabled.
 *
 * @param {{
 *   date: Date,
 *   disabledDates: { dates: Date[], weekDays: number[] }
 *   maxDate: Date,
 *   minDate: Date,
 * }} arguments Arguments needed to determine if `date` is disabled
 * @returns {boolean}
 */
const isDateDisabled = _ref => {
  let {
    date,
    disabledDates,
    maxDate,
    minDate
  } = _ref;
  return getDateWithTime(date) < getDateWithTime(minDate) || getDateWithTime(date) > getDateWithTime(maxDate) || disabledDates.dates.some(d => d.getTime() === date.getTime()) || disabledDates.weekDays.includes(date.getDay());
};

/**
 * Based on a set of criteria, pick a date to use as the default date in the Calendar component.
 *
 * If the calendar is supplied with a default date (`selectedDate`, `startDate` or `endDate`), we
 * need to pick a default date using this date to make sure that the selected date is visible in the
 * calendar when the component loads. If the user selects a date in the `TimeSelect` before choosing
 * a date, we need a default date to add the time to and set that as the `selectedDate`
 * (or `startDate`/`endDate`).
 *
 * **Date Priority** - assumes these dates aren't disabled
 * 1. date already visible in the calendar
 * 2. `endDate`
 * 3. `startDate`
 * 4. `selectedDate`
 * 5. `minDate` || `maxDate` - depending on which date is closer to today
 *
 * We should also keep in mind not to select dates that are disabled. After picking a date based on
 * the date priority, the date nearest to that date that is not disabled will be selected.
 *
 * @param {{
 *   currentDate: Date | null,
 *   disabledDates: { dates: Date[], weekDays: number[] },
 *   maxDate: Date,
 *   minDate: Date,
 *   month: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11,
 *   numberOfMonths: 1 | 2,
 *   year: number,
 * }} arguments An object containing values needed to pick a default date
 * @returns {Date} A date that should be used as the default date in a calendar
 */
const pickDefaultDate = _ref2 => {
  let {
    currentDate = null,
    disabledDates,
    maxDate,
    minDate,
    month,
    numberOfMonths,
    year
  } = _ref2;
  const today = new Date();
  let defaultDate = getAccurateDate(today.getFullYear(), today.getMonth(), 1);

  // `currentDate` represents `endDate`, `startDate` or `selectedDate` in that order
  if (currentDate) defaultDate = currentDate;

  // dateToUse is not in view - find a date in view
  if (isDateOutsideOfView(defaultDate, month, year, numberOfMonths)) {
    defaultDate = getAccurateDate(year, month, 1);
  }

  // `defaultDate` is not disabled
  if (!isDateDisabled({
    date: defaultDate,
    disabledDates,
    maxDate,
    minDate
  })) {
    return defaultDate;
  }

  // move left and right a day in search of a day that isn't disabled
  let right = defaultDate;
  let left = defaultDate;

  // prefer a date in the current month before picking a date in another month
  let rightPointerLeftStartingMonth = false;
  let leftPointerLeftStartingMonth = false;
  while (right <= maxDate || left >= minDate) {
    // signal that we should stop incrementing left/right until both have left starting month
    if (right.getMonth() !== defaultDate.getMonth()) rightPointerLeftStartingMonth = true;
    if (left.getMonth() !== defaultDate.getMonth()) leftPointerLeftStartingMonth = true;

    // disable `right`/`left` unless respective flag is false or both are true
    const isRightActive = !rightPointerLeftStartingMonth || leftPointerLeftStartingMonth;
    const isLeftActive = !leftPointerLeftStartingMonth || rightPointerLeftStartingMonth;

    // `right` is not disabled
    if (isRightActive && !isDateDisabled({
      date: right,
      disabledDates,
      maxDate,
      minDate
    })) {
      return right;
    }

    // `left` is not disabled
    if (isLeftActive && !isDateDisabled({
      date: left,
      disabledDates,
      maxDate,
      minDate
    })) {
      return left;
    }

    // move left and right one day
    if (isRightActive) right = addDays(right, 1);
    if (isLeftActive) left = addDays(left, -1);
  }
  return defaultDate;
};

/**
 * Determine whether or not the user can navigate backwards a month from the current month.
 *
 * @param {{
 *   minDate: Date,
 *   month: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11,
 *   step: number,
 *   year: number,
 * }} arguments An object containing various arguments needed to determine whether or not the user
 * can navigate backwards
 * @returns {boolean}
 */
const getCanNavigateBackwards = _ref3 => {
  let {
    minDate,
    month,
    step,
    year
  } = _ref3;
  const calendarDate = getAccurateDate(year, month, 1);
  const previousSelectableInterval = addMinutes(calendarDate, -step);
  return previousSelectableInterval.getTime() >= minDate.getTime();
};

/**
 * Determine whether or not the user can navigate forwards a month from the current month.
 *
 * @param {{
 *   maxDate: Date,
 *   month: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11,
 *   numberOfMonths: 0 | 1,
 *   year: number,
 * }} arguments An object containing various arguments needed to determine whether or not the user
 * can navigate forwards
 * @returns {boolean}
 */
const getCanNavigateForwards = _ref4 => {
  let {
    maxDate,
    month,
    numberOfMonths,
    year
  } = _ref4;
  const calendarDate = getAccurateDate(year, month, 1);
  const nextSelectableInterval = addMonths(calendarDate, numberOfMonths);
  return nextSelectableInterval.getTime() <= maxDate.getTime();
};

/**
 * Given `year`, `month` and the number of months to add to the current month, calculate the next
 * year and month.
 *
 * @param {number} year The current year
 * @param {0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11} month The current month
 * @param {number} monthsToAdd A number representing the number of months to add or subtract from
 * the given month/year
 * @returns {[number, 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11]}
 */
const getYearAndMonthAfterAddingMonths = (year, month, monthsToAdd) => {
  const startingDate = getAccurateDate(year, month, 1);
  const nextDate = addMonths(startingDate, monthsToAdd);
  return [nextDate.getFullYear(), nextDate.getMonth()];
};

/**
 * Applications that do server side rendering won't have access to HTMLElement; safely handle using
 * this environment variable.
 */
const checkForElement = () => {
  try {
    return HTMLElement;
  } catch (_) {
    return {};
  }
};

/**
 * The given func can only be executed every `timeout` seconds
 */
const debounce = function (func, timeout) {
  if (timeout === void 0) {
    timeout = 200;
  }
  let timer;
  return function () {
    for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
      args[_key] = arguments[_key];
    }
    clearTimeout(timer);
    timer = setTimeout(() => {
      func.apply(_this, args);
    }, timeout);
  };
};

/**
 * It may be worth noting that once we pass focus to the `Calendar.TimeInput` component, we don't
 * need custom logic to handle keyboard navigation. As long as the user's focus is in the
 * `Calendar.TimeInput` we can keep the default keyboard behavior.
 */

/**
 * Various different pieces of the `Calendar` component handle accessible navigation differently
 * depending on the context. This function organizes the keyboard navigation handler functions and
 * triggers them based on the the keys the user clicks.
 *
 * @param {e: Event} e DOM event
 * @param {import('../types').RefsObject} refs An object containing React refs to a number of
 * different parts of the Calendar component
 * @param {{
 *   arrowDown: (e: Event, refs: import('../types').RefsObject) => void,
 *   arrowLeft: (e: Event, refs: import('../types').RefsObject) => void,
 *   arrowRight: (e: Event, refs: import('../types').RefsObject) => void,
 *   arrowUp: (e: Event, refs: import('../types').RefsObject) => void,
 *   enter: (e: Event, refs: import('../types').RefsObject) => void,
 *   shiftTab: (e: Event, refs: import('../types').RefsObject) => void,
 *   tab: (e: Event, refs: import('../types').RefsObject) => void,
 * }} callback An object containing various callback functions that should be executed according to
 * the keys hit by the user
 * a callback function for handling shift + tabbing and tabbing
   * @returns {void}
 */
const handleKeyDown = (e, refs, callback) => {
  // select item being focused on
  if (e.key === 'Enter' || e.key === 'Space') {
    if (callback.enter) callback.enter(e, refs);

    // shift + tab backwards
  } else if (e.shiftKey && e.key === 'Tab') {
    if (callback.shiftTab) callback.shiftTab(e, refs);

    // tab forward
  } else if (e.key === 'Tab') {
    if (callback.tab) callback.tab(e, refs);

    // shift focus up, down, left or right according to the key pressed
  } else if (e.key === 'ArrowUp') {
    if (callback.arrowUp) callback.arrowUp(e, refs);
  } else if (e.key === 'ArrowDown') {
    if (callback.arrowDown) callback.arrowDown(e, refs);
  } else if (e.key === 'ArrowLeft') {
    if (callback.arrowLeft) callback.arrowLeft(e, refs);
  } else if (e.key === 'ArrowRight') {
    if (callback.arrowRight) callback.arrowRight(e, refs);
  }
};
const navigate = {
  /**
   * Given a current date and the number of days of difference between the current day and the next
   * date, shift focus to the next date. If the next date is in the previous or next month, navigate
   * to the next month and shift focus to that date.
   *
   * @param {{
   *   canChangeMonth: boolean,
   *   currentDate: Date,
   *   disabledDates: { dates: Date[], weekDays: number[] },
   *   maxDate: Date,
   *   minDate: Date,
   *   month: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11,
   *   nextDateDiff: -7 | -1 | 1 | 7,
   *   numberOfMonths: 1 | 2,
   *   setState: import('../types').setStateObject,
   *   year: number,
   * }} arguments Arguments needed to determine how to shift the focus to the next date
   * @returns {(e: Event, refs: import('../types').RefsObject) => void}
   */
  dates(_ref) {
    let {
      canChangeMonth,
      currentDate,
      disabledDates,
      maxDate,
      minDate,
      month,
      nextDateDiff,
      numberOfMonths,
      setState,
      year
    } = _ref;
    return (e, refs) => {
      // stop page from scrolling weirdly
      e.preventDefault();
      let nextDate = addDays(currentDate, nextDateDiff);

      // nextDate is within view and not disabled
      if (refs.days[nextDate] && refs.days[nextDate].current) {
        refs.days[nextDate].current.focus();

        // loop until we find the next available date - handles disabled and out of view dates
      } else if (canChangeMonth) {
        const incrementOrDecrement = nextDateDiff > 0 ? 1 : -1;

        // loop forward or backwards depending on the direction the user is looking in
        while (incrementOrDecrement > 0 ? nextDate <= maxDate : nextDate >= minDate) {
          const isNextDateDisabled = isDateDisabled({
            date: nextDate,
            disabledDates,
            maxDate,
            minDate
          });

          // we have found out next date
          if (!isNextDateDisabled) {
            // nextDate is currently not in view - update month/year and wait to shift focus until
            // the next month loads
            if (isDateOutsideOfView(nextDate, month, year, numberOfMonths)) {
              // we only need to update year and month if the date is outside of view
              setState.year(nextDate.getFullYear());
              setState.month(nextDate.getMonth());

              /* eslint-disable-next-line no-loop-func */
              refs.calendar.current.addEventListener('monthLoaded', event => {
                event.preventDefault();
                refs.days[nextDate].current.focus();
              }, {
                capture: true,
                once: true
              });

              // nextDate is currently in view - we already have the ref, just focus on it
            } else {
              refs.days[nextDate].current.focus();
            }

            // since we've found a valid date we can stop looping
            break;
          }

          // move left or right one day
          nextDate = addDays(nextDate, incrementOrDecrement);
        }
      }
    };
  }
};
const pivotFocus = {
  /**
   * Shift the user's focus to the dates in the body of the calendar.
   *
   * @param {{
   *   currentDate: Date | null,
   *   disabledDates: { dates: Date[], weekDays: number[] },
   *   maxDate: Date,
   *   minDate: Date,
   *   month: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11,
   *   numberOfMonths: 1 | 2,
   *   setState: import('../types').setStateObject,
   *   year: number,
   * }} arguments An object containing the arguments necessary for pivoting the user's focus
   * @returns {(e: Event, refs: import('../types').RefsObject) => void}
   */
  fromMenuToCalendarDate(_ref2) {
    let {
      currentDate,
      disabledDates,
      maxDate,
      minDate,
      month,
      numberOfMonths,
      setState,
      year
    } = _ref2;
    return (e, refs) => {
      // since we manually shift focus, prevent default behavior of original tab
      e.preventDefault();

      // pick a non-disabled date
      const dateToFocusOn = pickDefaultDate({
        currentDate,
        disabledDates,
        maxDate,
        minDate,
        month,
        numberOfMonths,
        year
      });

      // dateToFocusOn is within view
      if (refs.days[dateToFocusOn] && refs.days[dateToFocusOn].current) {
        refs.days[dateToFocusOn].current.focus();

        // `dateToFocusOn` is currently not in view - update month/year and wait to shift focus
        // until the next month loads
      } else if (isDateOutsideOfView(dateToFocusOn, month, year, numberOfMonths)) {
        // we only need to update year and month if the date is outside of view
        setState.year(dateToFocusOn.getFullYear());
        setState.month(dateToFocusOn.getMonth());

        /* eslint-disable-next-line no-loop-func */
        refs.calendar.current.addEventListener('monthLoaded', event => {
          event.preventDefault();
          refs.days[dateToFocusOn].current.focus();
        }, {
          capture: true,
          once: true
        });
      }
    };
  },
  /**
   * Shift the user's focus from the menu into the `Calendar` component's input.
   *
   * @returns {(e: Event, refs: import('../types').RefsObject) => void}
   */
  fromMenuToCalendarInput() {
    return (e, refs) => {
      e.preventDefault();
      if (refs.input.current) {
        refs.input.current.focus();
      }
    };
  },
  /**
   * Shift the user's focus from the `Calendar` component's input to the menu (if it exists) or to
   * the body of the calendar. The focus will be shifted to a date in the body of the calendar
   * following the priorities in the `helpers.pickDefaultDate` function.
   *
   * @param {{
   *   currentDate: Date | null,
   *   disabledDates: { dates: Date[], weekDays: number[] },
   *   maxDate: Date,
   *   minDate: Date,
   *   month: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11,
   *   numberOfMonths: 1 | 2,
   *   setState: import('../types').setStateObject,
   *   year: number,
   * }} arguments An object containing the arguments necessary for pivoting the user's focus
   * @returns {(e: Event, refs: import('../types').RefsObject) => void}
   */
  fromCalendarInputToMenuOrCalendarDate(_ref3) {
    let {
      currentDate,
      disabledDates,
      maxDate,
      minDate,
      month,
      numberOfMonths,
      setState,
      year
    } = _ref3;
    return (e, refs) => {
      e.preventDefault();

      // if we have a ref to the previous month button, go there
      if (refs.menu[0]?.current) {
        refs.menu[0].current.focus();

        // if we have a ref to the next month button, go there
      } else if (refs.menu[1] && refs.menu[1].current) {
        refs.menu[1].current.focus();

        // if prev and next month buttons are both not available, go straight to the calendar
      } else {
        pivotFocus.fromMenuToCalendarDate({
          currentDate,
          disabledDates,
          maxDate,
          minDate,
          month,
          numberOfMonths,
          setState,
          year
        })(e, refs);
      }
    };
  },
  /**
   * Shift the user's focus from the `Calendar` component's body into the month navigation menu if
   * it exists. If it doesn't exist, shift it into the input of the calendar.
   *
   * @returns {(e: Event, refs: import('../types').RefsObject) => void}
   */
  fromCalendarDateToMenuOrInput() {
    return (e, refs) => {
      // we have the "next month" button in the menu - shift focus to that
      if (refs.menu[1] && refs.menu[1].current) {
        e.preventDefault();
        refs.menu[1].current.focus();

        // we have the "previous month" button in the menu - shift focus to that
      } else if (refs.menu[0] && refs.menu[0].current) {
        e.preventDefault();
        refs.menu[0].current.focus();

        // shift focus to the calendar's input
      } else if (refs.input && refs.input.current) {
        e.preventDefault();
        refs.input.current.focus();
      }
    };
  },
  /**
   * Shift the user's focus from the body of the `Calendar` component to the `Calendar.TimeInput` if
   * we have one.
   *
   * @returns {(e: Event, refs: import('../types').RefsObject) => void}
   */
  fromCalendarDateToTimeInput() {
    return (e, refs) => {
      // the `Calendar.TimeInput` is being used - shift focus to it
      if (refs.timeInput.current) {
        // only prevent automatic focus shifting if we have a `Calendar.TimeInput` to shift focus to
        e.preventDefault();
        refs.timeInput.current.focus();
      }
    };
  },
  /**
   * When navigating forward or backwards a month, its possible to hit the `minDate` or `maxDate`
   * and not be able to navigate further. In this case, the button the user's focus was on may
   * disappear. If this happens, shift the user's focus to the other month navigation button if it
   * exists, or into the calendar body.
   *
   * @param {{
   *   currentDate: Date | null,
   *   disabledDates: { dates: Date[], weekDays: number[] },
   *   maxDate: Date,
   *   menuIndex: 0 | 1,
   *   minDate: Date,
   *   month: 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11,
   *   numberOfMonths: 1 | 2,
   *   setState: import('../types').setStateObject,
   *   step: number,
   *   year: number,
   * }} arguments An object containing the arguments necessary for pivoting the user's focus
   * @returns {(e: Event, refs: import('../types').RefsObject) => void}
   */
  monthNavigation(_ref4) {
    let {
      currentDate,
      disabledDates,
      maxDate,
      menuIndex,
      minDate,
      month,
      numberOfMonths,
      setState,
      step,
      year
    } = _ref4;
    return (e, refs) => {
      // handle clicking and shifting focus manually
      e.preventDefault();
      refs.menu[menuIndex].current.click();

      // after navigating backwards a month, can we continue to navigate backwards?
      const [previousYear, previousMonth] = getYearAndMonthAfterAddingMonths(year, month, -1);
      const canNavigateBackwards = getCanNavigateBackwards({
        minDate,
        month: previousMonth,
        step,
        year: previousYear
      });

      // after navigating forwards a month, can we continue to navigate forwards?
      const [nextYear, nextMonth] = getYearAndMonthAfterAddingMonths(year, month, 1);
      const canNavigateForwards = getCanNavigateForwards({
        maxDate,
        month: nextMonth,
        numberOfMonths,
        year: nextYear
      });

      // user's focus was on "next month" button, but the button will no longer exist
      if (menuIndex === 1 && !canNavigateForwards) {
        if (canNavigateBackwards) {
          refs.menu[0].current.focus();
        } else {
          pivotFocus.fromMenuToCalendarDate({
            currentDate,
            disabledDates,
            maxDate,
            minDate,
            month,
            numberOfMonths,
            setState,
            year
          })(e, refs);
        }

        // user's focus was on "previous month" button, but the button will no longer exist
      } else if (menuIndex === 0 && !canNavigateBackwards) {
        if (canNavigateForwards) {
          refs.menu[1].current.focus();
        } else {
          pivotFocus.fromMenuToCalendarDate({
            currentDate,
            disabledDates,
            maxDate,
            minDate,
            month,
            numberOfMonths,
            setState,
            year
          })(e, refs);
        }
      }
    };
  }
};

const MONTHS = {
  LOWERCASE: ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'],
  STARTCASE: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
};
const WEEKDAYS = {
  LOWERCASE: ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'],
  STARTCASE: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
};
const NUMBER_OF_MINUTES_IN_A_DAY = 1440;
const DEFAULT_DATE_FORMAT = 'MM/dd/yyyy'; // equivalent to 'P'

const DEFAULT_MAX_DATE = new Date(9999, 11, 31); // 12/31/9999
const DEFAULT_MAX_TIME = '23:59'; // 11:59 PM
const DEFAULT_MIN_DATE = new Date(0, 0, 1); // 01/01/1900
const DEFAULT_MIN_TIME = '00:00'; // 12:00 AM

// key names for state values in the useCalendar hooks
const KEYS = Object.freeze({
  END_DATE: 'endDate',
  END_TIME: 'endTime',
  SELECTED_DATE: 'selectedDate',
  SELECTED_TIME: 'selectedTime',
  START_DATE: 'startDate',
  START_TIME: 'startTime'
});

/**
 * The calendar hooks aren't able to validate errors with the default dates/times provided to them
 * because they are not exposed to `props`. Make sure that `error` exported from these hooks is up
 * to date by setting state on the first render if there is an error with one of these default
 * values.
 *
 * @param {boolean | string} error An error for a piece of state in the calendar
 * @param {(error: boolean | string) => void} onError A callback function to handle setting
 * `error`'s state in the calendar hooks
 * @returns {void}
 */
const useHandleErrorOnFirstRender = (error, onError) => {
  useEffect(() => {
    if (error && onError) onError(error);

    // we have an empty array here because we only want this to run on the first render!
    // this is the only time that `error` can be out of sync with the actual error state
  }, []); /* eslint-disable-line react-hooks/exhaustive-deps */
};

var useHandleErrorOnFirstRender$1 = useHandleErrorOnFirstRender;

const getPosition = (calendar, inputWrapper, align) => {
  if (!(inputWrapper.current instanceof checkForElement())) {
    return {};
  }
  const {
    bottom,
    left,
    right
  } = inputWrapper.current.getBoundingClientRect();
  const {
    width
  } = calendar.current?.getBoundingClientRect() || {
    width: 271.45
  };

  // TODO: defaulting width like this isn't ideal...
  // ! since we arent rendering the calendar until we need it we cant get width until its too late

  return {
    left: align === 'right' ? right - width : left,
    top: bottom
  };
};

/**
 * Given refs to the relevant parts of the calendar, return an object containing css positioning
 * information to correctly position the calendar when used with react portals.
 */
const usePortalPosition = (renderNode, refs, align) => {
  const [position, setPosition] = useState(renderNode ? getPosition(refs.calendar, refs.inputWrapper, align) : {});

  /* eslint-disable-next-line consistent-return */
  useLayoutEffect(() => {
    if (renderNode) {
      const debouncedSetPosition = debounce(() => setPosition(getPosition(refs.calendar, refs.inputWrapper, align)));
      window.addEventListener('resize', debouncedSetPosition);
      document.addEventListener('scroll', debouncedSetPosition, true);
      return () => {
        window.removeEventListener('resize', debouncedSetPosition);
        document.removeEventListener('scroll', debouncedSetPosition, true);
      };
    }
  }, [align, refs.calendar, refs.inputWrapper, renderNode]);

  // * Initially refs.inputWrapper.current may be null. We need to explicitly retrigger getting the
  // * portal position when the calendar input is clicked just in case we don't have position data
  const triggerSetPortalPosition = () => {
    setPosition(renderNode ? getPosition(refs.calendar, refs.inputWrapper, align) : {});
  };

  // we only want to pass positioning if we are using a portal
  return [renderNode ? position : {}, triggerSetPortalPosition];
};
var usePortalPosition$1 = usePortalPosition;

/* eslint-disable import/prefer-default-export */

const time = (props, propName, componentName) => {
  const [hours = '', minutes = ''] = props[propName] && props[propName].match(timeComponents) || [];
  const isTimeValid = Number(hours) > -1 && Number(hours) < 24 && Number(minutes) > -1 && Number(minutes) < 61;
  if (props[propName] && (!time$1.test(props[propName]) || !isTimeValid)) {
    return new Error(`Invalid prop ${propName} passed to ${componentName}. Expected a valid time value, instead "${props[propName]}" was passed. Please use \`HH:mm\` time format (e.g. '14:30').`);
  }
  return null;
};

/**
 * @name differenceInCalendarMonths
 * @category Month Helpers
 * @summary Get the number of calendar months between the given dates.
 *
 * @description
 * Get the number of calendar months between the given dates.
 *
 * @param {Date|Number} dateLeft - the later date
 * @param {Date|Number} dateRight - the earlier date
 * @returns {Number} the number of calendar months
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // How many calendar months are between 31 January 2014 and 1 September 2014?
 * const result = differenceInCalendarMonths(
 *   new Date(2014, 8, 1),
 *   new Date(2014, 0, 31)
 * )
 * //=> 8
 */

function differenceInCalendarMonths(dirtyDateLeft, dirtyDateRight) {
  requiredArgs(2, arguments);
  var dateLeft = toDate(dirtyDateLeft);
  var dateRight = toDate(dirtyDateRight);
  var yearDiff = dateLeft.getFullYear() - dateRight.getFullYear();
  var monthDiff = dateLeft.getMonth() - dateRight.getMonth();
  return yearDiff * 12 + monthDiff;
}

/**
 * @name isSameDay
 * @category Day Helpers
 * @summary Are the given dates in the same day (and year and month)?
 *
 * @description
 * Are the given dates in the same day (and year and month)?
 *
 * @param {Date|Number} dateLeft - the first date to check
 * @param {Date|Number} dateRight - the second date to check
 * @returns {Boolean} the dates are in the same day (and year and month)
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Are 4 September 06:00:00 and 4 September 18:00:00 in the same day?
 * const result = isSameDay(new Date(2014, 8, 4, 6, 0), new Date(2014, 8, 4, 18, 0))
 * //=> true
 *
 * @example
 * // Are 4 September and 4 October in the same day?
 * const result = isSameDay(new Date(2014, 8, 4), new Date(2014, 9, 4))
 * //=> false
 *
 * @example
 * // Are 4 September, 2014 and 4 September, 2015 in the same day?
 * const result = isSameDay(new Date(2014, 8, 4), new Date(2015, 8, 4))
 * //=> false
 */

function isSameDay(dirtyDateLeft, dirtyDateRight) {
  requiredArgs(2, arguments);
  var dateLeftStartOfDay = startOfDay(dirtyDateLeft);
  var dateRightStartOfDay = startOfDay(dirtyDateRight);
  return dateLeftStartOfDay.getTime() === dateRightStartOfDay.getTime();
}

/**
 * @name isSameMonth
 * @category Month Helpers
 * @summary Are the given dates in the same month (and year)?
 *
 * @description
 * Are the given dates in the same month (and year)?
 *
 * @param {Date|Number} dateLeft - the first date to check
 * @param {Date|Number} dateRight - the second date to check
 * @returns {Boolean} the dates are in the same month (and year)
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Are 2 September 2014 and 25 September 2014 in the same month?
 * const result = isSameMonth(new Date(2014, 8, 2), new Date(2014, 8, 25))
 * //=> true
 *
 * @example
 * // Are 2 September 2014 and 25 September 2015 in the same month?
 * const result = isSameMonth(new Date(2014, 8, 2), new Date(2015, 8, 25))
 * //=> false
 */

function isSameMonth(dirtyDateLeft, dirtyDateRight) {
  requiredArgs(2, arguments);
  var dateLeft = toDate(dirtyDateLeft);
  var dateRight = toDate(dirtyDateRight);
  return dateLeft.getFullYear() === dateRight.getFullYear() && dateLeft.getMonth() === dateRight.getMonth();
}

/**
 * Generate warning text for a given prop.
 *
 * This function is for the purpose of DRYing up warning text for props internally in this file.
 *
 * @param {string} propName The name of the prop to create warning text for
 * @returns {string} Text to be printed to the console as a warning for using invalid Props
 */
const getPropWarningText = propName => `The \`${propName}\` prop passed to the Screentone Calendar component is not valid.`;

/**
 * Given a string representing a date format, ensure that the date format is valid and usable by the
 * calendar component. If it is not, return the default date format and log a warning to the
 * console.
 *
 * @param {string} dateFormat A string representing a date format
 * @returns {string} A sanitized string representing a date format
 */
const sanitizeDateFormat = dateFormat => {
  // empty value provided for date format - return default
  if (!dateFormat) return DEFAULT_DATE_FORMAT;

  // date format is not a string - return default
  if (typeof dateFormat !== 'string') {
    /* eslint-disable-next-line no-console */
    console.warn(`${getPropWarningText('dateFormat')} The value provided to \`dateFormat\` must be a valid string. See \`https://date-fns.org/docs/format\` for a list of valid date formats.`);
    return DEFAULT_DATE_FORMAT;
  }
  try {
    // use `testDate` to test how `dateFormat` is encoding date/time data into a string
    const testDate = parse(format(new Date(), dateFormat), dateFormat, new Date());
    const rightNow = new Date();
    const dateWithoutTime = getDateWithTime(new Date());

    // if the days aren't the same day, not enough information is being encoded in the dateFormat
    // for it to be valid
    if (!isSameDay(testDate, rightNow)) {
      /* eslint-disable-next-line no-console */
      console.warn(`${getPropWarningText('dateFormat')} The value provided to \`dateFormat\` must contain enough information to properly encode a full date. See \`https://date-fns.org/docs/format\` for a list of valid date formats.`);
      return DEFAULT_DATE_FORMAT;
    }

    // if test date is the same day, but the time isn't the same, `dateFormat` is encoding time data
    // into the date and is thus invalid
    if (testDate.getTime() > dateWithoutTime.getTime()) {
      /* eslint-disable-next-line no-console */
      console.warn(`${getPropWarningText('dateFormat')} The value provided to \`dateFormat\` must not include a time value. Time values are handled by the \`Calendar.TimeInput\` component. See \`https://date-fns.org/docs/format\` for a list of valid date formats.`);
      return DEFAULT_DATE_FORMAT;
    }

    // `format` function will throw errors with invalid formats
  } catch (error) {
    /* eslint-disable-next-line no-console */
    console.warn(`${getPropWarningText('dateFormat')} The value provided to \`dateFormat\` is not a valid date format. See \`https://date-fns.org/docs/format\` for a list of valid date formats.`);
    return DEFAULT_DATE_FORMAT;
  }
  return dateFormat;
};

/**
 * Given a date, confirm that it is a valid date or `null`. If `date` is invalid, log out a warning
 * and return `null`.
 *
 * @param {Date | null} date the current date in any data type being sanitized
 * @returns {Date | null} a valid date representing `date` or `null`
 */
const sanitizeDate = date => {
  if (!date) return null;
  if (date && typeof date.getTime === 'function' && !Number.isNaN(date.getTime())) {
    return date;
  }

  /* eslint-disable-next-line no-console */
  console.warn(`The Screentone Calendar component was passed an invalid value. A Javascript date object was expected, but ${date} was received.`);

  // not a type that can be converted into valid date
  return null;
};

/**
 * Given two dates, one that represents the minimum date in the calendar and the other which
 * represents the maximum date in the calendar, sanitize both dates.
 *
 * ** NOTE:** For the sake of convenience, we ensure that `minDate` and `maxDate` represent the
 * minimum and maximum selectable unit of time respectively. This means taking into account
 * `disabledDates`, `disabledTimes`, `minTime` and `maxTime`. We may need to significantly adjust
 * the `minDate` and `maxDate` to account for this.
 *
 * @param {Date | null} minDateOrNull The minimum selectable date
 * @param {Date | null} maxDateOrNull The maximum selectable date
 * @param {string} minTime The minimum selectable time
 * @param {string} maxTime The maximum selectable time
 * @param {{ dates: Date[], weekDays: number[] }} disabledDates An object containing information
 * about which dates are disabled in the `Calendar`
 * @param {{ times: string[] }} disabledTimes An object containing information about which times are
 * disabled in the `Calendar.TimeInput`
 * @param {number} step The minimum unit of selectable time, in minutes
 * @returns {[Date, Date]} An array that represents [minDate, maxDate]
 */
const sanitizeMinAndMaxDates = (minDateOrNull, maxDateOrNull, minTime, maxTime, disabledDates, disabledTimes, step) => {
  const minDate = sanitizeDate(minDateOrNull) ?? DEFAULT_MIN_DATE;
  const maxDate = sanitizeDate(maxDateOrNull) ?? DEFAULT_MAX_DATE;
  const allWeekDaysAreDisabled = disabledDates.weekDays.length === 7;
  const allDatesAreDisabled = disabledDates.dates.length === differenceInCalendarDays(maxDate, minDate);
  const allTimesAreDisabled = disabledTimes.times.length === NUMBER_OF_MINUTES_IN_A_DAY / step;

  // if all dates/times are disabled, just return the defaults
  if (allWeekDaysAreDisabled || allDatesAreDisabled || allTimesAreDisabled) {
    return [minDate, maxDate];
  }

  // converting disabledDates/disabledTimes to an object reduces the cost of repeated lookups
  const disabledDateCache = disabledDates.dates.reduce((total, current) => {
    /* eslint-disable-next-line no-param-reassign */
    total[getDateWithTime(current)] = true;
    return total;
  }, {});
  const disabledTimeCache = disabledTimes.times.reduce((total, current) => {
    /* eslint-disable-next-line no-param-reassign */
    total[getTimeInMinutes(current)] = true;
    return total;
  }, {});
  const stepInMilliseconds = step * 60 * 1000;
  let min = new Date(minDate.getTime() - minDate.getTime() % stepInMilliseconds + stepInMilliseconds);
  let max = new Date(maxDate.getTime() - maxDate.getTime() % stepInMilliseconds);
  while (min < max) {
    let shouldBreak = false;
    const currentDateIsDisabled = disabledDateCache[getDateWithTime(min)] || disabledDates.weekDays.includes(min.getDay());

    // the current date is disabled, move to the next one
    if (currentDateIsDisabled) {
      min = addDays(min, 1);

      // the current date is valid, find a valid time
    } else {
      const minTimeInMinutes = getTimeInMinutes(minTime);
      const maxTimeInMinutes = getTimeInMinutes(maxTime);
      const initialTime = isSameDay(minDate, min) ? Math.max(min.getHours() * 60 + min.getMinutes(), minTimeInMinutes) : minTimeInMinutes;

      // set time to 00:00 before adding time back in
      min = getDateWithTime(min);
      for (let time = initialTime; time <= maxTimeInMinutes; time += step) {
        // if the time isn't disabled, we've found a valid time - add it to minDate
        if (!disabledTimeCache[time]) {
          min = new Date(min.getTime() + time * 60 * 1000);
          shouldBreak = true;
          break;
        }
      }

      // no times available for current day, move to the next
      if (!shouldBreak) min = addDays(min, 1);
    }

    // we've already selected a valid minDate, break out of the loop
    if (shouldBreak) break;
  }
  while (min < max) {
    let shouldBreak = false;
    const currentDateIsDisabled = disabledDateCache[getDateWithTime(max)] || disabledDates.weekDays.includes(max.getDay());

    // the current date is disabled, move to the next one
    if (currentDateIsDisabled) {
      max = addDays(max, -1);

      // the current date is valid, find a valid time
    } else {
      const minTimeInMinutes = getTimeInMinutes(minTime);
      const maxTimeInMinutes = getTimeInMinutes(maxTime);
      const initialTime = isSameDay(maxDate, max)
      // clearing the time interferes with selecting initialTime, default max time to be Infinity
      ? Math.min(max.getHours() * 60 + max.getMinutes() || Infinity, maxTimeInMinutes) : maxTimeInMinutes;

      // set time to 00:00 before adding time back in
      max = getDateWithTime(max);
      for (let time = initialTime; time >= minTimeInMinutes; time -= step) {
        // if the time isn't disabled, we've found a valid time - add it to maxDate
        if (!disabledTimeCache[time]) {
          max = new Date(max.getTime() + time * 60 * 1000);
          shouldBreak = true;
          break;
        }
      }

      // no times available for current day, move to the next
      if (!shouldBreak) max = addDays(max, -1);
    }

    // we've already selected a valid maxDate, break out of the loop
    if (shouldBreak) break;
  }

  // if `min` is greater than or equal to `max`, there are no non-disabled dates between the
  // `minDate` and `maxDate` range - return the defaults
  if (min >= max) return [minDate, maxDate];
  return [min, max];
};

/**
 * Given a range of dates, `minDate` and `maxDate`, check if `time` falls within the given range.
 * If `time` falls within the range, return `time`, otherwise return null.
 *
 * @param {Date} minDate A minimum date
 * @param {Date} date A date to be checked against a range of dates
 * @param {Date} maxDate A maximum date
 * @returns {Date | null}
 */
const sanitizeDateBounds = (minDate, date, maxDate) => {
  const isTimeWithinBounds = date >= minDate && date <= maxDate;
  return isTimeWithinBounds || isSameDay(date, minDate) || isSameDay(date, maxDate) ? date : null;
};

/**
 * Given a month (as either a string or a number), make sure that `month` is an integer that falls
 * within the `minDate` and `maxDate` range. If `month` is not valid, return `null`.
 *
 * @param {number | string} month the month passed to the calendar component via props
 * @param {Date} minDate the minimum selectable date for the calendar
 * @param {Date} maxDate the maximum selectable date for the calendar
 * @returns {number | null} number representing a month
 */
const sanitizeMonth = (month, minDate, maxDate) => {
  // `month` not passed as props - return now for simplicity
  if (month === null) return null;
  const monthAsNumber = Number(month);
  const monthIndex = MONTHS.LOWERCASE.indexOf(typeof month === 'string' && month.toLowerCase());

  // month is not a number and not a valid string
  if (Number.isNaN(monthAsNumber) && typeof month === 'string' && monthIndex === -1) {
    /* eslint-disable-next-line no-console */
    console.warn(`${getPropWarningText('month')} The value provided to \`month\` is not a valid month name.`);
    return null;
  }
  const partiallySanitizedMonth = monthIndex === -1 ? Number(month) : monthIndex;

  // invalid number
  if (Number.isNaN(partiallySanitizedMonth) || !Number.isInteger(partiallySanitizedMonth)) {
    /* eslint-disable-next-line no-console */
    console.warn(`${getPropWarningText('month')} The value provided to \`month\` is not a valid number.`);
    return null;
  }

  // out of valid month range
  if (month < 0 || month > 11) {
    /* eslint-disable-next-line no-console */
    console.warn(`${getPropWarningText('month')} The value provided to \`month\` is less than 0 or greater than 11. \`month\` must be an integer from 0 (January) to 11 (december).`);
    return null;
  }

  // out of minDate and maxDate range
  if (differenceInCalendarMonths(maxDate, minDate) < 12 && (partiallySanitizedMonth < minDate.getMonth() || partiallySanitizedMonth > maxDate.getMonth())) {
    /* eslint-disable-next-line no-console */
    console.warn(`${getPropWarningText('month')} The value provided to \`month\` is not within the \`minDate\` and \`maxDate\` range. Please make sure that \`month\` is within range.`);
    return null;
  }

  // valid
  return partiallySanitizedMonth;
};

/**
 * Validate the `numberOfMonths` prop. If `numberOfMonths` is anything other than 1 or 2, return 1.
 *
 * @param {number} numberOfMonths a number representing the number of months the calendar component
 * should display
 * @returns {1 | 2} a valid number representing the number of months the calendar component should
 * display
 */
const sanitizeNumberOfMonths = numberOfMonths => {
  const numberOfMonthsAsNumber = Number(numberOfMonths);

  // not valid
  if (numberOfMonthsAsNumber !== 1 && numberOfMonthsAsNumber !== 2) {
    /* eslint-disable-next-line no-console */
    console.warn(`${getPropWarningText('numberOfMonths')} \`numberOfMonths\` should be either 1 or 2. The value that was passed to \`numberOfMonths\` was ${numberOfMonths}.`);
    return 1;
  }

  // valid
  return numberOfMonthsAsNumber;
};

/**
 * Validate the timeInterval passed as props. If the interval does not evenly divide into one day,
 * is smaller than the minimum allowed interval or greater than the maximum allowed time interval,
 * return the default interval.
 *
 * @param {number} timeInterval the interval between times in the time select menu (in minutes) -
 * passed as props to the Calendar component
 * @param {number} step the minimum unit of selectable time, in seconds
 * @returns {number} the interval between times in the time select menu (in minutes)
 */
const sanitizeTimeInterval = (timeInterval, step) => {
  const DEFAULT_INTERVAL = 30;
  const MAXIMUM_TIME_INTERVAL = 120;

  // during sanitization, step was converted to seconds - convert it back
  const stepInMinutes = step / 60;

  // return default time interval for empty value
  if (!timeInterval) return stepInMinutes < DEFAULT_INTERVAL ? DEFAULT_INTERVAL : stepInMinutes;
  const isInvalidTimeInterval = typeof timeInterval !== 'number' || Number.isNaN(parseFloat(timeInterval)) // timeInterval is a float
  || NUMBER_OF_MINUTES_IN_A_DAY % timeInterval // interval doesn't fit into one day
  || timeInterval > MAXIMUM_TIME_INTERVAL; // interval is less than or equal to 1 day

  if (isInvalidTimeInterval) {
    /* eslint-disable-next-line no-console */
    console.warn(`${getPropWarningText('timeInterval')} \`timeInterval\` must be an integer that is divisible by ${NUMBER_OF_MINUTES_IN_A_DAY} and less than ${MAXIMUM_TIME_INTERVAL}.`);
    return DEFAULT_INTERVAL;
  }

  // ensure that timeInterval is not smaller than step - ignore timeInterval
  if (stepInMinutes > timeInterval) {
    return stepInMinutes > DEFAULT_INTERVAL ? stepInMinutes : DEFAULT_INTERVAL;
  }
  return timeInterval;
};

/**
 * Validate the `weekStartsOn` prop. `weekStartsOn` should be an integer between 0 and 6
 * (inclusive) or a string representing a day of the week. 0 represents Sunday and 6 represents
 * Saturday. If any value is passed that is not valid, return 0.
 *
 * @param {number | string} weekStartsOn the first day of the week to be displayed in the calendar
 * @returns {0 | 1 | 2 | 3 | 4 | 5 | 6} a number representing a day of the week from 0 (Sunday) to 6
 * (Saturday)
 */
const sanitizeWeekStartsOn = weekStartsOn => {
  const weekStartsOnAsNumber = Number(weekStartsOn);
  const weekStartsOnIndex = WEEKDAYS.LOWERCASE.indexOf(weekStartsOn);

  // weekStartsOn is not a number and not a valid string
  if (Number.isNaN(weekStartsOnAsNumber) && typeof weekStartsOn === 'string' && weekStartsOnIndex === -1) {
    /* eslint-disable-next-line no-console */
    console.warn(`${getPropWarningText('weekStartsOn')} The value provided to \`weekStartsOn\` is not a valid day of the week.`);
    return 0;
  }
  const partiallySanitizedWeekStartsOn = weekStartsOnIndex === -1 ? weekStartsOnAsNumber : weekStartsOnIndex;

  // invalid number
  if (Number.isNaN(partiallySanitizedWeekStartsOn) || !Number.isInteger(partiallySanitizedWeekStartsOn)) {
    /* eslint-disable-next-line no-console */
    console.warn(`${getPropWarningText('weekStartsOn')} The value provided to \`weekStartsOn\` is not a valid integer.`);
    return 0;
  }

  // is not in valid number range
  if (partiallySanitizedWeekStartsOn < 0 || partiallySanitizedWeekStartsOn > 6) {
    /* eslint-disable-next-line no-console */
    console.warn(`${getPropWarningText('weekStartsOn')} The value provided to \`weekStartsOn\` is less than 0 or greater than 6. \`weekStartsOn\` must be an integer from 0 (Sunday) to 6 (Saturday).`);
    return 0;
  }

  // valid
  return partiallySanitizedWeekStartsOn;
};

/**
 * Given a year (as either a string or a number), make sure that `year` is an integer that falls
 * within the `minDate` and `maxDate` range. If `year` is not valid, return `null`.
 *
 * @param {number | string} year the year passed to the calendar component via props
 * @param {0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11} month
 * @param {Date} minDate the minimum selectable date for the calendar
 * @param {Date} maxDate the maximum selectable date for the calendar
 * @returns {number | null} number representing a year
 */
const sanitizeYear = (year, month, minDate, maxDate) => {
  // `year` not passed as props - return now for simplicity
  if (!year) return null;
  const yearAsNumber = Number(year);

  // invalid year
  if (Number.isNaN(yearAsNumber) || !Number.isInteger(yearAsNumber)) {
    /* eslint-disable-next-line no-console */
    console.warn(`${getPropWarningText('year')} The value provided to \`year\` is not a valid number. Please pass either a number, or a string that represents a valid number.`);
    return null;
  }

  // if we have a month, we need to sanitize `year` with respect to month
  if (month !== null) {
    const monthAndYearDate = getAccurateDate(yearAsNumber, month, 1);
    const yearIsOutsideOfMinMaxRange = (monthAndYearDate < minDate || monthAndYearDate > maxDate) && !(isSameMonth(monthAndYearDate, minDate) || isSameMonth(monthAndYearDate, maxDate));

    // outside of `minDate`/`maxDate` range
    if (yearIsOutsideOfMinMaxRange) {
      /* eslint-disable-next-line no-console */
      console.warn(`${getPropWarningText('year')} The value provided to \`year\` is not within the \`minDate\` and \`maxDate\` range. Please make sure that \`month\` and \`year\` are within range.`);
      return null;
    }

    // `year` creates an invalid date. The year is likely way too large.
    if (!monthAndYearDate.getTime()) {
      /* eslint-disable-next-line no-console */
      console.warn(`${getPropWarningText('year')} The value provided to \`year\`, ${year}, is not a valid year. Its likely that \`year\` is a number that is way too large.`);
      return null;
    }
  }

  // valid
  return yearAsNumber;
};

/**
 * Given the `disabledDates` prop, sanitize the prop by ensuring that all of the properties exist on
 * the `disabledDates` object and are of the correct type. Furthermore, each property should only
 * contain values of the correct type.
 *
 * **NOTE:** `disabledDates.dates` may seem better suited towards being an object, but due to
 * complications around generating keys from Javascript date objects, this becomes cumbersome to
 * implement. For the sake of simplicity for the developer using this package, we use an array.
 *
 * @param {{ dates: Date[], weekDays: number[] }} disabledDates An object containing information
 * about which dates should be disabled in the calendar
 * @returns {{ dates: Date[], weekDays: number[] }} A sanitized object containing information about
 * which dates should be disabled in the calendar
 */
const sanitizeDisabledDates = disabledDates => {
  const sanitizedDisabledDates = {
    dates: [],
    weekDays: []
  };

  // sanitize array of dates
  if (disabledDates.dates) {
    const areDatesValid = Array.isArray(disabledDates.dates) && disabledDates.dates.every(date => date instanceof Date);
    if (areDatesValid) {
      // set time values to `"00:00"` - we don't care about time values for disabled dates
      sanitizedDisabledDates.dates = disabledDates.dates.map(d => getDateWithTime(d));
    } else {
      /* eslint-disable-next-line no-console */
      console.warn(`${getPropWarningText('disabledDates.dates')} The value passed to \`disabledDates.dates\` must be an array containing only Javascript Date objects.`);
    }
  }

  // sanitize array of numbers
  if (disabledDates.weekDays) {
    const isWeekDaysValid = Array.isArray(disabledDates.weekDays) && disabledDates.weekDays.every(weekDay => typeof weekDay === 'number' && weekDay > -1 && weekDay < 7);
    if (isWeekDaysValid) {
      sanitizedDisabledDates.weekDays = disabledDates.weekDays;
    } else {
      /* eslint-disable-next-line no-console */
      console.warn(`${getPropWarningText('disabledDates.weekDays')} The value passed to \`disabledDates.weekDays\` must be an array containing only integers between 0 and 6 (inclusive).`);
    }
  }
  return sanitizedDisabledDates;
};

/**
 * Given an object containing information about times disabled in the `Calendar.TimeInput`
 * component, sanitize this object.
 *
 * @param {{ times: string[] }} disabledTimes An object containing information about which times are
 * disabled in the `Calendar.TimeInput` component.
 * @returns {string[]} A valid array of disabled times
 */
const sanitizeDisabledTimes = disabledTimes => {
  // `disabledTimes` is not an object
  if (typeof disabledTimes !== 'object') {
    /* eslint-disable-next-line no-console */
    console.warn(`The \`disabledTimes\` prop passed to the Screentone Calendar component is not valid. An object was expected, but \`${typeof disabledTimes}\` was received.`);
    return {
      times: []
    };
  }

  // `disabledTimes.times` is not an array
  if ('times' in disabledTimes && !Array.isArray(disabledTimes.times)) {
    /* eslint-disable-next-line no-console */
    console.warn(`The \`disabledTimes.times\` prop passed to the Screentone Calendar component is not valid. An array of strings was expected, but \`${typeof disabledTimes.times}\` was received.`);
    return {
      times: []
    };
  }
  return {
    times: [...new Set(disabledTimes.times)].filter(time => {
      // time value is invalid
      if (typeof time !== 'string') {
        /* eslint-disable-next-line no-console */
        console.warn(`The \`disabledTimes.times\` prop passed to the Screentone Calendar component contains a time value that is not valid. A time of type \`string\` was expected, but \`${typeof time}\` was received.`);
        return false;
      }

      // time value is incorrectly formatted
      if (!isTimeValid(time)) {
        /* eslint-disable-next-line no-console */
        console.warn('The `disabledTimes.times` prop passed to the Screentone Calendar component contains a time value that is not valid. The the time value must follow the `HH:mm` format.');
        return false;
      }
      return true;
    }).sort((a, b) => getTimeInMinutes(a) > getTimeInMinutes(b))
  };
};

/**
 * Given a time value, sanitize and return the time value.
 *
 * @param {string} time A time value represented by a string in the HH:mm format
 * @returns {string} A time value represented by a string in the HH:mm format
 */
const sanitizeTime = time => {
  // in some cases using a default time value can affect sanitizing dates. Not specifying a time can
  // help simplify sanitizing certain values (e.g. minDate and maxDate ranges)
  if (!time) return '';
  if (!isTimeValid(time)) {
    /* eslint-disable-next-line no-console */
    console.warn(`A time value passed to the Screentone Calendar or Calendar.TimeInput component is not valid. The value expected was a string in the HH:mm format. The value received was \`${time}\`.`);
    return '';
  }
  return time;
};

/**
 * Given a `minTime` and a `maxTime`, sanitize the values and handle any overlaps. If the `minTime`
 * and `maxTime` overlap, set both values to the default.
 *
 * @param {string} minTime The minimum selectable time value in the `Calendar.TimeInput`
 * @param {string} maxTime The maximum selectable time value in the `Calendar.TimeInput`
 * @returns {[string, string]} An array containing the `minTime` and the 0th index and the `maxTime
 * as the 1st index
 */
const sanitizeMinAndMaxTimes = (minTime, maxTime) => {
  let cleanMinTime = sanitizeTime(minTime);
  let cleanMaxTime = sanitizeTime(maxTime);
  const hasBothEndsOfRange = cleanMinTime && cleanMaxTime;
  const hasTimeOverlap = getTimeInMinutes(cleanMaxTime) < getTimeInMinutes(cleanMinTime);

  // handle overlapping min and max times
  if (hasBothEndsOfRange && hasTimeOverlap) {
    cleanMinTime = '';
    cleanMaxTime = '';

    /* eslint-disable-next-line no-console */
    console.warn('The `minTime` and `maxTime` props supplied to the `Calendar.TimeInput` are overlapping. `minTime` should be less than `maxTime`.');
  }
  return [cleanMinTime, cleanMaxTime];
};

/**
 * Given a number, `step`, sanitize step and convert it from a number that represents minutes to a
 * number that represents seconds.
 *
 * The HTML input component accepts a number in seconds for the `step` attribute, but to simplify
 * things (since the `Calendar.TimeInput` component doesn't handle seconds) we accept a number
 * representing minutes as the step value.
 *
 * @param {number} step The minimum unit of selectable time, in minutes
 * @returns {number} The minimum unit of selectable time, in seconds
 */
const sanitizeStep = step => {
  const MINIMUM_STEP = 1;
  const MAXIMUM_STEP = 120;
  const stepIsInvalid = typeof step !== 'number' || Number.isNaN(parseFloat(step)) // step is a float
  || step < MINIMUM_STEP || step > MAXIMUM_STEP || MAXIMUM_STEP % step;
  if (stepIsInvalid) {
    /* eslint-disable-next-line no-console */
    console.warn(`${getPropWarningText('step')} The value passed to \`step\` must be an integer between 1 and 60 (inclusive). Instead, the value ${step} was passed to \`step\``);
    return 60;
  }
  return Number(step) * 60;
};

/**
 * Ensure that the props passed to the `Calendar` component are valid.
 *
 * If the `Calendar.TimeInput` component is not being used, props we would have received from that
 * component will be defaulted.
 */
const sanitizeCalendarProps = (_ref, _ref2) => {
  let {
    align,
    dateFormat,
    dateRangeEnd,
    dateRangeStart,
    disabledDates,
    endDate,
    maxDate,
    minDate,
    month,
    numberOfMonths,
    renderNode,
    selectedDate,
    showCalendarOnly,
    startDate,
    weekStartsOn,
    year,
    ...otherProps
  } = _ref;
  let {
    disabledTimes = {
      times: []
    },
    isUsingTimeSelect = false,
    maxTime = '',
    minTime = '',
    step = 1
  } = _ref2;
  const cleanDateFormat = sanitizeDateFormat(dateFormat);
  const isOneCalendarBeingUsedForADateRange = dateRangeStart && dateRangeEnd;

  // don't show input if one calendar is being used for a date range
  const cleanShowCalendarOnly = isOneCalendarBeingUsedForADateRange || showCalendarOnly;
  const cleanAlign = [null, 'left', 'right'].includes(align) ? align : null;
  const cleanRenderNode = renderNode instanceof checkForElement() ? renderNode : null;

  // we convert step to seconds for `Calendar.TimeSelect`, convert it back to minutes
  const cleanStep = sanitizeStep(step) / 60;
  const cleanDisabledDates = sanitizeDisabledDates(disabledDates);
  const [cleanMinTime, cleanMaxTime] = sanitizeMinAndMaxTimes(minTime, maxTime);
  const [cleanMinDate, cleanMaxDate] = sanitizeMinAndMaxDates(sanitizeDate(minDate), sanitizeDate(maxDate), cleanMinTime || DEFAULT_MIN_TIME, cleanMaxTime || DEFAULT_MAX_TIME, cleanDisabledDates, disabledTimes, cleanStep);
  const cleanEndDate = sanitizeDateBounds(cleanMinDate, sanitizeDate(endDate), cleanMaxDate);
  const cleanStartDate = sanitizeDateBounds(cleanMinDate, sanitizeDate(startDate), cleanMaxDate);
  const cleanSelectedDate = sanitizeDateBounds(cleanMinDate, sanitizeDate(selectedDate), cleanMaxDate);
  const cleanNumberOfMonths = sanitizeNumberOfMonths(numberOfMonths);
  const cleanMonth = sanitizeMonth(month, cleanMinDate, cleanMaxDate);
  const cleanYear = sanitizeYear(year, cleanMonth, cleanMinDate, cleanMaxDate);
  return {
    ...otherProps,
    align: cleanAlign,
    dateFormat: cleanDateFormat,
    dateRangeEnd,
    dateRangeStart,
    disabledDates: cleanDisabledDates,
    endDate: cleanEndDate,
    maxDate: cleanMaxDate,
    minDate: cleanMinDate,
    month: cleanMonth,
    numberOfMonths: cleanNumberOfMonths,
    renderNode: cleanRenderNode,
    selectedDate: cleanSelectedDate,
    showCalendarOnly: cleanShowCalendarOnly,
    startDate: cleanStartDate,
    step: isUsingTimeSelect ? cleanStep : NUMBER_OF_MINUTES_IN_A_DAY,
    weekStartsOn: sanitizeWeekStartsOn(weekStartsOn),
    year: cleanYear
  };
};
var sanitizeCalendarProps$1 = sanitizeCalendarProps;

/**
 * Ensure that all of the props passed to `Calendar.TimeInput` are valid.
 */
const sanitizeTimeProps = _ref => {
  let {
    disabledTimes,
    maxTime,
    minTime,
    selectedTime,
    step,
    timeInterval,
    ...otherProps
  } = _ref;
  const cleanStep = sanitizeStep(step);
  const cleanTimeInterval = sanitizeTimeInterval(timeInterval, cleanStep);
  const [cleanMinTime, cleanMaxTime] = sanitizeMinAndMaxTimes(minTime, maxTime);
  const cleanSelectedTime = sanitizeTime(selectedTime);
  const cleanDisabledTimes = sanitizeDisabledTimes(disabledTimes);
  return {
    ...otherProps,
    disabledTimes: cleanDisabledTimes,
    maxTime: cleanMaxTime || DEFAULT_MAX_TIME,
    minTime: cleanMinTime || DEFAULT_MIN_TIME,
    selectedTime: cleanSelectedTime,
    step: cleanStep,
    timeInterval: cleanTimeInterval
  };
};
var sanitizeTimeProps$1 = sanitizeTimeProps;

/**
 * A React hook for handling errors in the calendar hooks.
 *
 * Because of edge cases where error's state is set by multiple `Calendar` and/or
 * `Calendar.TimeInput` components at the same time, some of the state values will not be set. We
 * handle this by using a React ref to track error's state, and trigger a rerender by setting a
 * dummy piece of state.
 *
 * @param {import('./types').OnErrorArgumentType} defaultErrorState A value that represents new
 * error state in a calendar hook
 * @returns {[
 *   import('./types').OnErrorArgumentType,
 *   (newError: import('./types').OnErrorArgumentType) =>
 *     import('./types').OnErrorArgumentType
 * ]} An array containing a value that represents an error at the 0th index, and a state setting
 * function for error at the 1st index
 */
const useError = defaultErrorState => {
  const errorRef = useRef(defaultErrorState);

  // only to trigger rerender
  const [rerender, setRerender] = useState(false);

  /**
   * Update `error` in the calendar hook. The new state is returned for convenience.
   *
   * @param {import('./types').OnErrorArgumentType} error The next error state in the calendar
   * hook
   * @returns {import('./types').OnErrorArgumentType}
   */
  const setError = error => {
    // calendarHooks.useDateRange, calendarHooks.useDateAndTime, calendarHooks.useDateAndTimeRange
    if (typeof error === 'object') {
      errorRef.current = {
        ...errorRef.current,
        ...error
      };

      // calendarHooks.useDate
    } else {
      errorRef.current = error;
    }

    // trigger rerender
    setRerender(!rerender);

    // give instant feedback about next error state
    return errorRef.current;
  };
  return [errorRef.current, setError];
};

/**
 * Throw warnings if unknown values are passed to `onSelect` handler function.
 *
 * @param {{
 *   endDate?: true,
 *   endTime?: true,
 *   selectedDate?: true,
 *   selectedTime?: true,
 *   startDate?: true,
 *   startTime?: true,
 * }} validKeys An object containing keys that are valid in `nextState`
 * @param {import('./types').OnSelectArgumentType} nextState An object that represents new state
 * in the `useCalendar` hook
 */
const validateKeys = (name, validKeys, nextState) => {
  Object.keys(nextState).forEach(key => {
    /* eslint-disable-next-line no-console */
    if (!(key in validKeys)) console.warn(`Unknown value passed to \`${name}\`: "${key}".`);
  });
};

/**
 * An object containing React hooks related to the calendar.
 *
 * `calendarHooks.useDate`: A hook for selecting a single date from the calendar
 *
 * `calendarHooks.useDateAndTime`: A hook for selecting single date and time from the calendar
 *
 * `calendarHooks.useDateRange`: A hook for selecting a start date and end date from calendar(s)
 *
 * `calendarHooks.useDateAndTimeRange`: A hook for selecting a start date and time and an end date
 * and time from calendar(s)
 *
 * `hooks.usePortal`: A hook for creating a portal root for rendering the calendar from
 */
const calendarHooks = Object.freeze({
  /**
   * A React hook to manage creation of a calendar portal root element.
   */
  usePortal: () => {
    const PORTAL_ID = 'st-calendar-portal';
    let renderNode = document.getElementById(PORTAL_ID);
    if (!renderNode) {
      renderNode = document.createElement('div');
      renderNode.setAttribute('id', PORTAL_ID);
      document.body.appendChild(renderNode);
    }
    return renderNode;
  },
  /**
   * A React hook for Screentone's `Calendar` component to handle date selection.
   *
   * This hook exposes four different values:
   * - **selectedDate**: A Javascript date object that represents the value selected for the
   * `Calendar` component (when a corresponding time value is selected, it will be applied to the
   * date object).
   * - **onSelect**: A state setting function that handles updating `selectedDate`. This function
   * takes in a Javascript date object or `null` and updates the state of `selectedDate`
   * accordingly.
   * - **error**: Either a string which represents the error associated with the `Calendar`
   * component, or a boolean which indicates whether or not the `Calendar` component contains an
   * error.
   * - **onError**: A state setting function that handles updating `error`. This function takes in
   * either a string which represents the error, or a boolean which indicates whether or not the
   * calendar contains any errors. Passing this function to the `Calendar` component is optional,
   * but necessary for `error` to be up to date.
   *
   * @example
   * const MyComponent = () => {
   *   const { error, onError, onSelect, selectedDate } = useCalendar.date(defaultSelectedDate);
   *
   *   return (
   *     <Calendar
   *       onError={onError} // optional - for tracking errors
   *       onSelect={onSelect}
   *       selectedDate={selectedDate}
   *     />
   *   );
   * };
   *
   * @param {Date | null} [defaultState] - default value for the calendar's `selectedDate`
   * @returns {{
   *   error: string | boolean,
   *   onSelect: (newDate: Date | null) => Date | null,
   *   onError: (error: string | boolean) => string | boolean,
   *   selectedDate: Date | null,
   * }} object containing props to be passed to Screentone's `Calendar` component
   */
  useDate: defaultState => {
    // if default state is an object, log a warning if the keys are unrecognized
    if (defaultState !== null && !(defaultState instanceof Date) && typeof defaultState === 'object') {
      validateKeys('useDate', {
        [KEYS.SELECTED_DATE]: true
      }, defaultState);
    }
    let sanitizedDefault = defaultState?.selectedDate || defaultState || null;
    if (sanitizedDefault !== null && !isDateValid(sanitizedDefault)) {
      /* eslint-disable-next-line no-console */
      console.warn(`useCalendar can only accept parameters of type Date as default values for selectedDate. A value of type ${typeof sanitizedDefault} was supplied.`);
      sanitizedDefault = null;
    }
    const [error, onError] = useError(false);
    const [selectedDate, setSelectedDate] = useState(sanitizedDefault);

    /**
     * Given an object with key/value pairs that corresponds to state returned from the
     * `useCalendar` hooks, update the state accordingly.
     *
     * @param {import('./types').OnSelectArgumentType} newState An object containing new state
     * for the `useCalendar` hook.
     * @returns {Date | null} The new state in the calendar hook
     */
    const onSelect = newState => {
      // represents the state of the calendar hook after the state updates are made
      let returnedState = null;
      if (newState && !(newState instanceof Date)) {
        validateKeys('onSelect', {
          [KEYS.SELECTED_DATE]: true
        }, newState);
      }
      if (newState && KEYS.SELECTED_DATE in newState) {
        setSelectedDate(newState.selectedDate);
        returnedState = newState.selectedDate;
      } else {
        setSelectedDate(newState);
        returnedState = newState;
      }

      // give instant feedback about next state
      return returnedState;
    };
    return {
      error,
      onError,
      onSelect,
      selectedDate
    };
  },
  /**
   * A React hook for Screentone's `Calendar` component to handle date ranges.
   *
   * This hook exposes five different values:
   * - **startDate**: A Javascript date object that represents the value selected for the start of
   * the date range (when a corresponding time value is selected, it will be applied to the date
   * object).
   * - **endDate**: A Javascript date object that represents the value selected for the end of the
   * date range (when a corresponding time value is selected, it will be applied to the date
   * object).
   * - **onSelect**: A state setting function that handles updating `startDate`, `startTime`,
   * `endDate`, `endTime` and `error`. This function takes in an object with keys that correspond to
   * the piece of state to be updated, and values which represent their new values.
   * - **error**: An object which contains key/value pairs which correspond to pieces of state in
   * the hook, and errors associated with them. These errors will either be a string which
   * represents the error associated with the piece of state, or a boolean which indicates whether
   * that piece of state has an error. `onError` must be passed to the `Calendar` components for
   * `error` to be up to date.
   * - **onError**: A state setting function that handles updating `error`. This function takes in
   * an object with keys that correspond to the piece of state the contains an error and a value
   * that is either a string which represents an error, or a boolean which indicates whether or not
   * that piece of state has an error associated with it. Passing this function to the `Calendar`
   * components is optional, but necessary for `error` to be up to date.
   *
   * @example
   * const MyComponent = () => {
   *   const { endDate, error, onError, onSelect, startDate } = useCalendar.dateRange({
   *     endDate: defaultEndDate,
   *     endTime: defaultEndTime,
   *   });
   *
   *   return (
   *     <>
   *       <Calendar
   *         dateRangeStart // signifies the start of a date range
   *         onError={(err) => onError({ startDate: err })} // optional - for tracking errors
   *         onSelect={(day) => onSelect({ startDate: day })}
   *         startDate={startDate}
   *       />
   *       <Calendar
   *         dateRangeEnd // signifies the end of a date range
   *         onError={(err) => onError({ endDate: err })} // optional - for tracking errors
   *         onSelect={(day) => onSelect({ endDate: day })}
   *         endDate={endDate}
   *       />
   *     </>
   *   );
   * };
   *
   * @param {{
   *   endDate?: Date | null,
   *   startDate?: Date | null,
   * }} [defaultState] An object containing optional default values for `startDate` and `endDate`
   * @returns {{
   *   error: string | boolean,
   *   endDate: Date | null,
   *   onSelect: (newDate: import('./types').OnSelectArgumentType) =>
   *     import('./types').OnSelectArgumentType,
   *   onError: (error: import('./types').OnErrorArgumentType) =>
   *     import('./types').OnErrorArgumentType,
   *   startDate: Date | null,
   * }} object containing props to be passed to Screentone's `Calendar` component
   */
  useDateRange: defaultState => {
    // log a warning if the keys are unrecognized
    if (defaultState) {
      validateKeys('useDateRange', {
        [KEYS.END_DATE]: true,
        [KEYS.START_DATE]: true
      }, defaultState);
    }
    let sanitizedStartDefault = defaultState?.startDate || null;
    let sanitizedEndDefault = defaultState?.endDate || null;
    if (sanitizedStartDefault !== null && !isDateValid(sanitizedStartDefault)) {
      /* eslint-disable-next-line no-console */
      console.warn(`useCalendarRange can only accept parameters of type Date as default values for startDate. A value of type ${typeof sanitizedStartDefault} was supplied.`);
      sanitizedStartDefault = null;
    }
    if (sanitizedEndDefault !== null && !isDateValid(sanitizedEndDefault)) {
      /* eslint-disable-next-line no-console */
      console.warn(`useCalendarRange can only accept parameters of type Date as default values for endDate. A value of type ${typeof sanitizedEndDefault} was supplied.`);
      sanitizedEndDefault = null;
    }

    // log a warning if there is a date overlap in the default dates, but don't fix it
    if (sanitizedStartDefault && sanitizedEndDefault && sanitizedStartDefault > sanitizedEndDefault) {
      /* eslint-disable-next-line no-console */
      console.warn('useCalendarRange has been passed a default `startDate` that is greater than the default `endDate`.');
    }
    const [error, onError] = useError({
      startDate: false,
      endDate: false
    });
    const [startDate, setStartDate] = useState(sanitizedStartDefault);
    const [endDate, setEndDate] = useState(sanitizedEndDefault);

    /**
     * Given an object with key/value pairs that corresponds to state returned from the
     * `useCalendar` hooks, update the state accordingly.
     *
     * **NOTE:** This hook handles date range overlaps and switches `startDate` and `endDate`
     * accordingly.
     *
     * @param {import('./types').OnSelectArgumentType} newState An object containing new state
     * for the `useCalendar` hook.
     * @returns {import('./types').OnSelectArgumentType} The new state in the calendar hook
     */
    const onSelect = newState => {
      // represents the state of the calendar hook after the state updates are made
      const returnedState = {
        startDate,
        endDate
      };
      if (!newState) {
        /* eslint-disable-next-line no-console */
        console.warn('`onSelect` was passed an invalid value. Only values of type `Date` or an object containing `startDate` and `endDate` are valid.');
      }

      // range on one calendar - we manage which state (start or end) to set internally
      if (newState instanceof Date) {
        ReactDOM.unstable_batchedUpdates(() => {
          // start/restart range selection
          if (!startDate && !endDate || startDate && endDate) {
            setStartDate(newState);
            setEndDate(null);
            returnedState.startDate = newState;
            returnedState.endDate = null;

            // handle date range overlaps
          } else if (startDate && startDate > newState) {
            setStartDate(newState);
            setEndDate(startDate);
            returnedState.startDate = newState;
            returnedState.endDate = startDate;

            // handle date range overlaps
          } else if (endDate && endDate < newState) {
            setStartDate(endDate);
            setEndDate(newState);
            returnedState.startDate = endDate;
            returnedState.endDate = newState;

            // select the end of the range
          } else {
            setEndDate(newState);
            returnedState.endDate = newState;
          }
        });

        // manually controlled state setting - an object is passed as `newState` which determines
        // whether we should updated `startDate`, `endDate` or both
      } else {
        validateKeys('onSelect', {
          [KEYS.END_DATE]: true,
          [KEYS.START_DATE]: true
        }, newState);
        const newStateIncludesStart = (KEYS.START_DATE in newState);
        const newStateIncludesEnd = (KEYS.END_DATE in newState);
        const start = newStateIncludesStart ? newState.startDate : startDate;
        const end = newStateIncludesEnd ? newState.endDate : endDate;
        ReactDOM.unstable_batchedUpdates(() => {
          // if start and end overlap, swap start and end
          if (isDateValid(start) && isDateValid(end) && start > end) {
            setStartDate(end);
            setEndDate(start);
            returnedState.startDate = end;
            returnedState.endDate = start;
          } else {
            if (newStateIncludesStart) {
              setStartDate(start);
              returnedState.startDate = start;
            }
            if (newStateIncludesEnd) {
              setEndDate(end);
              returnedState.endDate = end;
            }
          }
        });
      }

      // give instant feedback about next state
      return returnedState;
    };
    return {
      error,
      endDate,
      onError,
      onSelect,
      startDate
    };
  },
  /**
   * A React hook for Screentone's `Calendar` component to handle date and time selection.
   *
   * This hook exposes five different values:
   * - **selectedDate**: A Javascript date object that represents the value selected for the
   * `Calendar` component (when a corresponding time value is selected, it will be applied to the
   * date object).
   * - **selectedTime**: A string that represents the time value selected by the
   * `Calendar.TimeInput` component.
   * - **onSelect**: A state setting function that handles updating `selectedDate` and
   * `selectedTime`. This function takes in an object with keys that correspond to the piece of
   * state to be updated, and values which represent their new values.
   * - **error**: An object which contains key/value pairs which correspond to pieces of state in
   * the hook, and errors associated with them. These errors will either be a string which
   * represents the error associated with the piece of state, or a boolean which indicates whether
   * that piece of state has an error. `onError` must be passed to the `Calendar` and/or the
   * `Calendar.TimeInput` components for `error` to be up to date.
   * - **onError**: A state setting function that handles updating `error`. This function takes in
   * an object with keys that correspond to the piece of state the contains an error and a value
   * that is either a string which represents an error, or a boolean which indicates whether or not
   * that piece of state has an error associated with it. Passing this function to the `Calendar`
   * and/or `Calendar.TimeInput` components is optional, but necessary for `error` to be up to date.
   *
   *
   * @example
   * const MyComponent = () => {
   *   const { error, onError, onSelect, selectedDate, selectedTime } = useCalendar.dateAndTime({
   *     selectedDate: defaultSelectedDate,
   *     selectedTime: defaultSelectedTime,
   *   });
   *
   *   return (
   *     <>
   *       <Calendar
   *         onError={(err) => onError({ selectedDate: err })} // optional - for tracking errors
   *         onSelect={(day) => onSelect({ selectedDate: day })}
   *         selectedDate={selectedDate}
   *       />
   *       <Calendar.TimeInput
   *         onError={(err) => onError({ selectedTime: err })} // optional - for tracking errors
   *         onSelect={(time) => onSelect({ selectedTime: time })}
   *         selectedDate={selectedDate}
   *         selectedTime={selectedTime}
   *       />
   *     </>
   *   );
   * };
   *
   * @param {{
   *   selectedDate?: Date | null,
   *   selectedTime?: string,
   * }} [defaultState] An object containing optional default values for `selectedDate` and
   * `selectedTime`
   * @returns {{
   *   error: string | boolean,
   *   onSelect: (newDate: import('./types').OnSelectArgumentType) =>
   *     import('./types').OnSelectArgumentType,
   *   onError: (error: import('./types').OnErrorArgumentType) =>
   *     import('./types').OnErrorArgumentType,
   *   selectedDate: Date | null,
   *   selectedTime: string,
   * }} object containing props to be passed to Screentone's `Calendar` component
   */
  useDateAndTime: defaultState => {
    // log a warning if the keys are unrecognized
    if (defaultState) {
      validateKeys('useDateAndTime', {
        [KEYS.SELECTED_DATE]: true,
        [KEYS.SELECTED_TIME]: true
      }, defaultState);
    }
    let sanitizedDefaultDate = defaultState?.selectedDate || null;
    let sanitizedDefaultTime = defaultState?.selectedTime || '';
    if (sanitizedDefaultDate !== null && !isDateValid(sanitizedDefaultDate)) {
      /* eslint-disable-next-line no-console */
      console.warn(`useCalendarAndTime can only accept parameters of type Date as default values for selectedDate. A value of type ${typeof sanitizedDefaultDate} was supplied.`);
      sanitizedDefaultDate = null;
    }
    if (sanitizedDefaultTime && typeof sanitizedDefaultTime !== 'string') {
      /* eslint-disable-next-line no-console */
      console.warn(`useCalendarAndTime can only accept parameters of type string as default values for selectedTime. A value of type ${typeof sanitizedDefaultTime} was supplied.`);
      sanitizedDefaultTime = '';
    }

    // apply time value to default date if we have it
    if (sanitizedDefaultDate && sanitizedDefaultTime) {
      sanitizedDefaultDate = getDateWithTime(sanitizedDefaultDate, sanitizedDefaultTime);
    }
    const [error, onError] = useError({
      selectedDate: false,
      selectedTime: false
    });
    const [selectedDate, setSelectedDate] = useState(sanitizedDefaultDate);
    const [selectedTime, setSelectedTime] = useState(sanitizedDefaultTime);

    /**
     * Given an object with key/value pairs that corresponds to state returned from the
     * `useCalendar` hooks, update the state accordingly.
     *
     * @param {import('./types').OnSelectArgumentType} newState An object containing new state
     * for the `useCalendar` hook.
     * @returns {import('./types').OnSelectArgumentType} The new state in the calendar hook
     */
    const onSelect = newState => {
      // represents the state of the calendar hook after the state updates are made
      const returnedState = {
        selectedDate,
        selectedTime
      };
      validateKeys('onSelect', {
        [KEYS.SELECTED_DATE]: true,
        [KEYS.SELECTED_TIME]: true
      }, newState);
      ReactDOM.unstable_batchedUpdates(() => {
        const shouldUpdateDate = KEYS.SELECTED_DATE in newState || KEYS.SELECTED_TIME in newState;
        if (shouldUpdateDate) {
          const date = KEYS.SELECTED_DATE in newState ? newState[KEYS.SELECTED_DATE] : selectedDate;
          const dateWithTime = getDateWithTime(date, newState.selectedTime || selectedTime);
          setSelectedDate(dateWithTime);
          returnedState.selectedDate = dateWithTime;
        }
        if (KEYS.SELECTED_TIME in newState) {
          setSelectedTime(newState.selectedTime);
          returnedState.selectedTime = newState.selectedTime;
        }
      });

      // give instant feedback about next state
      return returnedState;
    };
    return {
      error,
      onError,
      onSelect,
      selectedDate,
      selectedTime
    };
  },
  /**
   * A React hook for Screentone's `Calendar` component to handle date ranges with times.
   *
   * This hook exposes seven different values:
   * - **startDate**: A Javascript date object that represents the value selected for the start of
   * the date range (when a corresponding time value is selected, it will be applied to the date
   * object).
   * - **startTime**: A string that represents the time value selected for the start of the date
   * range.
   * - **endDate**: A Javascript date object that represents the value selected for the end of the
   * date range (when a corresponding time value is selected, it will be applied to the date
   * object).
   * - **endTime**: A string that represents the time value selected for the end of the date range.
   * - **onSelect**: A state setting function that handles updating `startDate`, `startTime`,
   * `endDate`, `endTime` and `error`. This function takes in an object with keys that correspond to
   * the piece of state to be updated, and values which represent their new values.
   * - **error**: An object which contains key/value pairs which correspond to pieces of state in
   * the hook, and errors associated with them. These errors will either be a string which
   * represents the error associated with the piece of state, or a boolean which indicates whether
   * that piece of state has an error. `onError` must be passed to the `Calendar` and/or the
   * `Calendar.TimeInput` components for `error` to be up to date.
   * - **onError**: A state setting function that handles updating `error`. This function takes in
   * an object with keys that correspond to the piece of state the contains an error and a value
   * that is either a string which represents an error, or a boolean which indicates whether or not
   * that piece of state has an error associated with it. Passing this function to the `Calendar`
   * and/or `Calendar.TimeInput` components is optional, but necessary for `error` to be up to date.
   *
   * @example
   * const MyComponent = () => {
   *   const {
   *     endDate, endTime, error, onError, onSelect, startDate, startTime,
   *   } = useCalendar.dateAndTimeRange({
   *     endDate: defaultEndDate,
   *     endTime: defaultEndTime,
   *     startDate: defaultStartDate,
   *     startTime: defaultStartTime,
   *   });
   *
   *   return (
   *     <>
   *       <Calendar
   *         dateRangeStart // signifies the start of a date range
   *         onError={(err) => onError({ startDate: err })} // optional - for tracking errors
   *         onSelect={(day) => onSelect({ startDate: day })}
   *         startDate={startDate}
   *       />
   *       <Calendar.TimeInput
   *         onError={(err) => onError({ startTime: err })} // optional - for tracking errors
   *         onSelect={(time) => onSelect({ startTime: time })}
   *         selectedDate={startDate}
   *         selectedTime={startTime}
   *       />
   *       <Calendar
   *         dateRangeEnd // signifies the end of a date range
   *         onError={(err) => onError({ endDate: err })} // optional - for tracking errors
   *         onSelect={(day) => onSelect({ endDate: day })}
   *         endDate={endDate}
   *       />
   *       <Calendar.TimeInput
   *         onError={(err) => onError({ endTime: err })} // optional - for tracking errors
   *         onSelect={(time) => onSelect({ endTime: time })}
   *         selectedDate={endDate}
   *         selectedTime={endTime}
   *       />
   *     </>
   *   );
   * };
   *
   * @param {{
   *   endDate?: Date | null,
   *   endTime?: string,
   *   startDate?: Date | null,
   *   startTime?: string,
   * }} [defaultState] An object containing optional default values for `startDate`, `startTime`,
   * `endDate` and `endTime`
   * @returns {{
   *   error: string | boolean,
   *   endDate: Date | null,
   *   endTime: string,
   *   onError: (error: import('./types').OnErrorArgumentType) =>
   *     import('./types').OnErrorArgumentType,
   *   onSelect: (newDate: import('./types').OnSelectArgumentType) =>
   *     import('./types').OnSelectArgumentType,
   *   startDate: Date | null,
   *   startTime: string,
   * }}
   */
  useDateAndTimeRange: defaultState => {
    // log a warning if the keys are unrecognized
    if (defaultState) {
      validateKeys('useDateAndTimeRange', {
        [KEYS.END_DATE]: true,
        [KEYS.END_TIME]: true,
        [KEYS.START_DATE]: true,
        [KEYS.START_TIME]: true
      }, defaultState);
    }
    let sanitizedDefaultStartDate = defaultState?.startDate || null;
    let sanitizedDefaultEndDate = defaultState?.endDate || null;
    let sanitizedDefaultStartTime = defaultState?.startTime || '';
    let sanitizedDefaultEndTime = defaultState?.endTime || '';
    if (sanitizedDefaultStartDate !== null && !isDateValid(sanitizedDefaultStartDate)) {
      /* eslint-disable-next-line no-console */
      console.warn(`useCalendarAndTimeRange can only accept parameters of type Date as default values for startDate. A value of type ${typeof sanitizedDefaultStartDate} was supplied.`);
      sanitizedDefaultStartDate = null;
    }
    if (sanitizedDefaultEndDate !== null && !isDateValid(sanitizedDefaultEndDate)) {
      /* eslint-disable-next-line no-console */
      console.warn(`useCalendarAndTimeRange can only accept parameters of type Date as default values for endDate. A value of type ${typeof sanitizedDefaultEndDate} was supplied.`);
      sanitizedDefaultEndDate = null;
    }
    if (sanitizedDefaultStartTime && typeof sanitizedDefaultStartTime !== 'string') {
      /* eslint-disable-next-line no-console */
      console.warn(`useCalendarAndTimeRange can only accept parameters of type string as default values for startTime. A value of type ${typeof sanitizedDefaultStartTime} was supplied.`);
      sanitizedDefaultStartTime = '';
    }
    if (sanitizedDefaultStartTime && typeof sanitizedDefaultStartTime !== 'string') {
      /* eslint-disable-next-line no-console */
      console.warn(`useCalendarAndTimeRange can only accept parameters of type string as default values for endTime. A value of type ${typeof sanitizedDefaultEndTime} was supplied.`);
      sanitizedDefaultEndTime = '';
    }

    // apply time value to start date if we have it
    if (sanitizedDefaultStartDate && sanitizedDefaultStartTime) {
      sanitizedDefaultStartDate = getDateWithTime(sanitizedDefaultStartDate, sanitizedDefaultStartTime);
    }

    // apply time value to end date if we have it
    if (sanitizedDefaultEndDate && sanitizedDefaultEndTime) {
      sanitizedDefaultEndDate = getDateWithTime(sanitizedDefaultEndDate, sanitizedDefaultEndTime);
    }

    // log a warning if there is a date overlap in the default dates, but don't fix it
    if (sanitizedDefaultStartDate && sanitizedDefaultEndDate && sanitizedDefaultStartDate > sanitizedDefaultEndDate) {
      /* eslint-disable-next-line no-console */
      console.warn('useCalendarRange has been passed a default `startDate` that is greater than the default `endDate`. If the values are the same, perhaps the default time values caused an overlap.');
    }
    const [error, onError] = useError({
      endDate: false,
      endTime: false,
      startDate: false,
      startTime: false
    });
    const [startDate, setStartDate] = useState(sanitizedDefaultStartDate);
    const [endDate, setEndDate] = useState(sanitizedDefaultEndDate);
    const [startTime, setStartTime] = useState(sanitizedDefaultStartTime);
    const [endTime, setEndTime] = useState(sanitizedDefaultEndTime);

    /**
     * Given an object with key/value pairs that corresponds to state returned from the
     * `useCalendar` hooks, update the state accordingly.
     *
     * **NOTE:** This hook handles date range overlaps and switches `startDate` and `endDate` and
     * `startTime` and `endTime` accordingly.
     *
     * @param {import('./types').OnSelectArgumentType} newState An object containing new state
     * for the `useCalendar` hook.
     * @returns {import('./types').OnSelectArgumentType}
     */
    const onSelect = newState => {
      // represents the state of the calendar hook after the state updates are made
      const returnedState = {
        endDate,
        endTime,
        startDate,
        startTime
      };
      validateKeys('onSelect', {
        [KEYS.END_DATE]: true,
        [KEYS.END_TIME]: true,
        [KEYS.START_DATE]: true,
        [KEYS.START_TIME]: true
      }, newState);
      const newStateIncludesStartDate = (KEYS.START_DATE in newState);
      const newStateIncludesStartTime = (KEYS.START_TIME in newState);
      const newStateIncludesEndDate = (KEYS.END_DATE in newState);
      const newStateIncludesEndTime = (KEYS.END_TIME in newState);
      const nextStartTime = newStateIncludesStartTime ? newState.startTime : startTime;
      let nextStartDate = startDate;

      // we have a new startDate, set the time value accordingly
      if (newStateIncludesStartDate) {
        nextStartDate = getDateWithTime(newState.startDate, nextStartTime);

        // if a new startTime has been selected, and not a new startDate, but we do have a
        // previously selected startDate, update the old date with the new time
      } else if (startDate && newStateIncludesStartTime) {
        nextStartDate = getDateWithTime(startDate, nextStartTime);
      }
      const nextEndTime = newStateIncludesEndTime ? newState.endTime : endTime;
      let nextEndDate = endDate;

      // we have a new endDate, set the time value accordingly
      if (newStateIncludesEndDate) {
        nextEndDate = getDateWithTime(newState.endDate, nextEndTime);

        // if a new endTime has been selected, and not a new endDate, but we do have a previously
        // selected endDate, update the old date with the new time
      } else if (endDate && newStateIncludesEndTime) {
        nextEndDate = getDateWithTime(endDate, nextEndTime);
      }
      ReactDOM.unstable_batchedUpdates(() => {
        const isDateRangeComplete = !!nextStartDate && !!nextEndDate;
        const isTimeRangeComplete = !!nextStartTime && !!nextEndTime;
        const isStartAndEndTheSameDay = isDateRangeComplete && Math.abs(differenceInCalendarDays(nextStartDate, nextEndDate)) < 1;
        const shouldSwapDates = !isStartAndEndTheSameDay && isDateRangeComplete && nextStartDate?.getTime() > nextEndDate?.getTime();
        const shouldSwapTimes = isStartAndEndTheSameDay && isTimeRangeComplete && getTimeInMinutes(nextStartTime) > getTimeInMinutes(nextEndTime);

        // if dates are on different days, swap days
        if (shouldSwapDates) {
          setStartDate(nextEndDate);
          setEndDate(nextStartDate);
          returnedState.startDate = nextEndDate;
          returnedState.endDate = nextStartDate;
          if (nextStartTime !== startTime) {
            setStartTime(nextStartTime);
            returnedState.startTime = nextStartTime;
          }
          if (nextEndTime !== endTime) {
            setEndTime(nextEndTime);
            returnedState.endTime = nextEndTime;
          }

          // if dates are on the same day, swap times
        } else if (shouldSwapTimes) {
          setStartTime(nextEndTime);
          setEndTime(nextStartTime);
          returnedState.startTime = nextEndTime;
          returnedState.endTime = nextStartTime;

          // be sure to update startDate and endDate with the correct times
          setStartDate(nextStartDate);
          setEndDate(nextEndDate);
          returnedState.startDate = nextStartDate;
          returnedState.endDate = nextEndDate;

          // set whatever pieces of state have updated
        } else {
          if (nextStartDate?.getTime() !== startDate?.getTime()) {
            setStartDate(nextStartDate);
            returnedState.startDate = nextStartDate;
          }
          if (nextEndDate?.getTime() !== endDate?.getTime()) {
            setEndDate(nextEndDate);
            returnedState.endDate = nextEndDate;
          }
          if (nextStartTime !== startTime) {
            setStartTime(nextStartTime);
            returnedState.startTime = nextStartTime;
          }
          if (nextEndTime !== endTime) {
            setEndTime(nextEndTime);
            returnedState.endTime = nextEndTime;
          }
        }
      });

      // give instant feedback about next state
      return returnedState;
    };
    return {
      error,
      endDate,
      endTime,
      onError,
      onSelect,
      startDate,
      startTime
    };
  }
});
var calendarHooks$1 = calendarHooks;

/**
 * @name getDaysInMonth
 * @category Month Helpers
 * @summary Get the number of days in a month of the given date.
 *
 * @description
 * Get the number of days in a month of the given date.
 *
 * @param {Date|Number} date - the given date
 * @returns {Number} the number of days in a month
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // How many days are in February 2000?
 * const result = getDaysInMonth(new Date(2000, 1))
 * //=> 29
 */

function getDaysInMonth(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var year = date.getFullYear();
  var monthIndex = date.getMonth();
  var lastDayOfMonth = new Date(0);
  lastDayOfMonth.setFullYear(year, monthIndex + 1, 0);
  lastDayOfMonth.setHours(0, 0, 0, 0);
  return lastDayOfMonth.getDate();
}

/**
 * @name startOfMonth
 * @category Month Helpers
 * @summary Return the start of a month for the given date.
 *
 * @description
 * Return the start of a month for the given date.
 * The result will be in the local timezone.
 *
 * @param {Date|Number} date - the original date
 * @returns {Date} the start of a month
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // The start of a month for 2 September 2014 11:55:00:
 * const result = startOfMonth(new Date(2014, 8, 2, 11, 55, 0))
 * //=> Mon Sep 01 2014 00:00:00
 */

function startOfMonth(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  date.setDate(1);
  date.setHours(0, 0, 0, 0);
  return date;
}

// TODO: add tests for this

// make any modifications with care, it's unlikely that this will need to be changed
const createCalendarMatrix = function (month, year, numberOfMonths, weekStartsOn) {
  if (numberOfMonths === void 0) {
    numberOfMonths = 1;
  }
  if (weekStartsOn === void 0) {
    weekStartsOn = 0;
  }
  const matrix = [];

  /* eslint-disable-next-line no-plusplus */
  for (let m = month; m < month + numberOfMonths; m++) {
    const currentMonthForPanel = m % 12;
    const currentYearForPanel = year + Math.floor(m / 12);
    const currentDateForPanel = getAccurateDate(currentYearForPanel, currentMonthForPanel, 1);
    const dayOfWeekOfFirstDayOfMonth = startOfMonth(currentDateForPanel, {
      weekStartsOn
    }).getDay();
    const prevMonthOffset = dayOfWeekOfFirstDayOfMonth - weekStartsOn < 0 ? 6 + (dayOfWeekOfFirstDayOfMonth - weekStartsOn) : dayOfWeekOfFirstDayOfMonth - weekStartsOn - 1;
    matrix.push([[], [], [], [], [], []]); // fixed at 6 weeks per panel

    let currentDate = addMonths(currentDateForPanel, -1);
    let numberOfDaysInCurrentMonth = getDaysInMonth(currentDate);
    let day = numberOfDaysInCurrentMonth - prevMonthOffset;
    let limit = day + 7;

    /* eslint-disable-next-line no-plusplus */
    for (let week = 0; week < 6; week++, limit += 7) {
      /* eslint-disable-next-line no-plusplus */
      for (; day < limit; day++) {
        if (day > numberOfDaysInCurrentMonth) {
          currentDate = addMonths(currentDate, 1);
          numberOfDaysInCurrentMonth = getDaysInMonth(currentDate);
          limit -= day - 1;
          day = 1;
        }
        matrix[matrix.length - 1][week].push(day);
      }
    }
  }
  return matrix;
};
var createCalendarMatrix$1 = createCalendarMatrix;

/**
 * @name differenceInCalendarYears
 * @category Year Helpers
 * @summary Get the number of calendar years between the given dates.
 *
 * @description
 * Get the number of calendar years between the given dates.
 *
 * @param {Date|Number} dateLeft - the later date
 * @param {Date|Number} dateRight - the earlier date
 * @returns {Number} the number of calendar years
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // How many calendar years are between 31 December 2013 and 11 February 2015?
 * const result = differenceInCalendarYears(
 *   new Date(2015, 1, 11),
 *   new Date(2013, 11, 31)
 * )
 * //=> 2
 */

function differenceInCalendarYears(dirtyDateLeft, dirtyDateRight) {
  requiredArgs(2, arguments);
  var dateLeft = toDate(dirtyDateLeft);
  var dateRight = toDate(dirtyDateRight);
  return dateLeft.getFullYear() - dateRight.getFullYear();
}

/**
 * @name compareAsc
 * @category Common Helpers
 * @summary Compare the two dates and return -1, 0 or 1.
 *
 * @description
 * Compare the two dates and return 1 if the first date is after the second,
 * -1 if the first date is before the second or 0 if dates are equal.
 *
 * @param {Date|Number} dateLeft - the first date to compare
 * @param {Date|Number} dateRight - the second date to compare
 * @returns {Number} the result of the comparison
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Compare 11 February 1987 and 10 July 1989:
 * const result = compareAsc(new Date(1987, 1, 11), new Date(1989, 6, 10))
 * //=> -1
 *
 * @example
 * // Sort the array of dates:
 * const result = [
 *   new Date(1995, 6, 2),
 *   new Date(1987, 1, 11),
 *   new Date(1989, 6, 10)
 * ].sort(compareAsc)
 * //=> [
 * //   Wed Feb 11 1987 00:00:00,
 * //   Mon Jul 10 1989 00:00:00,
 * //   Sun Jul 02 1995 00:00:00
 * // ]
 */

function compareAsc(dirtyDateLeft, dirtyDateRight) {
  requiredArgs(2, arguments);
  var dateLeft = toDate(dirtyDateLeft);
  var dateRight = toDate(dirtyDateRight);
  var diff = dateLeft.getTime() - dateRight.getTime();

  if (diff < 0) {
    return -1;
  } else if (diff > 0) {
    return 1; // Return 0 if diff is 0; return NaN if diff is NaN
  } else {
    return diff;
  }
}

/**
 * @name differenceInYears
 * @category Year Helpers
 * @summary Get the number of full years between the given dates.
 *
 * @description
 * Get the number of full years between the given dates.
 *
 * @param {Date|Number} dateLeft - the later date
 * @param {Date|Number} dateRight - the earlier date
 * @returns {Number} the number of full years
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // How many full years are between 31 December 2013 and 11 February 2015?
 * const result = differenceInYears(new Date(2015, 1, 11), new Date(2013, 11, 31))
 * //=> 1
 */

function differenceInYears(dirtyDateLeft, dirtyDateRight) {
  requiredArgs(2, arguments);
  var dateLeft = toDate(dirtyDateLeft);
  var dateRight = toDate(dirtyDateRight);
  var sign = compareAsc(dateLeft, dateRight);
  var difference = Math.abs(differenceInCalendarYears(dateLeft, dateRight)); // Set both dates to a valid leap year for accurate comparison when dealing
  // with leap days

  dateLeft.setFullYear(1584);
  dateRight.setFullYear(1584); // Math.abs(diff in full years - diff in calendar years) === 1 if last calendar year is not full
  // If so, result must be decreased by 1 in absolute value

  var isLastYearNotFull = compareAsc(dateLeft, dateRight) === -sign;
  var result = sign * (difference - Number(isLastYearNotFull)); // Prevent negative zero

  return result === 0 ? 0 : result;
}

/**
 * Given a Javascript date object, `date`, that represents the next selected date value in the
 * calendar, calculate other corresponding state values and validate the next state of the calendar.
 * This function returns an object containing all of the pending state updates that will then be
 * made elsewhere.
 *
 * **NOTE:** `startDate`, `endDate` and `selectedDate` all fall under `selected` in the returned
 * object. This is because the calendar itself does not handle choosing which piece of state to
 * update for these values. This logic is contained in various hooks used alongside the calendar
 * also exported from the package.
 *
 * @param {{
 *   month: number,
 *   props: import('../types').CalendarProps,
 *   year: string | number | null,
 * }} internalValues An object containing various internal values used to update state
 * @returns {(date: Date | null) => ({
 *   error: string | boolean;
 *   inputValue: string;
 *   month: number;
 *   selected: Date | null;
 *   year: number;
 * })} An object representing the state updates that need to be made in the calendar
 */
const getNextState = _ref => {
  let {
    month,
    props,
    year
  } = _ref;
  return date => {
    const jsDateObj = typeof date === 'string' && parse(date, props.dateFormat, new Date()) || date;
    const strDate = date instanceof Date && format(jsDateObj, props.dateFormat) || date || '';
    const isDateWithinRange$1 = isDateWithinRange(jsDateObj, getDateWithTime(props.minDate),
    // using 00:00 avoids edge cases with minDates
    getDateWithTime(props.maxDate, '23:59') // using 23:59 avoids edge cases with maxDates
    );

    const isDateDisabled$1 = jsDateObj ? isDateDisabled({
      date: jsDateObj,
      disabledDates: props.disabledDates,
      maxDate: props.maxDate,
      minDate: props.minDate
    }) : false;
    const isDateValid$1 = isDateValid(jsDateObj);
    const isInputDateOutsideOfView = isDateOutsideOfView(jsDateObj, month, year, props.numberOfMonths);

    // an object containing state that needs to be updated
    const pendingStateUpdates = {
      inputValue: strDate // we always want to update the input value
    };

    // if the date is cleared, clear the error
    if (!date) {
      pendingStateUpdates.selected = null;
      pendingStateUpdates.error = false;

      // the date is invalid
    } else if (!isDateValid$1) {
      pendingStateUpdates.selected = null;
      pendingStateUpdates.error = `The current input is not a valid date. Please use the format ${props.dateFormat}`;

      // the date is outside of the minDate / maxDate range
    } else if (!isDateWithinRange$1) {
      pendingStateUpdates.selected = null;
      pendingStateUpdates.error = 'The selected date is out of range.';

      // the date has been disabled
    } else if (isDateDisabled$1) {
      pendingStateUpdates.selected = null;
      pendingStateUpdates.error = 'The selected date has been disabled from selection.';

      // the date is valid
    } else {
      pendingStateUpdates.selected = jsDateObj;
      pendingStateUpdates.error = false;

      // the date is not currently visible on the calendar
      if (isInputDateOutsideOfView) {
        pendingStateUpdates.month = jsDateObj.getMonth();
        pendingStateUpdates.year = jsDateObj.getFullYear();
      }
    }
    return pendingStateUpdates;
  };
};

/**
 * Handles setting state for dates when a date is selected either via clicking on a day in the
 * calendar's body or typing in a date in the calendar's input. Not only is the date itself updated,
 * but values related to the selected date, (the calendar's input value, `error`, `month` and
 * `year`) may be updated as well.
 *
 * This function is curried to pass all of the prerequisite values (`month`, `oldProps`, `props`,
 * state setting functions and `year`) into context before exporting the result from the calendar's
 * context. The resulting function is what sets the state
 *
 * @param {{
 *   month: number,
 *   oldProps: import('../types').OldCalendarProps,
 *   props: import('../types').CalendarProps,
 *   refs: { hover: { current: { previousHover: Date | null }, } }
 *   setState: {
 *     error: (error: string | boolean) => void,
 *     inputValue: (text: string) => void,
 *     month: (month: number) => void,
 *     selected: (import('../types').OnSelectArgumentType) => void,
 *     year: (year: number) => void,
 *   },
 *   year: number,
 * }} internalValues Internal state, props and state setting functions used to handle date selection
 * @returns {(date: Date, validate?: boolean) => void} A function exported from the calendar's
 * context, that handles setting state around dates. This function takes in a date and a flag that
 * indicates whether or not the state should be validated.
 */
const handleDateSelection = _ref2 => {
  let {
    error,
    month,
    oldProps,
    props,
    refs,
    setState,
    year
  } = _ref2;
  return function (date, validate) {
    if (validate === void 0) {
      validate = false;
    }
    const nextState = getNextState({
      month,
      props,
      year
    })(date);

    // if we don't want to validate the new state, don't set any errors on state. We may not want to
    // set errors while the user is still typing in the input, but if their focus leaves the input
    // while it is still invalid, then we may want to set the error on state. If we clear an existing
    // error, then it is ok to update the error's state. We do however, want to continue validating if
    // there already is an error; this ensures that the error is up to date.
    if (!error && !validate && nextState.error) delete nextState.error;
    const isDateRangeOnOneCalendar = props.dateRangeStart && props.dateRangeEnd;
    const isOldDateSameAsNewDate = oldProps.currentDate?.getTime() === nextState.selected?.getTime();

    // if the previous date value is the same as the current, don't set it again unless a date range
    // is on the same calendar
    if (!isDateRangeOnOneCalendar && isOldDateSameAsNewDate) {
      delete nextState.selected;
    }

    // update all of the pieces of state that are pending
    ReactDOM.unstable_batchedUpdates(() => {
      Object.entries(nextState).forEach(_ref3 => {
        let [key, value] = _ref3;
        return setState[key](value);
      });
    });

    // use the last selected date for tracking hover css
    /* eslint-disable-next-line no-param-reassign */
    refs.hover.current = nextState.selected;
  };
};

/**
 * If `maxDate`'s year and month are the same as `year` and `month`, ensure that adjust `month` to
 * ensure that we minimize the number of disabled days/months in the calendar.
 *
 * @param {[number, 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11]} yearAndMonth The default year
 * and month
 * @param {import('../types').CalendarProps} props The props passed to the calendar on the
 * current render
 * @returns {[number, 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11]} [year, month]
 */
const handleNumberOfMonths = (_ref4, _ref5) => {
  let [year, month] = _ref4;
  let {
    maxDate,
    minDate,
    numberOfMonths
  } = _ref5;
  // if `numberOfMonths` is 1, we don't have to worry about accidentally displaying disabled months
  if (numberOfMonths === 1) return [year, month];
  const yearAndMonthFallOnMaxDate = year === maxDate.getFullYear() && month === maxDate.getMonth();
  const lastDayOfPreviousMonth = addDays(getAccurateDate(year, month, 1), -1);
  const isPreviousMonthOutOfRange = lastDayOfPreviousMonth < minDate && !isSameDay(lastDayOfPreviousMonth, minDate);

  // if `maxDate`'s year and month are the same as `year` and `month` AND numberOfMonths is greater
  // than 1, the second month in the calendar is disabled - shift month back one to ensure that we
  // have a minimum number of disabled dates in the calendar
  if (yearAndMonthFallOnMaxDate && !isPreviousMonthOutOfRange) {
    return getYearAndMonthAfterAddingMonths(year, month, -1);
  }
  return [year, month];
};

/**
 * Get a default year and default month based on a series of criteria.
 *
 * **NOTE:** be careful to consider how a `numberOfMonths` greater than 1 can display whole months
 * that are out of the `minDate`/`maxDate` range.
 *
 * **Year/Month Priority** - year and month have already been sanitized
 * 1. `year` AND `month` have been passed to the calendar as props (and are in range)
 * 2. `year` OR `month` have been passed as props (and are in range), figure out the compliment
 * 3. `endDate`'s, `startDate`'s or `selectedDate`'s year and month
 * 4. today's year and month
 * 5. `minDate` || `maxDate` - depending on which is closer to today
 *
 * @param {Date | null} currentDate A Javascript date object that represents the value in the
 * calendar's input
 * @param {import('../types').CalendarProps} props The props passed to the calendar on the
 * current render
 * @returns {[number, 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11]} [year, month]
 */
const getDefaultYearAndMonth = (currentDate, props) => {
  const {
    maxDate,
    minDate,
    month,
    year
  } = props;
  const today = new Date();
  const minDateIsClosestToToday = Math.abs(minDate.getTime() - today.getTime()) < Math.abs(maxDate.getTime() - today.getTime());
  const endOfRangeClosestToToday = minDateIsClosestToToday ? minDate : maxDate;

  // adjust `minDate` and `maxDate` to make comparisons a little easier
  const min = getAccurateDate(minDate.getFullYear(), minDate.getMonth(), 1);
  const max = addDays(addMonths(getAccurateDate(maxDate.getFullYear(), maxDate.getMonth(), 1, 23, 59), 1), -1);

  // we already have a month and year
  if (month !== null && year !== null) {
    const date = getAccurateDate(year, month, 1);

    // `year` and `month` are in the `minDate`/`maxDate` range
    if (date >= min && date <= max) {
      return handleNumberOfMonths([year, month], props);
    }
  }

  // we have year, but not month
  if (month === null && year !== null) {
    const isTodayInRange = today >= minDate && today <= maxDate;

    // `year` is in the `minDate`/`maxDate` range, use `year` - if today is in range, handle
    // selecting `year` and `month` below
    if (year >= minDate.getFullYear() && year <= maxDate.getYear() && !isTodayInRange) {
      return handleNumberOfMonths([year, endOfRangeClosestToToday.getMonth()], props);
    }
  }

  // we have month, but not year
  if (month !== null && year === null) {
    const monthIsOutOfMinDateAndMaxDateRange = differenceInYears(maxDate, minDate) && month < minDate.getMonth() && month > maxDate.getMonth();

    // `month` is in the `minDate`/`maxDate` range, use `month`
    if (!monthIsOutOfMinDateAndMaxDateRange) {
      const chosenYear = today.getFullYear() >= minDate.getFullYear && today.getFullYear() <= maxDate.getFullYear() ? today.getFullYear() : endOfRangeClosestToToday.getFullYear();
      return handleNumberOfMonths([chosenYear, month], props);
    }
  }

  // we have `endDate`, `startDate` or `selectedDate` - use that as the default month and year
  if (currentDate) {
    return handleNumberOfMonths([currentDate.getFullYear(), currentDate.getMonth()], props);
  }

  // if today is in the `minDate`/`maxDate` range - use today's month and year
  if (today >= minDate && today <= maxDate) {
    return handleNumberOfMonths([today.getFullYear(), today.getMonth()], props);
  }

  // `minDate` is closer to today
  if (minDateIsClosestToToday) {
    return handleNumberOfMonths([minDate.getFullYear(), minDate.getMonth()], props);
  }

  // `maxDate` is closer to today
  return handleNumberOfMonths([maxDate.getFullYear(), maxDate.getMonth()], props);
};

/**
 * Given a date and props passed to the calendar, determine the default state for the calendar's
 * context.
 *
 * @param {Date | null} currentDate A Javascript date object that represents the value in the
 * calendar's input
 * @param {import('../types').CalendarProps} props The props passed to the calendar on the
 * current render
 * @returns {{
 *   error: string | boolean,
 *   inputValue: string,
 *   month: number,
 *   selected?: Date | null,
 *   year?: number,
 * }} An object containing default values for the calendar's internal state
 */
const getDefaultState = (currentDate, props) => {
  const [defaultYear, defaultMonth] = getDefaultYearAndMonth(currentDate, props);
  const defaultDate = pickDefaultDate({
    currentDate,
    disabledDates: props.disabledDates,
    maxDate: props.maxDate,
    minDate: props.minDate,
    month: defaultMonth,
    numberOfMonths: props.numberOfMonths,
    year: defaultYear
  });
  const defaultState = getNextState({
    month: props.month,
    props,
    year: props.year
  })(defaultDate);
  return {
    ...defaultState,
    selected: defaultState.selected || currentDate,
    month: defaultMonth,
    year: defaultYear
  };
};

/**
 * Given the props from the previous render of the calendar and the props from the current render of
 * the calendar, determine which props, if any, have changed. Return an object containing
 * information about which props have changed.
 *
 * These flags are used in the derived state from props for the calendar's context to determine
 * which pieces of state should be updated.
 *
 * @param {import('../types').OldCalendarProps} oldProps An object containing the props passed to
 * the calendar on the previous render
 * @param {{
 *   currentDate: Date | null,
 *   dateFormat: string,
 *   month: number,
 *   year: number,
 * }} props The props passed to the calendar on the current render
 * @param {string} inputValue A string that represents the text value of the calendar's input
 * @returns {{
 *   dateFormat: boolean,
 *   inputHasBeenRemotelyCleared: boolean,
 *   inputValueDeviatedFromCurrentDate: boolean,
 *   month: boolean,
 *   props: boolean,
 *   year: boolean,
 * }} An object containing all of the flags needed to handle derived state from props for the
 * calendar's context
 */
const compareProps$1 = (oldProps, props, currentDate, inputValue) => {
  const inputValueDeviatedFromCurrentDate = inputValue === null ? false // input ref is null - input ref can't be out of sync until input is rendered
  : !!currentDate && !isDateStringEqualToDate(currentDate, inputValue, props.dateFormat);

  // input may be remotely cleared - make sure that it hasn't been cleared by typing an invalid date
  // into the input
  const inputHasBeenRemotelyCleared = !!oldProps.currentDate && !currentDate && oldProps.inputValue === inputValue;
  const dateFormatHasChanged = oldProps.dateFormat !== props.dateFormat;
  const monthHasChanged = oldProps.month !== props.month;
  const yearHasChanged = oldProps.year !== props.year;
  const propsHaveChanged = dateFormatHasChanged || inputValueDeviatedFromCurrentDate || inputHasBeenRemotelyCleared || monthHasChanged || yearHasChanged;
  return {
    dateFormat: dateFormatHasChanged,
    inputHasBeenRemotelyCleared,
    inputValueDeviatedFromCurrentDate,
    month: monthHasChanged,
    props: propsHaveChanged,
    // determines if "derived state from props" should run
    year: yearHasChanged
  };
};

/**
 * Ensure that internal state is up to date by checking for changes in props and making changes
 * accordingly.
 *
 * @param {{
 *   currentDate: Date | null,
 *   error: boolean | string,
 *   month: number,
 *   oldProps: import('../types').OldCalendarProps,
 *   props: import('../types').CalendarProps,
 *   refs: {
 *     hoverDayRange: { current: Date[] },
 *     input: { current: HTMLInputElement | null },
 *   },
 *   setError: (error: boolean | string) => void,
 *   setMonth: (month: number) => void,
 *   setYear: (year: number) => void,
 *   year: number,
 * }} arguments An object containing a slew of different values needed to handle derived state from
 * props
 * @returns {void}
 */
const handleDerivedStateFromProps = _ref6 => {
  let {
    currentDate,
    error,
    month,
    oldProps,
    props,
    refs,
    setError,
    setMonth,
    setYear,
    year
  } = _ref6;
  // an object that contains comparisons between various categories of old and new props for
  // determining whether or not to update various pieces of state
  // * don't use useMemo - caching incorrect
  const propChanged = compareProps$1(oldProps, props, currentDate, refs.input.current?.value ?? null);

  // derived state from props - even though this may be triggered frequently this should only
  // trigger a rerender if we actually need it to; if a piece of state needs to be updated
  if (propChanged.props) {
    ReactDOM.unstable_batchedUpdates(() => {
      let monthAndYearUpdated = false;

      // if the currentDate has been updated and is now out of alignment with the calendar's input,
      // update the input's value to reflect this change. This should only happen if overlapping
      // ranges are swapped, or if a `currentDate` chosen by an entity other than the calendar. When
      // the calendar sets the state of `currentDate`, it always internally handles updating
      // the calendar input's value. This may also be hit if `dateFormat` changes.
      if (propChanged.inputValueDeviatedFromCurrentDate) {
        const derivedStateFromProps = getDefaultState(currentDate, props);
        if (refs.input.current) {
          /* eslint-disable-next-line no-param-reassign */
          refs.input.current.value = format(currentDate, props.dateFormat);

          // set the error's state
          if (derivedStateFromProps.error !== error) setError(derivedStateFromProps.error);
        }

        // use `month` and `year` instead of the values we receive from props. If we get a month and
        // year prop, we will set the derived state from props later on
        const isInputDateOutsideOfView = isDateOutsideOfView(currentDate, month, year, props.numberOfMonths);

        // if the new currentDate is out of view of the calendar, bring it into view. If both the
        // month and year props have changed, we can update those separately,
        if (isInputDateOutsideOfView && (!propChanged.month || !propChanged.year)) {
          monthAndYearUpdated = true;
          setMonth(derivedStateFromProps.month || month);
          setYear(derivedStateFromProps.year || year);
        }
      }

      // the current date has been set to `null` outside of the calendar, clear the input.
      // inputHasBeenRemotelyCleared is mutually exclusive with inputValueDeviatedFromCurrentDate,
      // so we don't risk clearing the input right after setting it
      if (propChanged.inputHasBeenRemotelyCleared) {
        if (refs.input.current) {
          /* eslint-disable-next-line no-param-reassign */
          refs.input.current.value = '';

          // if input is cleared, we have no errors
          if (error) setError(false);
        }
      }

      // only update month and year if they haven't been updated above
      if (!monthAndYearUpdated) {
        const [sanitizedYear, sanitizedMonth] = getDefaultYearAndMonth(currentDate, props);
        if (propChanged.year) setYear(sanitizedYear);
        if (propChanged.month) setMonth(sanitizedMonth);
      }
    });
  }
};

/* eslint-disable react/prop-types */ // we handle prop-types in `Calendar/Calendar/index.js`
const CalendarContext = /*#__PURE__*/React.createContext();
const CalendarProvider = _ref => {
  let {
    children,
    __inputWrapperRef,
    __timeInputRef,
    onError,
    onSelect,
    ...props
  } = _ref;
  const refs = useRef({
    calendar: {
      current: null
    },
    days: {},
    // accessibility/css: a reference to each day in the calendar
    hover: {
      current: null
    },
    // for handling mouse hover css in calendar
    input: props.calendarInputProps.componentRef || {
      current: null
    },
    // controls text in input
    inputWrapper: __inputWrapperRef,
    menu: [],
    // accessibility: for shifting focus to month navigation
    timeInput: __timeInputRef
  });

  // represents the date that should be displayed in the calendar's input
  const currentDate = props.dateRangeEnd && props.endDate || props.dateRangeStart && props.startDate || !props.dateRangeStart && !props.dateRangeEnd && props.selectedDate || null;

  // for tracking changes in props to apply "derived state from props" updates
  const oldPropsRef = useRef({
    currentDate,
    dateFormat: props.dateFormat,
    inputValue: refs.current.input.current?.value ?? null,
    month: props.month,
    year: props.year
  });

  // given various props, determine the initial state of the calendar
  // leave useMemo's dependency array empty - we only want this to run once
  const defaultState = useMemo(() => getDefaultState(currentDate, props), []); /* eslint-disable-line react-hooks/exhaustive-deps, max-len */

  // error only handles error internal to the calendar
  const [error, handleError] = useState(defaultState.error);
  const [isCalendarOpen, setIsCalendarOpen] = useState(props.showCalendarOnly || false);
  const [month, setMonth] = useState(defaultState.month);
  const [year, setYear] = useState(defaultState.year);

  // when a react portal is used for the calendar drop down, get css positioning for the calendar
  const [portalPosition, setPortalPosition] = usePortalPosition$1(props.renderNode, refs.current, props.align);

  /**
   * handles errors locally in the calendar provider and in the calendar hook
   */
  const setError = useCallback(e => {
    handleError(e);
    if (onError) onError(e);
  }, [onError]);

  // make sure that `error` in hooks is up to date if we have an error on the **first** render only
  useHandleErrorOnFirstRender$1(error, onError);

  // keep internal state up to date based on prop changes
  handleDerivedStateFromProps({
    currentDate,
    error,
    month,
    oldProps: oldPropsRef.current,
    props,
    refs: refs.current,
    setError,
    setMonth,
    setYear,
    year
  });

  // update old props after evaluating "derived state from props"
  oldPropsRef.current = {
    currentDate,
    dateFormat: props.dateFormat,
    inputValue: refs.current.input.current?.value ?? null,
    month: props.month,
    year: props.year
  };

  /**
   * handles date selection either in the calendar's input or the calendar's body
   */
  const handleDateSelectionCurried = useCallback(function () {
    return handleDateSelection({
      error,
      month,
      oldProps: oldPropsRef.current,
      props,
      refs: refs.current,
      setState: {
        error: setError,
        // mock a state setting function for calendar's input value
        inputValue: text => {
          if (refs.current.input.current) {
            refs.current.input.current.value = text;
          }
        },
        month: setMonth,
        selected: onSelect,
        year: setYear
      },
      year
    })(...arguments);
  }, [error, month, props, setError, setMonth, onSelect, year]);
  const weekFormat = useMemo(() => WEEKDAYS.STARTCASE.slice(props.weekStartsOn).concat(WEEKDAYS.STARTCASE.slice(0, props.weekStartsOn)), [props.weekStartsOn]);
  const calendarMatrix = useMemo(() => createCalendarMatrix$1(month, year, props.numberOfMonths, props.weekStartsOn), [month, props.numberOfMonths, props.weekStartsOn, year]);

  // value passed for useContext
  const value = useMemo(() => ({
    events: {
      monthLoaded: new Event('monthLoaded')
    },
    props,
    refs: refs.current,
    state: {
      calendarMatrix,
      currentDate,
      error,
      isCalendarOpen,
      month,
      portalPosition,
      weekFormat,
      year
    },
    setState: {
      dateSelection: handleDateSelectionCurried,
      isCalendarOpen: setIsCalendarOpen,
      month: setMonth,
      portalPosition: setPortalPosition,
      year: setYear
    }
  }), [calendarMatrix, currentDate, error, handleDateSelectionCurried, isCalendarOpen, month, portalPosition, props, setMonth, setIsCalendarOpen, setPortalPosition, setYear, weekFormat, year]);
  return /*#__PURE__*/React.createElement(CalendarContext.Provider, {
    value: value
  }, children);
};
var CalendarProvider$1 = CalendarProvider;

/* eslint-disable react/prop-types */ // incorrectly complaining about prop validation

const CalendarErrorText = () => {
  const {
    props,
    state
  } = useContext(CalendarContext);

  // errors passed down from props get priority over internally managed errors. If there are no
  // errors passed via props, fallback onto internally handled errors
  const error = typeof props.error === 'string' ? props.error : state.error;
  if (error && typeof error === 'string') {
    return /*#__PURE__*/React.createElement(FormHelperText, {
      error: true
    }, error);
  }
  return null;
};
var CalendarErrorText$1 = CalendarErrorText;

const CalendarInput = () => {
  const {
    props: {
      calendarInputProps,
      dateFormat,
      disabled,
      disabledDates,
      error: propsError,
      maxDate,
      minDate,
      month,
      numberOfMonths,
      placeholder,
      renderNode,
      year
    },
    refs,
    setState,
    state: {
      currentDate,
      error: stateError
    }
  } = useContext(CalendarContext);

  // if the calendar is initially rendered with an already selected date, populate the input with it
  const defaultValue = currentDate ? format(currentDate, dateFormat) : '';
  return /*#__PURE__*/React.createElement(Input, _extends({}, calendarInputProps, {
    componentRef: refs.input,
    defaultValue: defaultValue,
    disabled: disabled,
    error: !!propsError || !!stateError,
    icon: IconCalendar,
    onBlur: e => {
      const didFocusLeaveCalendar = !refs.calendar.current.contains(e.relatedTarget);

      // don't handle blur for the calendar if user clicks inside of the calendar body
      if (!didFocusLeaveCalendar) return;

      // if this code is running, focus has left the calendar (both input and body)
      // close the calendar
      setState.isCalendarOpen(false);

      // only validate if we have input
      setState.dateSelection(refs.input.current?.value || '', !!e.target.value);

      // the definition of this event handler shadows any on `calendarInputProps`, make sure to
      // call `calendarInputProps.onBlur` if it exists
      if (didFocusLeaveCalendar && calendarInputProps.onBlur) {
        // be sure to keep this at the end of the onBlur event handler to ensure that we do
        // everything we need to do before invoking `props.onBlur`
        calendarInputProps.onBlur(e);
      }
    },
    onChange: e => {
      setState.dateSelection(e.target.value);

      // the definition of this event handler shadows any on `calendarInputProps`, make sure to
      // call `calendarInputProps.onChange` if it exists
      if (calendarInputProps.onChange) calendarInputProps.onChange(e);
    },
    onClick: e => {
      // If we are using a react portal for the calendar drop down, it is possible that we didn't
      // get css positioning data initially if the calendar input wasn't rendered. Ensure that we
      // get that data by getting the data again on click
      if (renderNode) setState.portalPosition();

      // the definition of this event handler shadows any on `calendarInputProps`, make sure to
      // call `calendarInputProps.onClick` if it exists
      if (calendarInputProps.onClick) calendarInputProps.onClick(e);
    },
    onFocus: e => {
      setState.isCalendarOpen(true);

      // the definition of this event handler shadows any on `calendarInputProps`, make sure to
      // call `calendarInputProps.onFocus` if it exists
      if (calendarInputProps.onFocus) calendarInputProps.onFocus(e);
    },
    onKeyDown: e => {
      handleKeyDown(e, refs, {
        shiftTab: () => {},
        // let tab function as normal
        tab: pivotFocus.fromCalendarInputToMenuOrCalendarDate({
          currentDate,
          disabledDates,
          maxDate,
          minDate,
          month,
          numberOfMonths,
          setState,
          year
        })
      });

      // the definition of this event handler shadows any on `calendarInputProps`, make sure to
      // call `calendarInputProps.onKeyDown` if it exists
      if (calendarInputProps.onKeyDown) calendarInputProps.onKeyDown(e);
    },
    placeholder: placeholder
  }));
};
var CalendarInput$1 = CalendarInput;

const CalendarHeader = _ref => {
  let {
    matrixMonthNumber
  } = _ref;
  const {
    props: {
      disabled
    },
    state: {
      month,
      year
    }
  } = useContext(CalendarContext);
  const [headerYear, headerMonth] = getYearAndMonthAfterAddingMonths(year, month, matrixMonthNumber);
  return /*#__PURE__*/React.createElement(Typography, {
    "aria-live": "polite",
    className: `st_calendar__month_heading${disabled ? ' st_calendar__month_heading--disabled' : ''}`,
    role: "heading",
    size: "lg"
  }, MONTHS.STARTCASE[headerMonth], ' ', headerYear);
};
CalendarHeader.propTypes = {
  /**
   * A number representing which month of the matrix we are currently displaying
   */
  matrixMonthNumber: PropTypes.number.isRequired
};
var CalendarHeader$1 = CalendarHeader;

/**
 * @name isAfter
 * @category Common Helpers
 * @summary Is the first date after the second one?
 *
 * @description
 * Is the first date after the second one?
 *
 * @param {Date|Number} date - the date that should be after the other one to return true
 * @param {Date|Number} dateToCompare - the date to compare with
 * @returns {Boolean} the first date is after the second date
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Is 10 July 1989 after 11 February 1987?
 * const result = isAfter(new Date(1989, 6, 10), new Date(1987, 1, 11))
 * //=> true
 */

function isAfter(dirtyDate, dirtyDateToCompare) {
  requiredArgs(2, arguments);
  var date = toDate(dirtyDate);
  var dateToCompare = toDate(dirtyDateToCompare);
  return date.getTime() > dateToCompare.getTime();
}

/**
 * @name isBefore
 * @category Common Helpers
 * @summary Is the first date before the second one?
 *
 * @description
 * Is the first date before the second one?
 *
 * @param {Date|Number} date - the date that should be before the other one to return true
 * @param {Date|Number} dateToCompare - the date to compare with
 * @returns {Boolean} the first date is before the second date
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Is 10 July 1989 before 11 February 1987?
 * const result = isBefore(new Date(1989, 6, 10), new Date(1987, 1, 11))
 * //=> false
 */

function isBefore(dirtyDate, dirtyDateToCompare) {
  requiredArgs(2, arguments);
  var date = toDate(dirtyDate);
  var dateToCompare = toDate(dirtyDateToCompare);
  return date.getTime() < dateToCompare.getTime();
}

// css classes used by `Day`
const DAY_CLASS_PREFIX = 'st_calendar__day';
const DAY_CLASSES = Object.freeze({
  DAY: DAY_CLASS_PREFIX,
  END_OF_RANGE: `${DAY_CLASS_PREFIX}--end`,
  IN_RANGE: `${DAY_CLASS_PREFIX}--range`,
  OUTSIDE: `${DAY_CLASS_PREFIX}--outside`,
  SELECTED: `${DAY_CLASS_PREFIX}--selected`,
  START_OF_RANGE: `${DAY_CLASS_PREFIX}--start`,
  TODAY: `${DAY_CLASS_PREFIX}--today`
});

/**
 * Given a number of different arguments determine which css classes should apply to the given date.
 *
 * This function will be triggered once per `Day` on render and whenever a new hover range is being
 * selected (onMouseOver or onFocus). Since a date selection triggers a rerender, the css of `Day`
 * shouldn't need to be manually handled in any other cases (except when the user's focus/mouse
 * leaves the calendar).
 *
 * @param {{
 *   date: Date,
 *   endDate: Date | null,
 *   isDisabled: boolean,
 *   selectedDate: Date | null,
 *   startDate: Date | null,
 * }} arguments A variety of different arguments to determine which css classes should be applied to
 * `date`
 * @returns {string}
 */
const getDayCSS = _ref => {
  let {
    date,
    endDate,
    isDisabled,
    selectedDate,
    startDate
  } = _ref;
  const classNames = [DAY_CLASSES.DAY];

  // today
  if (isSameDay(date, new Date())) {
    classNames.push(DAY_CLASSES.TODAY);
  }
  switch (true) {
    // outside / disabled
    case isDisabled:
      classNames.push(DAY_CLASSES.OUTSIDE);
      break;

    // selectedDate
    case isSameDay(date, selectedDate):
    case isSameDay(startDate, endDate) && isSameDay(startDate, date):
    case startDate && !endDate && isSameDay(startDate, date):
    case !startDate && endDate && isSameDay(endDate, date):
      classNames.push(DAY_CLASSES.SELECTED);
      break;

    // startDate
    case isSameDay(date, startDate):
      classNames.push(DAY_CLASSES.START_OF_RANGE);
      break;

    // endDate
    case isSameDay(date, endDate):
      classNames.push(DAY_CLASSES.END_OF_RANGE);
      break;

    // between startDate and endDate
    case isBefore(startDate, date) && isAfter(endDate, date):
      classNames.push(DAY_CLASSES.IN_RANGE);
      break;
  }
  return classNames.join(' ');
};

/**
 * Handles adding to and removing from a hover range. This function should be triggered when a user
 * mouses into a `Day` or focuses on a `Day`. This function may also be triggered when the user
 * mouses out of the calendar or the user's focus leaves the calendar.
 *
 * **NOTE:** This function will not be triggered when a user hovers over a disabled day. Also, since
 * we don't track refs for disabled days, so they are skipped over for handling the hover range as
 * well.
 *
 * @param {{
 *   date: Date,
 *   dateRangeEnd: boolean,
 *   dateRangeStart: boolean,
 *   disabled: boolean,
 *   disabledDates: { dates: Date[], weekDays: number[] },
 *   endDate: Date | null,
 *   maxDate: Date,
 *   minDate: Date,
 *   month: number,
 *   numberOfMonths: 1 | 2,
 *   refs: {
 *     days: Record<{ [string]: HTMLDivElement | null }>,
 *     hover: { current: HTMLDivElement | null },
 *   },
 *   selectedDate: Date | null,
 *   year: number,
 * }} arguments A variety of different arguments to handle applying css classes for hover ranges to
 * the calendar
 * @returns {void}
 */
const handleHoverRange = _ref2 => {
  let {
    date,
    dateRangeEnd,
    dateRangeStart,
    disabled,
    disabledDates,
    endDate,
    maxDate,
    minDate,
    month,
    numberOfMonths,
    refs,
    selectedDate,
    startDate,
    year
  } = _ref2;
  // the calendar is being used for a date range and we have one end of the range selected
  const onlyOneEndOfRangeIsSelected = dateRangeEnd && startDate && !endDate || dateRangeStart && !startDate && endDate;

  // if one end of the date range is selected and the other is not, handle hover
  if (onlyOneEndOfRangeIsSelected) {
    const endOfTheRangeThatIsSelected = startDate || endDate;
    const earliestDateInPreviousAndCurrentHoverRange = new Date(Math.min(date?.getTime() || Infinity, endOfTheRangeThatIsSelected.getTime(), refs.hover.current?.getTime() || Infinity));
    const latestDateInPreviousAndCurrentHoverRange = new Date(Math.max(date?.getTime() || -Infinity, endOfTheRangeThatIsSelected.getTime(), refs.hover.current?.getTime() || -Infinity));

    // month and year represent the earliest month / year in calendar
    const earliestCalendarDate = getAccurateDate(year, month, 1);
    const latestCalendarDate = addDays(addMonths(earliestCalendarDate, numberOfMonths), -1);

    // make sure to clear any time values in earliestDateInView and latestDateInView
    const earliestDateInView = getDateWithTime(earliestDateInPreviousAndCurrentHoverRange < earliestCalendarDate ? earliestCalendarDate : earliestDateInPreviousAndCurrentHoverRange);
    const latestDateInView = getDateWithTime(latestDateInPreviousAndCurrentHoverRange > latestCalendarDate ? latestCalendarDate : latestDateInPreviousAndCurrentHoverRange);

    /* eslint-disable-next-line no-plusplus */
    for (let d = earliestDateInView; d <= latestDateInView; d = addDays(d, 1)) {
      const isDisabled = disabled || isDateDisabled({
        date: d,
        disabledDates,
        maxDate,
        minDate
      });
      const endOfCurrentHoverRange = endDate || date;
      const startOfCurrentHoverRange = startDate || date;

      /* eslint-disable-next-line no-param-reassign */
      refs.days[d].current.className = getDayCSS({
        date: d,
        endDate: endOfCurrentHoverRange > startOfCurrentHoverRange ? endOfCurrentHoverRange : startOfCurrentHoverRange,
        isDisabled,
        selectedDate,
        startDate: startOfCurrentHoverRange < endOfCurrentHoverRange ? startOfCurrentHoverRange : endOfCurrentHoverRange
      });
    }
  }
};

const Day = _ref => {
  let {
    day,
    dayAsDate,
    isDisabled,
    isFirstNonDisabledDate
  } = _ref;
  const {
    events,
    props: {
      canChangeMonth,
      dateFormat,
      dateRangeEnd,
      dateRangeStart,
      disabled,
      disabledDates,
      endDate,
      maxDate,
      minDate,
      numberOfMonths,
      selectedDate,
      showCalendarOnly,
      startDate
    },
    refs,
    setState,
    state: {
      currentDate,
      month,
      year
    }
  } = useContext(CalendarContext);
  const dayRef = useRef(null);

  // put dayRef on `refs` if not already there
  if (!isDisabled) {
    refs.days[dayAsDate] = dayRef;
  }

  // when the component renders, trigger a custom event to let us know that we can shift the user's
  // focus to the next month when using accessible navigation
  useEffect(() => {
    // we only need to trigger this event once per month change
    if (isFirstNonDisabledDate) {
      dayRef.current.dispatchEvent(events.monthLoaded);
    }
  }, []); /* eslint-disable-line react-hooks/exhaustive-deps */

  // used for onClick and onKeyPress
  const handleDaySelect = e => {
    // disable behavior for outside dates or dates outside of min and max range
    if (isDisabled) return;
    setState.dateSelection(dayAsDate);

    // close the calendar when the user clicks the time
    setState.isCalendarOpen(false);

    // * prevents any possible label that wraps this component from blocking hiding the calendar
    // * by sending focus to the CalendarInput
    e.preventDefault();
  };

  // used for onFocus and onMouseOver
  const handleHover = () => {
    // we don't need to handle hover behavior for disabled dates
    if (isDisabled) return;

    // if there is a hover range - update the hover ranged based on the latest hover
    handleHoverRange({
      date: dayAsDate,
      dateRangeEnd,
      dateRangeStart,
      disabled,
      disabledDates,
      endDate,
      isDisabled,
      maxDate,
      minDate,
      month,
      numberOfMonths,
      refs,
      selectedDate,
      startDate,
      year
    });

    // mark this calendar date as being the last hovered date - we need to do this to manage
    // clearing hover ranges and adding new hover ranges
    refs.hover.current = dayAsDate;
  };

  // allow the user to tab into the calendar's body only if there is no input and no month
  // navigation buttons
  const tabIndex = isFirstNonDisabledDate && showCalendarOnly && !refs.menu.length ? '0' : '-1';
  return /*#__PURE__*/React.createElement("div", {
    "aria-label": format(dayAsDate, dateFormat),
    "aria-disabled": String(isDisabled),
    "aria-selected": String(isSameDay(dayAsDate, currentDate)),
    className: getDayCSS({
      date: dayAsDate,
      endDate,
      isDisabled,
      selectedDate,
      startDate
    }),
    onClick: handleDaySelect,
    onFocus: handleHover,
    onMouseOver: handleHover,
    onKeyDown: e => {
      const currentDateWithoutTime = getDateWithTime(dayAsDate);
      handleKeyDown(e, refs, {
        arrowDown: navigate.dates({
          canChangeMonth,
          currentDate: currentDateWithoutTime,
          disabledDates,
          maxDate,
          minDate,
          month,
          nextDateDiff: 7,
          // move forward one week
          numberOfMonths,
          setState,
          year
        }),
        arrowLeft: navigate.dates({
          canChangeMonth,
          currentDate: currentDateWithoutTime,
          disabledDates,
          maxDate,
          minDate,
          month,
          nextDateDiff: -1,
          // move backward one day
          numberOfMonths,
          setState,
          year
        }),
        arrowRight: navigate.dates({
          canChangeMonth,
          currentDate: currentDateWithoutTime,
          disabledDates,
          maxDate,
          minDate,
          month,
          nextDateDiff: 1,
          // move forward on day
          numberOfMonths,
          setState,
          year
        }),
        arrowUp: navigate.dates({
          canChangeMonth,
          currentDate: currentDateWithoutTime,
          disabledDates,
          maxDate,
          minDate,
          month,
          nextDateDiff: -7,
          // move backward one week
          numberOfMonths,
          setState,
          year
        }),
        enter: event => {
          handleDaySelect(event);
          pivotFocus.fromCalendarDateToTimeInput()(event, refs);
        },
        shiftTab: pivotFocus.fromCalendarDateToMenuOrInput(),
        tab: pivotFocus.fromCalendarDateToTimeInput()
      });
    },
    ref: !isDisabled ? dayRef : null,
    role: "gridcell",
    tabIndex: tabIndex,
    type: "button"
  }, day);
};
Day.propTypes = {
  /** The day of the month represented by a number */
  day: PropTypes.number.isRequired,
  /** The date representing this day. */
  dayAsDate: PropTypes.instanceOf(Date).isRequired,
  /** A flag that indicates whether or not `Day` is disabled */
  isDisabled: PropTypes.bool.isRequired,
  /** A flag that indicates whether or not `Day` is the first non-disabled date in the calendar. */
  isFirstNonDisabledDate: PropTypes.bool.isRequired
};
var Day$1 = Day;

const MonthNavigationButtons = () => {
  const {
    props: {
      disabled,
      disabledDates,
      maxDate,
      minDate,
      numberOfMonths,
      step
    },
    refs,
    setState,
    state: {
      currentDate,
      month,
      year
    }
  } = useContext(CalendarContext);

  // we need to shift focus here from the overlay for accessability
  const menuRefPrevious = useRef(null);
  const menuRefNext = useRef(null);
  refs.menu = [menuRefPrevious, menuRefNext];

  // flags that indicate whether or not to render the previous month button and next month button
  const canNavigateBackwards = getCanNavigateBackwards({
    minDate,
    month,
    step,
    year
  });
  const canNavigateForwards = getCanNavigateForwards({
    maxDate,
    month,
    numberOfMonths,
    year
  });
  return /*#__PURE__*/React.createElement(React.Fragment, null, canNavigateBackwards && /*#__PURE__*/React.createElement(Button, {
    "aria-label": "Previous Month",
    className: "st_calendar__navbar_button",
    componentRef: menuRefPrevious,
    disabled: disabled,
    icon: IconCaretLeft,
    margin: {
      right: 'lg',
      top: 'sm'
    },
    onClick: () => {
      const [, prevMonth] = getYearAndMonthAfterAddingMonths(year, month, -1);
      setState.month(prevMonth);
      refs.days = {};
      if (month - 1 < 0) setState.year(year - 1);
    },
    onKeyDown: e => {
      handleKeyDown(e, refs, {
        enter: pivotFocus.monthNavigation({
          currentDate,
          disabledDates,
          maxDate,
          menuIndex: 0,
          minDate,
          month,
          numberOfMonths,
          setState,
          step,
          year
        }),
        shiftTab: pivotFocus.fromMenuToCalendarInput(),
        tab: canNavigateForwards ? () => {} // only jump into the calendar if we don't have another menu button
        : pivotFocus.fromMenuToCalendarDate({
          currentDate,
          disabledDates,
          maxDate,
          minDate,
          month,
          numberOfMonths,
          setState,
          year
        })
      });
    },
    tertiary: true
  }), canNavigateForwards && /*#__PURE__*/React.createElement(Button, {
    "aria-label": "Next Month",
    className: "st_calendar__navbar_button",
    componentRef: menuRefNext,
    disabled: disabled,
    icon: IconCaretRight,
    margin: {
      right: 'sm',
      top: 'sm'
    },
    onClick: () => {
      const [, nextMonth] = getYearAndMonthAfterAddingMonths(year, month, 1);
      refs.days = {};
      setState.month(nextMonth);
      if (month + 1 > 11) setState.year(year + 1);
    },
    onKeyDown: e => {
      handleKeyDown(e, refs, {
        enter: pivotFocus.monthNavigation({
          currentDate,
          disabledDates,
          maxDate,
          menuIndex: 1,
          minDate,
          month,
          numberOfMonths,
          setState,
          step,
          year
        }),
        shiftTab: canNavigateBackwards ? () => {} // only jump to input if we have no back button
        : pivotFocus.fromMenuToCalendarInput(),
        tab: pivotFocus.fromMenuToCalendarDate({
          currentDate,
          disabledDates,
          maxDate,
          minDate,
          month,
          numberOfMonths,
          setState,
          year
        })
      });
    },
    tertiary: true
  }));
};
var MonthNavigationButtons$1 = MonthNavigationButtons;

const CalendarBody = () => {
  const {
    props: {
      canChangeMonth,
      dateRangeEnd,
      dateRangeStart,
      disabled,
      disabledDates,
      endDate,
      maxDate,
      minDate,
      numberOfMonths,
      selectedDate,
      startDate
    },
    state: {
      calendarMatrix,
      month,
      weekFormat,
      year
    },
    refs
  } = useContext(CalendarContext);

  // if there is a hover range clear the current hover range after user's mouse leaves the calendar
  const handleClearingHoverRange = useCallback(() => {
    const selectedEndOfRange = startDate || endDate;
    handleHoverRange({
      date: selectedEndOfRange,
      dateRangeEnd,
      dateRangeStart,
      endDate,
      disabled,
      disabledDates,
      maxDate,
      minDate,
      month,
      numberOfMonths,
      refs,
      selectedDate,
      startDate,
      year
    });

    // when a new startDate or endDate is selected, the hover range is set to that
    // date. Since we've cleared the range, reset the state of the hover ref to what
    // it was after selecting startDate or endDate
    refs.hover.current = selectedEndOfRange;
  }, [dateRangeEnd, dateRangeStart, endDate, disabled, disabledDates, maxDate, minDate, month, numberOfMonths, refs, selectedDate, startDate, year]);

  // a flag that indicates whether or not the first non-disabled date has already been iterated over
  let hasFirstNonDisabledDateBeenPassed = false;
  return /*#__PURE__*/React.createElement("div", {
    className: "st_calendar__calendar_body"
  }, canChangeMonth && /*#__PURE__*/React.createElement(MonthNavigationButtons$1, null), /*#__PURE__*/React.createElement("div", {
    className: "st_calendar__month_wrapper"
  }, calendarMatrix.map((m, i) => {
    let isOutside = true;
    let [currentYear, currentMonth] = getYearAndMonthAfterAddingMonths(year, month, i - 1 // subtract 1 to handle the outside days before initial `month`
    );

    return /*#__PURE__*/React.createElement("div", {
      key: currentMonth,
      className: "st_calendar__month",
      role: "grid"
    }, /*#__PURE__*/React.createElement(CalendarHeader$1, {
      matrixMonthNumber: i
    }), /*#__PURE__*/React.createElement("div", {
      className: "st_calendar__weekday_heading",
      role: "rowgroup"
    }, /*#__PURE__*/React.createElement("div", {
      className: "st_calendar__week_row",
      role: "row"
    }, weekFormat.map(dayOfWeek => /*#__PURE__*/React.createElement("div", {
      className: `st_calendar__weekday${disabled ? ' st_calendar__weekday--disabled' : ''}`,
      key: dayOfWeek,
      role: "columnheader"
    }, /*#__PURE__*/React.createElement("abbr", {
      className: "st_calendar__weekday_abbr",
      title: dayOfWeek
    }, dayOfWeek.slice(0, 2)))))), /*#__PURE__*/React.createElement("div", {
      className: "st_calendar__body",
      role: "rowgroup",
      onBlur: handleClearingHoverRange,
      onMouseLeave: handleClearingHoverRange
    }, m.map((week, j) =>
    /*#__PURE__*/
    /* eslint-disable-next-line react/no-array-index-key */
    React.createElement("div", {
      key: `${currentYear}-${currentMonth}-${j}`,
      className: "st_calendar__week_row",
      role: "row"
    }, week.map(day => {
      // by default, we start currentYear/currentMonth the month before `month`
      // even if there are no outside dates for that month prior to the first.
      if (day === 1) {
        isOutside = !isOutside;
        [currentYear, currentMonth] = getYearAndMonthAfterAddingMonths(currentYear, currentMonth, 1);
      }
      const dayAsDate = getAccurateDate(currentYear, currentMonth, day);
      const isDisabled = isOutside || disabled || isDateDisabled({
        date: dayAsDate,
        disabledDates,
        maxDate,
        minDate
      });
      const isFirstNonDisabledDate = !isDisabled && !hasFirstNonDisabledDateBeenPassed;

      // ensure we don't trigger the flag again
      if (!isDisabled) hasFirstNonDisabledDateBeenPassed = true;
      return /*#__PURE__*/React.createElement(Day$1, {
        day: day,
        dayAsDate: dayAsDate,
        isDisabled: isDisabled,
        isFirstNonDisabledDate: isFirstNonDisabledDate,
        key: day
      });
    })))));
  })));
};
var CalendarBody$1 = CalendarBody;

const CalendarWrapper = () => {
  const {
    props: {
      align,
      calendarBodyProps,
      margin,
      renderNode,
      showCalendarOnly
    },
    refs,
    setState,
    state: {
      isCalendarOpen,
      portalPosition
    }
  } = useContext(CalendarContext);
  let cssOverlayWrapperClasses = ['st_calendar'];

  // apply user's css directly to overlay
  if (calendarBodyProps.className) {
    cssOverlayWrapperClasses = utils.classNames.addClassNames(calendarBodyProps.className, cssOverlayWrapperClasses);
  }

  // calendars that use the portals handle alignment differently
  if (!showCalendarOnly && align && !renderNode) {
    cssOverlayWrapperClasses = utils.classNames.addClassNames(`st_calendar--align-${align}`, cssOverlayWrapperClasses);
  }

  // position calendar absolutely relative to input
  if (!showCalendarOnly) {
    cssOverlayWrapperClasses = utils.classNames.addClassNames('st_calendar--with_calendar_input', cssOverlayWrapperClasses);
  }

  // allows calendar to be closed
  if (!isCalendarOpen && !showCalendarOnly) return null;
  const content = /*#__PURE__*/React.createElement(Box, _extends({}, calendarBodyProps, {
    arrow: showCalendarOnly || align === 'center' ? null : 'top',
    className: cssOverlayWrapperClasses.join(' '),
    componentRef: refs.calendar,
    margin: showCalendarOnly ? margin : {
      left: align === null ? 'md' : null,
      top: 'md'
    },
    onBlur: e => {
      if (!refs.calendar.current.contains(e.relatedTarget)) {
        setState.isCalendarOpen(false);
      }

      // if `calendarBodyProps` had an `onBlur` event handler, make sure to call it here
      if (calendarBodyProps.onBlur) calendarBodyProps.onBlur(e);
    },
    onFocus: e => {
      setState.isCalendarOpen(true);

      // if `calendarBodyProps` had an `onFocus` event handler, make sure to call it here
      if (calendarBodyProps.onFocus) calendarBodyProps.onFocus(e);
    },
    style: {
      ...portalPosition,
      ...calendarBodyProps.style
    },
    tabIndex: showCalendarOnly ? '-1' : '0' // * allows this element to become e.target onBlur in the input - but only if we are using the input
  }), /*#__PURE__*/React.createElement(CalendarBody$1, null));
  if (!renderNode) return content;

  // render the calendar drop down in the renderNode
  return /*#__PURE__*/ReactDOM.createPortal(content, renderNode);
};
var CalendarWrapper$1 = CalendarWrapper;

/**
 * Get the starting and ending time interval given the `minTime` and `maxTime`.
 *
 * @param {number} timeInterval The number of minutes between each selectable time
 * @param {string} minTime The minimum time that can be selected in the time interval menu
 * @param {string} maxTime The maximum time that can be selected in the time interval menu
 * @returns {[number, number]} ...
 */
const getTimeIntervalRange = (timeInterval, minTime, maxTime) => {
  const minTimeTotalMinutes = getTimeInMinutes(minTime);
  const maxTimeTotalMinutes = getTimeInMinutes(maxTime);
  const totalIntervals = NUMBER_OF_MINUTES_IN_A_DAY / timeInterval;
  const startingInterval = timeInterval ? Math.floor(minTimeTotalMinutes / timeInterval) : 0;
  const endingInterval = timeInterval ? Math.floor(maxTimeTotalMinutes / timeInterval) : totalIntervals;
  return [startingInterval, endingInterval];
};

/**
 * Given a date and a time, see if the combined date and time fall within the minDate/maxDate range.
 * If so, the date is valid, if not it is invalid.
 *
 * @param {Date} date A Javascript date object
 * @param {string} time A time in HH:mm format
 * @param {Date} minDate The minimum valid date in the calendar
 * @param {Date} maxDate The maximum valid date in the calendar
 * @returns {boolean} A flag that indicates whether or not selectedDate + time is within the valid
 * minDate/maxDate range
 */
const isDateAndTimeInRange = (date, time, minDate, maxDate) => {
  // we don't have a date, so its valid
  if (!date) return true;
  if (!time) return true;
  const [hours = '', minutes = ''] = time.match(timeComponents) || [];
  const dateAndTime = getAccurateDate(date.getFullYear(), date.getMonth(), date.getDate(), hours, minutes);
  return dateAndTime >= minDate && dateAndTime <= maxDate;
};

/**
 * Given a string, `time`, return a string that represents an error associated with the given time.
 * If the time contains no errors, it returns `false`.
 *
 * A time's validity is based on whether or not the time is within the `minTime` and `maxTime` range
 *  and if it is compatible with step.
 *
 * @param {string} time A string representing a time in HH:mm format
 * @param {{
 *   dateInCorrespondingCalendar: Date | null,
 *   disabledTimes: { times: string[] },
 *   maxDate: Date,
 *   maxTime: string,
 *   minDate: Date,
 *   minTime: string,
 *   step: number,
 * }} props The props passed to the `Calendar.TimeInput` component
 * @param {{
 *   current: {
 *     input: { current: HTMLInputElement | null },
 *     inputWrapper: { current: HTMLElement | null },
 *   }
 * }} refs An object containing React refs used by the `Calendar.TimeInput` component
 * @returns {string | false}
 */
const getTimeError = (time, _ref, refs) => {
  let {
    dateInCorrespondingCalendar,
    disabledTimes,
    maxDate,
    maxTime,
    minDate,
    minTime,
    step
  } = _ref;
  const {
    validationMessage,
    validity
  } = refs.input.current || {
    validationMessage: '',
    validity: {
      badInput: true
    }
  };

  // the time input has a error from the native html - the input might appear empty based on
  // `ref.current.input.current.value`, but it actually may be partially complete
  if (validity.badInput && validationMessage) return validationMessage;

  // null/empty value is ok
  if (!time) return false;

  // error text for different time errors
  const DISABLED_TIME_ERROR = 'Time is disabled. Please choose another time.';
  const TIME_RANGE_ERROR = 'Time is out of range.';
  const TIME_STEP_ERROR = `Time must be in ${step / 60} minute intervals.`;
  const [hours = '', minutes = ''] = time.match(timeComponents) || [];
  const inputTimeInMinutes = Number(hours) * 60 + Number(minutes);
  const minTimeInMinutes = minTime ? getTimeInMinutes(minTime) : 0;
  const maxTimeInMinutes = maxTime ? getTimeInMinutes(maxTime) : NUMBER_OF_MINUTES_IN_A_DAY;
  const newDateIsOutsideOfMinMaxTimeRange = inputTimeInMinutes < minTimeInMinutes || inputTimeInMinutes > maxTimeInMinutes;
  const isValidStep = !(inputTimeInMinutes % (step / 60));
  const dateWithTime = getDateWithTime(dateInCorrespondingCalendar, time);
  const isTimeOutOfMinDateMaxDateRange = dateInCorrespondingCalendar && (dateWithTime.getTime() < minDate.getTime() || dateWithTime.getTime() > maxDate.getTime());

  // out of valid time range - return error text
  if (newDateIsOutsideOfMinMaxTimeRange || isTimeOutOfMinDateMaxDateRange) {
    return TIME_RANGE_ERROR;
  }

  // invalid step - return error text
  if (!isValidStep) return TIME_STEP_ERROR;

  // time is disabled - return error text
  if (disabledTimes.times.includes(time)) return DISABLED_TIME_ERROR;

  // no errors
  return false;
};

/* eslint-disable import/prefer-default-export */

/**
 * Given the props from the previous render of the time input and the props from the current render
 * of the time input, determine which props, if any, have changed. Return an object containing
 * information about which props have changed.
 *
 * These flags are used in the derived state from props for the time input to determine which pieces
 * of state should be updated.
 *
 * @param {{
 *   dateInCorrespondingCalendar: Date | null,
 *   selectedTime: string,
 * }} oldProps An object containing the props passed to the time input on the previous render
 * @param {{
 *   dateInCorrespondingCalendar: Date | null,
 *   selectedTime: string,
 * }} props The props passed to the time input on the current render
 * @param {{
 *   input: { current: HTMLInputElement | null },
 *   inputWrapper: { current: HTMLElement | null },
 * }} refs An object containing React refs used by the `Calendar.TimeInput` component
 * @returns {{
 *   dateInCorrespondingCalendar: boolean,
 *   inputHasBeenRemotelyCleared: boolean,
 *   inputValueDeviatedFromCurrentTime: boolean,
 *   props: boolean,
 * }} An object containing all of the flags needed to handle derived state from props for the
 * time input
 */
const compareProps = (oldProps, props, refs) => {
  const {
    value
  } = refs.input.current || {
    value: null
  };

  // don't validate input if it hasn't been rendered yet. If the input has been rendered, compare
  // `selectedTime` against input value only if we have a `selectedTime`. `selectedTime` will be an
  // empty string if the input is not complete, so only compare it if the input is complete
  const inputValueDeviatedFromCurrentTime = value === null ? false : !!props.selectedTime && props.selectedTime !== value;

  // the input has been cleared by a mechanism that is external to the calendar. This is a tricky
  // thing to track; be careful to account for the selectedTime being cleared because of invalid
  // input
  const inputHasBeenRemotelyCleared = !getTimeError(value, props, refs) && !!oldProps.selectedTime && !props.selectedTime && !!value;

  // because we update dateInCorrespondingCalendar with different time values ignore the time values
  const dateInCorrespondingCalendarHasBeenUpdated = !!oldProps.dateInCorrespondingCalendar && !!props.dateInCorrespondingCalendar && !isSameDay(oldProps.dateInCorrespondingCalendar, props.dateInCorrespondingCalendar);
  const dateInCorrespondingCalendarHasBeenSet = !oldProps.dateInCorrespondingCalendar && !!props.dateInCorrespondingCalendar;
  const dateInCorrespondingCalendarHasChanged = dateInCorrespondingCalendarHasBeenSet || dateInCorrespondingCalendarHasBeenUpdated;
  const propsHaveChanged = dateInCorrespondingCalendarHasChanged || inputValueDeviatedFromCurrentTime || inputHasBeenRemotelyCleared;
  return {
    dateInCorrespondingCalendar: dateInCorrespondingCalendarHasChanged,
    inputHasBeenRemotelyCleared,
    inputValueDeviatedFromCurrentTime,
    props: propsHaveChanged
  };
};

/**
 * Ensure that internal state is up to date by checking for changes in props and making changes
 * accordingly.
 *
 * @param {{
 *   error: boolean | string,
 *   oldProps: {
 *     dateInCorrespondingCalendar: Date | null,
 *     selectedTime: string,
 *   },
 *   props: {
 *     dateInCorrespondingCalendar: Date | null,
 *     selectedTime: string,
 *   },
 *   refs: {
 *     input: { current: HTMLInputElement | null },
 *     inputWrapper: { current: HTMLElement | null },
 *   },
 *   setError: (error: boolean | string) => void,
 * }}
 * @returns {void}
 */
const useDerivedStateFromProps = _ref => {
  let {
    error,
    oldProps,
    props,
    refs,
    setError
  } = _ref;
  // an object that contains comparisons between various categories of old and new props for
  // determining whether or not to update various pieces of state
  // * don't use useMemo caching incorrect
  const propChanged = compareProps(oldProps, props, refs);

  // derived state from props - even though this may be triggered frequently this should only
  // trigger a rerender if we actually need it to; if a piece of state needs to be updated
  if (propChanged.props) {
    ReactDOM.unstable_batchedUpdates(() => {
      // if `selectedTime` has been updated and is now out of alignment with the time input's value,
      // update the input's value to reflect this change. This should only happen if overlapping
      // ranges are swapped, or if a `selectedTime` is chosen by an entity other than the time
      // input.
      if (propChanged.inputValueDeviatedFromCurrentTime) {
        if (refs.input.current) {
          /* eslint-disable-next-line no-param-reassign */
          refs.input.current.value = props.selectedTime;
          const nextError = getTimeError(props.selectedTime, props, refs);
          if (nextError !== error) setError(nextError);
        }
      }

      // the selectedTime has been set to `''` outside of the `Calendar.TimeInput`, clear the input.
      // inputHasBeenRemotelyCleared is mutually exclusive with inputValueDeviatedFromCurrentTime,
      // so we don't risk clearing the input right after setting it
      if (propChanged.inputHasBeenRemotelyCleared) {
        if (refs.input.current) {
          /* eslint-disable-next-line no-param-reassign */
          refs.input.current.value = '';

          // if input is cleared, we have no errors
          if (error) setError(false);
        }
      }

      // if the `dateInCorrespondingCalendar` has changed, make sure the time is in bounds
      if (propChanged.dateInCorrespondingCalendar) {
        const nextError = getTimeError(props.selectedTime, props, refs);
        if (nextError !== error) setError(nextError);
      }
    });
  }
};

const TimeIntervalButton = _ref => {
  let {
    disabled,
    handleTimeSelect,
    intervalDate,
    isSelected,
    refs
  } = _ref;
  const timeRef = useRef(null);

  // we don't want to scroll times into view that are disabled, unless its selected
  if (isSelected || !disabled) {
    refs.times.push(timeRef);
  }
  const className = ['st_calendar__interval-button', isSelected ? 'st_calendar__interval_button--selected' : '', disabled ? 'st_calendar__interval-button--disabled' : '' // mimic css for disabled button
  ].join(' ');
  return /*#__PURE__*/React.createElement(Button, {
    className: className,
    componentRef: timeRef,
    onClick: () => {
      // "disable" the button like this to allow e.relatedTarget in `TimeInterval` to not be null
      if (disabled) return;
      let minutes = intervalDate.getMinutes();
      if (minutes < 10) minutes = `0${minutes}`;
      let hours = intervalDate.getHours();
      if (hours >= 0 && hours < 10) hours = `0${hours}`;
      handleTimeSelect(`${hours}:${minutes}`);
    },
    tabIndex: "-1" // disables tabbing into interval list menu
    ,
    tertiary: true,
    title: disabled ? 'This time is out of range.' : '',
    value: getTimeString.twentyFourHour(intervalDate)
  }, getTimeString.twelveHour(intervalDate));
};
TimeIntervalButton.propTypes = {
  disabled: PropTypes.bool.isRequired,
  handleTimeSelect: PropTypes.func.isRequired,
  intervalDate: PropTypes.instanceOf(Date).isRequired,
  isSelected: PropTypes.bool.isRequired,
  refs: PropTypes.shape({
    input: PropTypes.any,
    inputWrapper: PropTypes.any,
    times: PropTypes.arrayOf(PropTypes.any).isRequired
  }).isRequired
};
var TimeIntervalButton$1 = TimeIntervalButton;

/**
 * Scroll the selectedTime, current time or minimum selectable time into view in the time interval
 * drop down component.
 *
 * @param {{
 *   dateInCorrespondingCalendar: Date | null,
 *   minDate: Date,
 *   minTime: string,
 *   times: { current: HTMLButtonElement }[],
 *   selectedTime: string,
 *   timeInterval: number,
 * }} arguments An assortment of props and pieces of state used to determine which time to scroll
 * into view
 * @returns {void}
 */
const useScrollTimeIntoView = _ref => {
  let {
    dateInCorrespondingCalendar,
    minDate,
    minTime,
    refs,
    selectedTime,
    timeInterval
  } = _ref;
  return useLayoutEffect(() => {
    const timeFromMinDate = getTimeString.twentyFourHour(minDate);
    const minScrollTime = isSameDay(dateInCorrespondingCalendar, minDate) && getTimeInMinutes(timeFromMinDate) > getTimeInMinutes(minTime) ? getTimeInMinutes(timeFromMinDate) : getTimeInMinutes(minTime);
    const selectedTimeInMinutes = getTimeInMinutes(selectedTime);
    const currentTimeInMinutes = getTimeInMinutes(getTimeString.twentyFourHour(new Date()));
    const shouldUseCurrentTime = currentTimeInMinutes >= minScrollTime;
    const intervalElemRef = refs.times.find((_ref2, i) => {
      let {
        current: {
          value
        }
      } = _ref2;
      const currentIntervalInMinutes = getTimeInMinutes(value);

      // scroll the selectedTime into view
      if (selectedTime) {
        const selectedTimeDoesntFitIntoInterval = selectedTimeInMinutes % timeInterval;
        const selectedTimeIsNextToInterval = Math.abs(selectedTimeInMinutes - currentIntervalInMinutes) < timeInterval;
        if (value === selectedTime) return true;
        if (selectedTimeDoesntFitIntoInterval && selectedTimeIsNextToInterval) {
          return true;
        }

        // scroll the current time into view
      } else if (shouldUseCurrentTime) {
        if (currentTimeInMinutes <= currentIntervalInMinutes) return true;

        // scroll the minimum selectable time into view
      } else if (currentIntervalInMinutes >= minScrollTime) {
        return true;
      }

      // the selectedTime or current time is after the last interval
      if (i === refs.times.length - 1) return true;
      return false;
    });

    // we wont't have `intervalElemRef` if all times are disabled
    if (intervalElemRef) {
      intervalElemRef.current.scrollIntoView({
        behavior: selectedTime ? 'smooth' : 'auto',
        // only scroll if selected
        block: 'nearest',
        inline: 'nearest'
      });
    }
  }, [dateInCorrespondingCalendar, minDate, minTime, refs.times, selectedTime, timeInterval]);
};
var useScrollTimeIntoView$1 = useScrollTimeIntoView;

const IntervalList = _ref => {
  let {
    dateInCorrespondingCalendar,
    disabled,
    disabledTimes,
    handleTimeSelect,
    maxDate,
    maxTime,
    minDate,
    minTime,
    refs,
    selectedTime,
    timeInterval
  } = _ref;
  // scroll selectedTime, current time or minimum selectable time into view depending on props
  useScrollTimeIntoView$1({
    dateInCorrespondingCalendar,
    minDate,
    minTime,
    refs,
    selectedTime,
    timeInterval
  });
  const dateInUse = dateInCorrespondingCalendar || new Date();
  const dateInUseWithoutTime = getDateWithTime(dateInUse);
  const [startingInterval, endingInterval] = getTimeIntervalRange(timeInterval, minTime, maxTime);
  const intervals = [];

  // every time this runs we are creating new intervals, reset the time refs
  refs.times = []; /* eslint-disable-line no-param-reassign */

  /* eslint-disable-next-line no-plusplus */
  for (let i = startingInterval; i <= endingInterval; i++) {
    const totalMinutes = i * timeInterval;
    const intervalDate = addMinutes(dateInUseWithoutTime, totalMinutes);
    const shouldDisable = disabled || dateInCorrespondingCalendar && (intervalDate < minDate || intervalDate > maxDate) || disabledTimes.times.includes(getTimeString.twentyFourHour(intervalDate));
    const selectedTimeInMinutes = selectedTime && getTimeInMinutes(selectedTime);
    const isSelected = totalMinutes === selectedTimeInMinutes;
    intervals.push( /*#__PURE__*/React.createElement(TimeIntervalButton$1, {
      disabled: shouldDisable,
      handleTimeSelect: handleTimeSelect,
      intervalDate: intervalDate,
      isSelected: isSelected,
      key: i,
      refs: refs
    }));
  }
  return intervals;
};
IntervalList.propTypes = {
  dateInCorrespondingCalendar: PropTypes.instanceOf(Date),
  disabled: PropTypes.bool.isRequired,
  disabledTimes: PropTypes.shape({
    times: PropTypes.arrayOf(time)
  }),
  handleTimeSelect: PropTypes.func.isRequired,
  maxDate: PropTypes.instanceOf(Date).isRequired,
  maxTime: time,
  minDate: PropTypes.instanceOf(Date).isRequired,
  minTime: time,
  refs: PropTypes.shape({
    input: PropTypes.any,
    inputWrapper: PropTypes.any,
    times: PropTypes.arrayOf(PropTypes.any).isRequired
  }).isRequired,
  selectedTime: time,
  timeInterval: PropTypes.number.isRequired
};
var IntervalList$1 = IntervalList;

const TimeInput = props => {
  let calendarContext = null;

  // if the `Calendar.TimeInput` component is not nested inside of the `Calendar`, catch the error
  // and throw a more specific error
  try {
    calendarContext = useContext(CalendarContext);
    if (!calendarContext) throw new Error();
  } catch (err) {
    throw new Error('The Screentone `Calendar.TimeInput` component must be nested inside of the Screentone `Calendar` component. Please see the documentation for more information.');
  }
  const timeProps = sanitizeTimeProps$1(props);

  // often times several props will need to be passed to utils at once. Combine both the
  // `Calendar`'s props and the `Calendar.TimeInput`'s props for convenience.
  const combinedProps = {
    ...calendarContext.props,
    ...timeProps,
    dateInCorrespondingCalendar: calendarContext.state.currentDate // make `currentDate` more clear
  };

  const {
    disabled,
    maxDate,
    minDate
  } = calendarContext.props;

  // make `currentDate` more clear
  const dateInCorrespondingCalendar = calendarContext.state.currentDate;
  const {
    componentRef,
    disabledTimes,
    error: externalError,
    margin,
    maxTime,
    minTime,
    onError,
    onSelect,
    selectedTime,
    step,
    timeInputProps,
    timeInputWrapperProps,
    timeInterval
  } = timeProps;
  const refs = useRef({
    input: componentRef || calendarContext.refs.timeInput,
    inputWrapper: timeInputWrapperProps.componentRef || {
      current: null
    },
    times: []
  });

  // for tracking changes in props to apply "derived state from props" updates
  const oldPropsRef = useRef({
    dateInCorrespondingCalendar,
    selectedTime
  });
  const [error, handleError] = useState(getTimeError(selectedTime, combinedProps, refs.current));
  const [isOpen, setIsOpen] = useState(false);

  // handles errors locally in the `Calendar.TimeInput` and in the calendar hook
  const setError = useCallback(e => {
    handleError(e);
    if (onError) onError(e);
  }, [onError]);

  // make sure that `error` in hooks is up to date if we have an error on the **first** render only
  useHandleErrorOnFirstRender$1(error, onError);

  // keep internal state up to date based on prop changes
  useDerivedStateFromProps({
    error,
    oldProps: oldPropsRef.current,
    props: combinedProps,
    refs: refs.current,
    setError
  });

  // these are the props that are related to the internal state of the calendar, we need to
  // track these props and update them when they change
  oldPropsRef.current = {
    dateInCorrespondingCalendar,
    selectedTime
  };
  let cssWrapperClasses = ['st_calender__time_input_wrapper'];
  if (timeInputWrapperProps.className) {
    cssWrapperClasses = utils.classNames.addClassNames(timeInputWrapperProps.className, cssWrapperClasses);
  }
  if (margin) {
    cssWrapperClasses = utils.spacing.addSpacingClasses({
      margin,
      cssClasses: cssWrapperClasses
    });
  }
  let cssInputClasses = ['st_calender__time_input'];
  if (timeInputProps.className) {
    cssInputClasses = utils.classNames.addClassNames(timeInputProps.className, cssInputClasses);
  }

  // errors passed down from props get priority over internally managed errors. If there are no
  // errors passed via props, fallback onto internally handled errors
  const currentError = typeof externalError === 'string' ? externalError : error || externalError;
  return /*#__PURE__*/React.createElement("div", _extends({}, timeInputWrapperProps, {
    className: cssWrapperClasses.join(' '),
    ref: refs.current.inputWrapper
  }), /*#__PURE__*/React.createElement(Input, _extends({
    className: cssInputClasses.join(' '),
    defaultValue: selectedTime,
    disabled: disabled,
    error: !!currentError,
    icon: IconClock,
    type: "time",
    step: step
  }, timeInputProps, {
    componentRef: refs.current.input,
    onBlur: e => {
      // close the interval list drop down if the user's focus left the time input all together
      if (!refs.current.inputWrapper.current.contains(e.relatedTarget)) {
        setIsOpen(false);
      }

      // handle next error state
      const nextError = getTimeError(e.target.value, combinedProps, refs.current);
      setError(nextError);

      // the definition of this event handler shadows any on `timeInputProps`, make sure to call
      // `timeInputProps.onBlur` if it exists
      if (timeInputProps.onBlur) timeInputProps.onBlur(e);
    },
    onChange: e => {
      const time = e.target.value;

      // input was valid, but has been cleared - handle this separately to avoid superfluous
      // internal html errors
      if (selectedTime && !time) {
        onSelect('');
        setError(false);
        return;
      }
      const timeError = getTimeError(time, combinedProps, refs.current);
      const dateIsValid = isDateAndTimeInRange(dateInCorrespondingCalendar, time, minDate, maxDate);

      // input is valid --- update selectedDate and clear error
      if (!timeError && dateIsValid) {
        onSelect(time);
        setError(false);

        // selectedTime contains an error
      } else {
        onSelect('');
        if (timeError !== error) setError(timeError);
      }

      // the definition of this event handler shadows any on `timeInputProps`, make sure to call
      // `timeInputProps.onChange` if it exists
      if (timeInputProps.onChange) timeInputProps.onChange(e);
    },
    onFocus: e => {
      setIsOpen(true);

      // the definition of this event handler shadows any on `timeInputProps`, make sure to call
      // `timeInputProps.onFocus` if it exists
      if (timeInputProps.onFocus) timeInputProps.onFocus(e);
    }
  })), isOpen ? /*#__PURE__*/React.createElement("div", {
    className: "st_calender__time_input_interval_container",
    tabIndex: "-1"
  }, /*#__PURE__*/React.createElement(IntervalList$1, {
    dateInCorrespondingCalendar: dateInCorrespondingCalendar,
    disabled: disabled,
    disabledTimes: disabledTimes,
    handleTimeSelect: time => {
      refs.current.input.current.value = time;
      onSelect(time);
      setIsOpen(false);
      setError(false);
    },
    maxDate: maxDate,
    maxTime: maxTime,
    minDate: minDate,
    minTime: minTime,
    refs: refs.current,
    selectedTime: selectedTime,
    timeInterval: timeInterval
  })) : '', currentError && typeof currentError === 'string' && /*#__PURE__*/React.createElement(FormHelperText, {
    error: true
  }, currentError));
};
TimeInput.propTypes = {
  /**
   * An object containing information about which times are disabled. `disabledTimes.times` is an
   * array containing various time values that should be disabled from selection by the
   * `Calendar.TimeInput` component.
   */
  disabledTimes: PropTypes.shape({
    times: PropTypes.arrayOf(time)
  }),
  /**
   * Either a boolean which represents whether or not the `Calendar.TimeInput` component contains an
   * error according to external validation, or a string which represents the same kind of error. If
   * a string is passed to the `error` prop, this string will be displayed as error text. **NOTE:**
   * The calendar handles validation internally as well. Errors may be triggered naturally without
   * setting up any form of external error validation.
   */
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  /** Margin to be applied to the `Calendar.TimeInput`'s wrapper */
  margin: PropTypes.shape({
    bottom: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl']),
    horizontal: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl']),
    left: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl']),
    right: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl']),
    top: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl']),
    vertical: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl'])
  }),
  /**
   * The maximum selectable time (inclusive) in the `Calendar.TimeInput` component. e.g. if
   * `'12:00'` was passed as the `maxTime`, no time value after `'12:00'` would be shown in the
   * `Calendar.TimeInput`'s dropdown, nor would they be valid for selection.
   */
  maxTime: time,
  /**
   * The minimum selectable time (inclusive) in the `Calendar.TimeInput` component. e.g. if
   * `'12:00'` was passed as the `minTime`, no time value before `'12:00'` would be shown in the
   * `Calendar.TimeInput`'s dropdown, nor would they be valid for selection.
   */
  minTime: time,
  /** A callback function triggered when an error occurs internally in the calendar. */
  onError: PropTypes.func,
  /**
   * A function to handle setting a new date/time. If an invalid time is passed in, the time will be
   * set to an empty string.
   */
  onSelect: PropTypes.func.isRequired,
  /**
   * A string that represents the time value selected by the `Calendar.TimeInput` component. The
   * `HH:mm` time format is used. An empty string represents an empty field.
   */
  selectedTime: time,
  /**
   * A number (in minutes) that represents what valid increments can be "stepped" through via
   * navigation with arrow keys. Defaults to `1`.
   */
  step: PropTypes.number,
  /**
   * An object containing props to pass directly to the `Calendar.TimeInput`'s input component. The
   * default input component is a regular Screentone input. A different component may be used for
   * the input if a value is passed to `inputEl`. **NOTE:** use `timeInputProps` to pass
   * `className`, `componentEl`, `componentRef`, etc. directly to the input (and not the
   * `Calendar.TimeInput` wrapper) if desired.
   */
  timeInputProps: PropTypes.shape({
    className: PropTypes.string,
    componentRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
      current: PropTypes.any
    })]),
    disabled: PropTypes.bool,
    error: PropTypes.bool,
    icon: PropTypes.instanceOf(HTMLElement),
    margin: PropTypes.shape({
      bottom: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl']),
      horizontal: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl']),
      left: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl']),
      right: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl']),
      top: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl']),
      vertical: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl'])
    })
  }),
  /**
   * An object containing props to pass directly to the `Calendar.TimeInput` component's wrapper.
   */
  timeInputWrapperProps: PropTypes.any,
  /**
   * A number (in minutes) that represents what time increments should be displayed in the interval
   * list drop down. If the `step` prop is larger than `timeInterval`, `timeInterval` will be set to
   * the value of `step`.
   */
  timeInterval: PropTypes.number
};
TimeInput.defaultProps = {
  disabledTimes: {
    times: []
  },
  error: false,
  margin: null,
  maxTime: '',
  // sanitized to constants.DEFAULT_MAX_TIME
  minTime: '',
  // sanitized to constants.DEFAULT_MIN_TIME
  onError: null,
  selectedTime: '',
  step: 1,
  timeInputProps: {},
  timeInputWrapperProps: {},
  timeInterval: null // sanitized to 30
};

var TimeInput$1 = TimeInput;

const Calendar = _ref => {
  let {
    children,
    ...props
  } = _ref;
  const __inputWrapperRef = useRef(null); /* eslint-disable-line no-underscore-dangle */
  let __timeInputRef = useRef(null); /* eslint-disable-line no-underscore-dangle */

  let timeProps = {};

  // if we have a `Calendar.TimeInput` component, extract the props. We need some of the props from
  // this component to sanitize the `Calendar` component
  if (children) {
    // the calendar only accepts `Calendar.TimeInput` as a child - throw an error for anything else
    if (children.type.displayName !== TimeInput$1.displayName) {
      throw new Error('The Screentone Calendar component was passed a child other than the `Calendar.TimeInput` component. This behavior is unsupported. If other elements are wrapping the `Calendar.TimeInput` component, please remove them.');
    }

    // should include `minTime`, `maxTime` and `disabledTimes`
    timeProps = {
      ...children.props
    };
    timeProps.isUsingTimeSelect = true; // used for sanitizing props

    // if a ref is passed to the `Calendar.TimeInput`, use that one instead
    if (timeProps.timeInputProps.componentRef) {
      __timeInputRef = timeProps.timeInputProps.componentRef;
    }
  }

  // validate props, and clear their values if not valid. Also reformat props if necessary
  const sanitizedProps = sanitizeCalendarProps$1(props, timeProps);

  // Tell the developer to use `dateRangeStart` or `dateRangeEnd` if they are trying to use the
  // calendar to select a range.
  if ((sanitizedProps.startDate || sanitizedProps.endDate) && !props.dateRangeStart && !props.dateRangeEnd) {
    const errorText = 'Missing prop `dateRangeStart` and/or `dateRangeEnd`! One or both of these props is necessary for the Screentone Calendar component to select a date range.';
    console.error(errorText); /* eslint-disable-line no-console */

    throw new Error(errorText);
  }
  let cssClasses = ['st_calendar__wrapper'];
  if (props.margin) {
    cssClasses = utils.spacing.addSpacingClasses({
      margin: props.margin,
      cssClasses
    });
  }
  if (props.calendarWrapperProps.className) {
    cssClasses = utils.classNames.addClassNames(props.calendarWrapperProps.className, cssClasses);
  }
  return /*#__PURE__*/React.createElement(CalendarProvider$1, _extends({
    __inputWrapperRef: __inputWrapperRef,
    __timeInputRef: __timeInputRef
  }, sanitizedProps), /*#__PURE__*/React.createElement("div", _extends({}, props.calendarWrapperProps, {
    className: cssClasses.join(' '),
    ref: __inputWrapperRef
  }), !sanitizedProps.showCalendarOnly && /*#__PURE__*/React.createElement(CalendarInput$1, null), /*#__PURE__*/React.createElement(CalendarWrapper$1, null), /*#__PURE__*/React.createElement(CalendarErrorText$1, null)), children);
};
Calendar.TimeInput = TimeInput$1;

/* eslint-disable max-len */
Calendar.propTypes = {
  /**
   * The alignment of the calendar dropdown; `"left"`, `"right"` or `null`. This prop is ignored if
   * `showCalendarOnly` is true. The default value of this prop is `null`.
   */
  align: PropTypes.oneOf(['left', 'right', null]),
  /**
   * A flag indicating whether or not buttons for month navigation are displayed, default `true`.
   */
  canChangeMonth: PropTypes.bool,
  /** An object containing props to pass directly to the `Calendar` component's dropdown. */
  calendarBodyProps: PropTypes.shape({
    arrow: PropTypes.oneOf(['bottom', 'left', 'right', 'top', null]),
    attached: PropTypes.bool,
    className: PropTypes.string,
    componentEl: PropTypes.string,
    componentRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
      current: PropTypes.any
    })]),
    fullWidth: PropTypes.bool,
    margin: PropTypes.shape({
      bottom: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl']),
      horizontal: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl']),
      left: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl']),
      right: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl']),
      top: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl']),
      vertical: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl'])
    }),
    padding: PropTypes.shape({
      bottom: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl']),
      horizontal: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl']),
      left: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl']),
      right: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl']),
      top: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xlg']),
      vertical: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl'])
    })
  }),
  /**
   * An object containing props to pass directly to the calendar's input component. The default
   * input component is a regular Screentone input. A different component may be used for the input
   * if a value is passed to `inputEl`. **NOTE:** use `calendarInputProps` to pass `className`,
   * `componentEl`, `componentRef`, etc. to the input if desired.
   */
  calendarInputProps: PropTypes.shape({
    className: PropTypes.string,
    componentRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
      current: PropTypes.any
    })]),
    disabled: PropTypes.bool,
    error: PropTypes.bool,
    icon: PropTypes.instanceOf(HTMLElement),
    margin: PropTypes.shape({
      bottom: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl']),
      horizontal: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl']),
      left: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl']),
      right: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl']),
      top: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl']),
      vertical: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl'])
    })
  }),
  /** An object containing props to pass directly to the Calendar component's wrapper. */
  calendarWrapperProps: PropTypes.any,
  /**
   * A string representing the format returned for dates. The default `dateFormat` is `MM/dd/yyyy`.
   * **NOTE:** `dateFormat` should not include time values. If you wish to use time values, use the
   * `Calendar.TimeInput`. It should also be noted that `dateFormat` should be able to encode enough
   * information to accurately track a date. If a time value is included or there is not enough
   * information to accurately track a date, the default date format will be returned.
   */
  dateFormat: PropTypes.string,
  /**
   * A flag that indicates the end of a date range. If this prop is passed to the calendar, both
   * `startDate` and `endDate` should be passed to the calendar as well. If the same component uses
   * both `dateRangeStart` and `dateRangeEnd`, `showCalendarOnly` will implicitly be `true`.
   */
  dateRangeEnd: PropTypes.bool,
  /**
   * A flag that indicates the start of a date range. If this prop is passed to the calendar, both
   * `startDate` and `endDate` should be passed to the calendar as well. If the same component uses
   * both `dateRangeStart` and `dateRangeEnd`, `showCalendarOnly` will implicitly be `true`.
   */
  dateRangeStart: PropTypes.bool,
  /** A flag that indicates whether or not the whole calendar dropdown and input is disabled. */
  disabled: PropTypes.bool,
  /**
   * An object containing information about which dates in the calendar should be disabled. The
   * property `dates` should be an array full of date objects which represents dates that should
   * not be selectable by the user. The property `weekDays` should be an array that contains numbers
   * ranging from 0 to 6 which represent a day of the week that should be disabled. The
   * `weekStartsOn` prop will not effect this prop.
   */
  disabledDates: PropTypes.shape({
    dates: PropTypes.arrayOf(PropTypes.instanceOf(Date)),
    weekDays: PropTypes.arrayOf(PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6]))
  }),
  /**
   * Represents the ending date in a date range. The `startDate` prop should also be passed to the
   * same calendar to be able do display both the start date and end date on the same calendar. This
   * prop must be used with the `dateRangeStart` and/or `dateRangeEnd` prop.
   */
  endDate: PropTypes.instanceOf(Date),
  /**
   * Either a boolean which represents whether or not the calendar contains an error according to
   * external validation, or a string which represents the same kind of error. If a string is passed
   * to the `error` prop, this string will be displayed as error text. **NOTE:** The calendar
   * handles validation internally as well. Errors may be triggered naturally without setting up any
   * form of external error validation.
   */
  error: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  /** Margin for component, if any */
  margin: PropTypes.shape({
    bottom: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl']),
    horizontal: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl']),
    left: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl']),
    right: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl']),
    top: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl']),
    vertical: PropTypes.oneOf(['xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl'])
  }),
  /**
   * The maximum valid date (inclusive) for the calendar. Disables calendar days after this date,
   * and prevents month navigation beyond this month. Invalid dates are converted to `null`. The
   * default `maxDate` is 12/31/9999.
   */
  maxDate: PropTypes.instanceOf(Date),
  /**
   * The minimum valid date (inclusive) for the calendar. Disables calendar days before this date,
   * and prevents month navigation beyond this month. Invalid dates are converted to `null`. The
   * default `minDate` is 01/01/1900. **It should be noted that Javascript dates will not always act as expected prior to the year 1900**.
   */
  minDate: PropTypes.instanceOf(Date),
  /**
   * Starting month for calendar to be displayed at, defaults to current month. Invalid dates are
   * converted to `null`.
   */
  month: PropTypes.oneOfType([PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]), PropTypes.oneOf(['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december']) // constants.MONTHS.LOWERCASE
  ]),

  /**
   * The number of months to be displayed in the calendar, either 1 or 2. Defaults to 1, any value
   * other than 1 or 2 will result in one month being displayed.
   */
  numberOfMonths: PropTypes.oneOf([1, 2]),
  /** A callback function triggered when an error occurs internally in the calendar. */
  onError: PropTypes.func,
  /**
   * A function to handle setting a new date/time. If an invalid date is passed in, the date will be
   * set to `null`.
   */
  onSelect: PropTypes.func.isRequired,
  /** Placeholder text for the input trigger for the calendar */
  placeholder: PropTypes.string,
  /**
   * The node that should render the calendar dropdown. By default the calendar will render in the
   * normal flow of the document. Use the `calendarHooks.usePortal()` hook to get a value to pass to
   * this prop. If the calendar dropdown is not being used (e.g. `showCalendarOnly` is `true`), this
   * prop will be ignored.
   */
  renderNode: PropTypes.shape({
    current: PropTypes.oneOf([PropTypes.instanceOf(HTMLElement), null])
  }),
  /** The currently selected date. Invalid dates are converted to `null`. */
  selectedDate: PropTypes.instanceOf(Date),
  /**
   * A flag that indicates whether or not to show only the calendar component without the input.
   * This prop will be implicitly `true` if both `dateRangeStart` and `dateRangeEnd` are passed to
   * the same calendar component.
   */
  showCalendarOnly: PropTypes.bool,
  /**
   * Represents the starting date in a date range. The `endDate` prop should also be passed to the
   * same calendar to be able do display both the start date and end date on the same calendar. This
   * prop must be used with the `dateRangeStart` and/or `dateRangeEnd` prop.
   */
  startDate: PropTypes.instanceOf(Date),
  /**
   * The day of the week that should represent the first day of the week in the calendar. Sunday is
   * `0`, Monday is `1`, all of the way up to Saturday which is `6`. Defaults to `0` (Sunday).
   */
  weekStartsOn: PropTypes.oneOfType([PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6]), PropTypes.oneOf(['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']) // constants.WEEKDAYS.LOWERCASE,
  ]),

  /**
   * Starting `year` for calendar to be displayed at, defaults to current year (unless this
   * conflicts with `minDate`/`maxDate`). Invalid dates are converted to `null`. `year` must be a
   * number between `1` and `9999` (inclusive).
   */
  year: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string, PropTypes.number])
};
/* eslint-enable max-len */

Calendar.defaultProps = {
  align: null,
  canChangeMonth: true,
  calendarBodyProps: {},
  calendarInputProps: {},
  calendarWrapperProps: {},
  dateFormat: null,
  dateRangeEnd: false,
  dateRangeStart: false,
  disabled: false,
  disabledDates: {
    dates: [],
    weekDays: []
  },
  error: false,
  endDate: null,
  margin: null,
  maxDate: new Date(9999, 11, 31, 11, 59),
  // constants.DEFAULT_MAX_DATE,
  minDate: new Date(0, 0, 1),
  // constants.DEFAULT_MIN_DATE,
  month: null,
  numberOfMonths: 1,
  onError: null,
  placeholder: null,
  renderNode: null,
  selectedDate: null,
  showCalendarOnly: false,
  startDate: null,
  weekStartsOn: 0,
  year: null
};
var Calendar$1 = Calendar;

export { calendarHooks$1 as calendarHooks, Calendar$1 as default, format, parse };
